import { CommonModule, isPlatformBrowser } from '@angular/common';
import { HttpErrorResponse } from '@angular/common/http';
import {
  ChangeDetectorRef,
  Component,
  ElementRef,
  HostBinding,
  HostListener,
  Inject,
  NgZone,
  OnDestroy,
  OnInit,
  PLATFORM_ID,
  SecurityContext,
  ViewChild,
  inject,
} from '@angular/core';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { FormsModule } from '@angular/forms';
import { NavigationEnd, Router } from '@angular/router';
import { marked } from 'marked';
import {
  Subject,
  Subscription,
  catchError,
  filter,
  firstValueFrom,
  interval,
  of,
  startWith,
  switchMap,
  takeUntil,
  takeWhile,
} from 'rxjs';
import { environment } from '../../environments/environment';
import { AuthService } from '../auth.service';
import { UserService } from '../user.service';
import { ChatAccessService } from '../services/chat-access.service';
import { UserProfile } from '../userProfile';
import { AssistantAutomationsService, AssistantAutomationRun } from '../services/assistant-automations.service';
import {
  ChatAction,
  ChatActivityStatus,
  ChatAttachmentRef,
  ChatConfirmation,
  ChatConfirmationDetail,
  ChatFile,
  ChatFollowUp,
  ChatMemoryItem,
  ChatReportJob,
  ChatSource,
  ChatbotIaService,
  ChatMessagePayload,
} from '../services/chatbot-ia.service';
import { S3UploadService } from '../services/s3-upload.service';
import {
  ChatConversationRecord,
  ChatbotConversationsService,
  StoredChatMessage,
} from '../services/chatbot-conversations.service';
import { ChatbotPageContextService } from '../services/chatbot-page-context.service';
import {
  ChatbotLauncherService,
  ChatbotLaunchRequest,
} from '../services/chatbot-launcher.service';
import {
  ChatbotQuickQuestionsCacheService,
  QuickQuestionsCacheEntry,
} from '../services/chatbot-quick-questions-cache.service';
import {
  CHAT_FONT_SIZE_SCALE,
  CHAT_RESPONSE_STYLE_OPTIONS,
  ChatFontSize,
  ChatResponseStyle,
  ChatbotSettings,
  ChatbotSettingsService,
  DEFAULT_CHATBOT_SETTINGS,
} from '../services/chatbot-settings.service';
import {
  CHAT_MODEL_OPTIONS,
  ChatModelOption,
  resolveChatModelOption,
} from './chatbot-models';
import {
  cancelSpeech,
  isSpeechSynthesisSupported,
  speakPortuguese,
} from './chatbot-speech-tts';
import {
  hasDistinctActivityDetail,
  processStepActionLabel,
  processStepLabel,
} from './chatbot-tool-labels';
import { ChatbotThinkingTraceComponent } from './chatbot-thinking-trace.component';
import {
  faviconUrlForUrl,
  isExternalHttpUrl,
} from './chatbot-web-favicon';
import { injectWebSourceCitesIntoHtml } from './chatbot-source-cites';
import {
  compactGeneratedImageReply,
  filterActionsForChatFiles,
  formatGeneratedImageLabel,
  IMAGE_GENERATION_STATUS_LABEL,
  IMAGE_LOADING_STATUS_LABEL,
  isGeneratedImageFile,
  isImageGenerationInProgress,
  isImageToolAwaitingFile,
  resolveImageGenerationActivity,
} from './chatbot-generated-image';
import { getChatWelcomeContent } from './chatbot-quick-questions';
import {
  ChatMention,
  SlashCommandItem,
  SlashQuery,
  buildAgentSlashItems,
  buildComposerCommandItems,
  buildComposerSourceItems,
  isComposerCommandItem,
  compactSlashSubtitle,
  excludeMentionedSlashItems,
  filterSlashItems,
  findSlashQuery,
  groupSlashItems,
  highlightMentions,
  insertMentionToken,
  isMentionOnlyMessage,
  syncMentions,
} from './chatbot-slash-commands';
import {
  ChatbotVoiceRecorder,
  VOICE_MAX_BYTES,
  VOICE_MAX_DURATION_MS,
  VOICE_MIN_DURATION_MS,
  blobToBase64,
  formatVoiceTimer,
  idleVoiceLevels,
  isVoiceCaptureSupported,
} from './chatbot-speech';
import {
  CHAT_ATTACHMENT_ACCEPT_EXTENSIONS,
  CHAT_ATTACHMENT_MAX_COUNT,
  CHAT_ATTACHMENT_UPLOAD_RULES,
} from '../utils/file-upload/file-upload-rules';
import { partitionValidatedFiles } from '../utils/file-upload/validate-upload-file';
import {
  materializeUploadFile,
  resolveUploadContentType,
} from '../utils/file-upload/resolve-upload-content-type';

marked.use({
  breaks: true,
  gfm: true,
});

type ChatRole = 'assistant' | 'user';

interface ChatMessage {
  id: string;
  role: ChatRole;
  text: string;
  displayText?: string;
  html: SafeHtml;
  actions: ChatAction[];
  confirmations: ChatConfirmation[];
  process?: ChatActivityStatus[];
  reports?: ChatReportJob[];
  files?: ChatFile[];
  attachments?: ChatAttachmentRef[];
  mentions?: ChatMention[];
  sources?: ChatSource[];
  followUps?: ChatFollowUp[];
  revisions?: string[];
  revisionIndex?: number;
  interrupted?: boolean;
  createdAt: number;
  thinkingDurationSeconds?: number;
}

interface PendingChatUpload {
  id: string;
  file: File;
  fileName: string;
  mimeType: string;
  fileSize: number;
  status: 'queued' | 'uploading' | 'ready' | 'error';
  objectKey?: string;
  error?: string;
}

interface PendingChatJob {
  conversationId: string;
  abort: AbortController;
  currentActivity: ChatActivityStatus | null;
  activityLog: ChatActivityStatus[];
  thinkingStartedAt: number;
  pipelineStarted?: boolean;
  streamingMessageId?: string;
  pendingRevisions?: string[];
  pendingText?: string;
  typedText?: string;
  pendingReply?: AssistantReply;
  lateFollowUps?: ChatFollowUp[];
  typewriterTimer?: ReturnType<typeof setTimeout>;
}

interface QueuedOutgoing {
  id: string;
  text: string;
  mentions?: ChatMention[];
  attachments: ChatAttachmentRef[];
}

interface AssistantReply {
  text: string;
  actions: ChatAction[];
  confirmations: ChatConfirmation[];
  reports: ChatReportJob[];
  files: ChatFile[];
  sources?: ChatSource[];
  followUps?: ChatFollowUp[];
  conversationTitle?: string;
  interrupted?: boolean;
}

const PROCESS_PHASES = new Set<ChatActivityStatus['phase']>([
  'tool',
  'searching',
  'memory',
  'agent',
  'confirm',
  'depth',
  'reflect',
  'title',
  'thinking',
  'writing',
]);

const PIPELINE_PHASES = new Set<ChatActivityStatus['phase']>([
  'depth',
  'reflect',
  'thinking',
  'writing',
  'tool',
  'searching',
  'agent',
  'confirm',
  'memory',
]);

const SILENT_THOUGHT_PHASES = new Set<ChatActivityStatus['phase']>([
  'depth',
  'reflect',
  'title',
]);

/** Titulador / Calibrador / Pensador: progresso → resultado no mesmo passo. */
const IN_PLACE_PROCESS_PHASES = new Set<ChatActivityStatus['phase']>([
  'title',
  'depth',
  'reflect',
]);

const DEPTH_SUMMARY: Record<string, string> = {
  low: 'Raciocínio rápido',
  medium: 'Raciocínio equilibrado',
  high: 'Raciocínio profundo',
  max: 'Raciocínio máximo',
};
const MAX_PROCESS_STEPS = 48;
const TYPEWRITER_MS_SLOW = 16;
const TYPEWRITER_MS_FAST = 8;
const TYPEWRITER_SLOW_UNTIL = 160;
const QUICK_QUESTION_ICONS = [
  'pending_actions',
  'folder_shared',
  'hourglass_top',
  'receipt_long',
  'car_crash',
  'assignment_late',
  'confirmation_number',
  'storefront',
  'summarize',
  'pageview',
] as const;

const QUICK_QUESTION_PLACEHOLDER_SLOTS = [1, 2, 3, 4] as const;
const MOBILE_BREAKPOINT_PX = 768;
const DRAG_THRESHOLD_PX = 4;
const PANEL_LAYOUT_STORAGE_KEY = 'wosi-chat-panel-layout:v1';
const PANEL_POSITION_STORAGE_KEY = 'wosi-chat-panel-position:v1';
const PANEL_POSITION_MARGIN_PX = 8;
const PANEL_MIN_WIDTH_PX = 360;
const PANEL_MIN_HEIGHT_PX = 420;
const SIDEBAR_MIN_WIDTH_PX = 200;
const SIDEBAR_MAX_WIDTH_PX = 420;
const SIDEBAR_DEFAULT_WIDTH_PX = 280;

type ResizeEdge = 'n' | 's' | 'e' | 'w' | 'ne' | 'nw' | 'se' | 'sw';

interface PanelLayout {
  x: number;
  y: number;
  width?: number;
  height?: number;
  sidebarWidth?: number;
}

const CHAT_UUID =
  '[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}';

const CHAT_LISTING_LABELS: Record<string, string> = {
  clientes: 'Clientes',
  parceiros: 'Parceiros',
  usuarios: 'Usuários',
  produtos: 'Produtos',
  sinistro: 'Sinistros',
  solicitacoes: 'Solicitações',
  'cupons-de-desconto': 'Cupons',
  auditoria: 'Auditoria',
  reboques: 'Reboques',
  comissoes: 'Comissões',
  financeiro: 'Financeiro',
  'lojas-e-vendedores': 'Lojas e vendedores',
};

function chatActionPath(url: string): string {
  return String(url ?? '')
    .split('?')[0]
    .split('#')[0]
    .replace(/\/$/, '');
}

function chatListingFamily(url: string): string | null {
  const path = chatActionPath(url).replace(/\/\d+$/, '');
  const visualizar = path.match(
    /^(?<family>\/minha-conta\/.+?)\/visualizar\//i
  );
  if (visualizar?.groups?.['family']) return visualizar.groups['family'];

  const cupom = path.match(
    new RegExp(
      `^(?<family>/minha-conta/cupons-de-desconto)/${CHAT_UUID}`,
      'i'
    )
  );
  if (cupom?.groups?.['family']) return cupom.groups['family'];

  if (/^\/minha-conta\/[^/]+$/.test(path)) return path;
  return null;
}

function isChatListingUrl(url: string): boolean {
  const family = chatListingFamily(url);
  if (!family) return false;
  return chatActionPath(url).replace(/\/\d+$/, '') === family;
}

/** 2+ fichas da mesma lista → 1 CTA de listagem (espelha mergeChatActions da API). */
function collapseChatActions(actions: ChatAction[] | undefined): ChatAction[] {
  if (!actions?.length) return [];

  const buckets = new Map<
    string,
    { listings: ChatAction[]; details: ChatAction[] }
  >();

  for (const action of actions) {
    const family = chatListingFamily(action.url);
    if (!family) continue;
    let bucket = buckets.get(family);
    if (!bucket) {
      bucket = { listings: [], details: [] };
      buckets.set(family, bucket);
    }
    if (isChatListingUrl(action.url)) bucket.listings.push(action);
    else bucket.details.push(action);
  }

  const drop = new Set<ChatAction>();
  const extras: ChatAction[] = [];

  for (const [family, { listings, details }] of buckets) {
    if (details.length < 2) continue;
    for (const detail of details) drop.add(detail);
    if (listings.length) continue;

    const slug = family.split('/').pop() ?? '';
    extras.push({
      label: `Abrir ${CHAT_LISTING_LABELS[slug] ?? 'listagem'}`,
      url: /\/\d+$/.test(family) ? family : `${family}/1`,
    });
  }

  return [...actions.filter((action) => !drop.has(action)), ...extras];
}

function finalizeChatActions(
  actions: ChatAction[] | undefined,
  files?: ChatFile[]
): ChatAction[] {
  return filterActionsForChatFiles(collapseChatActions(actions), files);
}

@Component({
  selector: 'app-chatbot-ia',
  standalone: true,
  imports: [CommonModule, FormsModule, ChatbotThinkingTraceComponent],
  templateUrl: './chatbot-ia.component.html',
  styleUrl: './chatbot-ia.component.scss',
})
export class ChatbotIaComponent implements OnInit, OnDestroy {
  @ViewChild('messagesViewport') private messagesViewport?: ElementRef<HTMLDivElement>;
  @ViewChild('messageInput') private messageInput?: ElementRef<HTMLTextAreaElement>;
  @ViewChild('fileInput') private fileInput?: ElementRef<HTMLInputElement>;
  @ViewChild('chatPanel') private chatPanel?: ElementRef<HTMLElement>;
  @ViewChild('headerMore') private headerMore?: ElementRef<HTMLElement>;

  @HostBinding('class.chatbot-ia--open')
  get hostIsOpen(): boolean {
    return this.isOpen && this.isChatAvailable;
  }

  @HostBinding('class.chatbot-ia--docked-mobile')
  get hostDockedMobile(): boolean {
    return this.isDockedInAccountNav;
  }

  @HostBinding('class.chatbot-ia--suppressed')
  get hostSuppressedOnTerms(): boolean {
    const path = this.router.url.split('?')[0];
    return path.startsWith('/termos-de-parceria');
  }

  @HostBinding('hidden')
  get hostHidden(): boolean {
    return !this.isChatAvailable;
  }

  private hostElement = inject(ElementRef<HTMLElement>);
  private chatbotService = inject(ChatbotIaService);
  private automationsApi = inject(AssistantAutomationsService);
  private s3Upload = inject(S3UploadService);
  private conversationsService = inject(ChatbotConversationsService);
  private settingsService = inject(ChatbotSettingsService);
  private quickQuestionsCache = inject(ChatbotQuickQuestionsCacheService);
  private pageContextService = inject(ChatbotPageContextService);
  private chatbotLauncher = inject(ChatbotLauncherService);
  private authService = inject(AuthService);
  private userService = inject(UserService);
  private chatAccess = inject(ChatAccessService);
  private router = inject(Router);
  private ngZone = inject(NgZone);
  private destroy$ = new Subject<void>();

  readonly intelligenceMarkSrc = '/assets/images/inteligencia-wosi.png';
  welcomeHeadline = 'Como posso ajudar?';
  welcomeMessage =
    'Sou a Inteligência Wosi. Posso orientar sobre produtos, contratação e navegação no site.';
  welcomeSubtitle = 'Selecione uma sugestão abaixo ou descreva sua necessidade.';
  quickQuestions: readonly string[] = [];
  quickQuestionsLoading = false;
  quickQuestionsGenerated = false;
  readonly quickQuestionPlaceholderSlots = QUICK_QUESTION_PLACEHOLDER_SLOTS;
  private quickQuestionsRequestId = 0;

  isOpen = false;
  private conversationsBootstrapped = false;
  /** Evita refetch quando user$ e a liberação disparam no mesmo ciclo. */
  private assistantSyncKey: string | null = null;
  private ephemeralConversations = new Map<string, ChatConversationRecord>();
  private pendingAutomationRunId: string | null = null;
  private automationRunPollers = new Map<string, Subscription>();
  private liveAutomationRunIds = new Set<string>();
  private automationRunAwaitingConversation: string | null = null;
  private automationLiveStreamingMessageId: string | null = null;
  private deferredLaunch: ChatbotLaunchRequest | null = null;
  private pendingLaunch: {
    text: string;
    mentions?: ChatMention[];
    expand?: boolean;
    conversationTitle?: string;
    attachments?: ChatAttachmentRef[];
  } | null = null;
  private pendingFreshConversation = false;
  isExpanded = false;
  isMobile = false;
  isDragging = false;
  isResizing = false;
  isResizingSidebar = false;
  headerMenuOpen = false;
  settingsOpen = false;
  memories: ChatMemoryItem[] = [];
  memoriesLoading = false;
  memoryDrafts: Record<string, string> = {};
  memoryBusyId: string | null = null;
  memoryNotice: string | null = null;
  shareNotice: string | null = null;
  editingMessageId: string | null = null;
  readOnlyShare = false;
  private voiceLoopArmed = false;
  speakingMessageId: string | null = null;
  readonly ttsSupported = isSpeechSynthesisSupported();
  settings: ChatbotSettings = { ...DEFAULT_CHATBOT_SETTINGS };
  copiedKey: string | null = null;
  private copiedTimer: ReturnType<typeof setTimeout> | null = null;
  private readonly messagesScrollBottomThreshold = 72;
  private followMessagesScroll = true;
  private suppressMessagesScrollTracking = false;
  private messagesScrollPinGeneration = 0;
  private messagesPointerStartY: number | null = null;
  readonly fontSizeOptions: { id: ChatFontSize; label: string; sample: string }[] = [
    { id: 'sm', label: 'Pequena', sample: 'A' },
    { id: 'md', label: 'Média', sample: 'A' },
    { id: 'lg', label: 'Grande', sample: 'A' },
  ];
  readonly responseStyleOptions = CHAT_RESPONSE_STYLE_OPTIONS;
  readonly chatModelOptions = CHAT_MODEL_OPTIONS;
  modelPickerOpen = false;
  sidebarWidth = SIDEBAR_DEFAULT_WIDTH_PX;
  panelLayout: PanelLayout | null = null;
  private dragPending = false;
  private dragMoved = false;
  private ignoreCloseFromDrag = false;
  private ignoreOutsideCloseUntil = 0;
  private dragStartPoint = { x: 0, y: 0 };
  private dragOffset = { x: 0, y: 0 };
  private resizeEdge: ResizeEdge | null = null;
  private resizeStart = {
    x: 0,
    y: 0,
    width: 0,
    height: 0,
    pointerX: 0,
    pointerY: 0,
  };
  private sidebarResizeStart = {
    pointerX: 0,
    width: SIDEBAR_DEFAULT_WIDTH_PX,
  };
  showSidebar = false;
  draft = '';
  draftMentions: ChatMention[] = [];
  /** Layout empilhado do composer — só após overflow de linha ou `\n`. */
  private composerExpanded = false;
  draftHighlightHtml: SafeHtml = '';
  slashOpen = false;
  slashIndex = 0;
  slashSource: 'slash' | 'at' | 'plus' | null = null;
  slashGroups: { group: string; items: SlashCommandItem[] }[] = [];
  private slashQuery: SlashQuery | null = null;
  private slashItems: SlashCommandItem[] = [];
  private composerCommandItems: SlashCommandItem[] = [];
  private readonly composerSourceItems = buildComposerSourceItems();
  readonly isComposerCommandItem = isComposerCommandItem;
  voiceSupported = false;
  isVoiceStarting = false;
  isVoiceRecording = false;
  isVoiceTranscribing = false;
  voiceLevels = idleVoiceLevels();
  voiceElapsedMs = 0;
  voiceError: string | null = null;
  private voiceRecorder: ChatbotVoiceRecorder | null = null;
  private transcribeSub?: Subscription;
  private voiceStopping = false;
  pendingConfirmation: ChatConfirmation | null = null;
  pendingReport: ChatReportJob | null = null;
  pendingChatFile: ChatFile | null = null;
  pendingUploads: PendingChatUpload[] = [];
  isComposerDragOver = false;
  readonly chatAttachmentAccept = CHAT_ATTACHMENT_ACCEPT_EXTENSIONS;
  readonly maxChatAttachments = CHAT_ATTACHMENT_MAX_COUNT;
  private reportPollers = new Map<string, Subscription>();
  private pendingJobs = new Map<string, PendingChatJob>();
  private outgoingQueues = new Map<string, QueuedOutgoing[]>();
  currentActivity: ChatActivityStatus | null = null;
  activityLog: ChatActivityStatus[] = [];
  /** Timestamp de início do pensamento ao vivo (automação sem job). */
  thinkingTraceFallbackStartedAt: number | null = null;
  expandedSourceMessageIds = new Set<string>();
  private sourceFaviconFailures = new Set<string>();
  private expandedProcessIds = new Set<string>();
  private imagePreviewLoadStates = new Map<string, 'loading' | 'loaded' | 'error'>();
  private imagePreviewLoadRetries = new Map<string, number>();
  private imagePreviewLoadWatchers = new Map<string, ReturnType<typeof setTimeout>>();
  private static readonly IMAGE_PREVIEW_LOAD_TIMEOUT_MS = 15_000;
  private static readonly IMAGE_PREVIEW_MAX_RETRIES = 3;

  conversations: ChatConversationRecord[] = [];
  conversationSearchQuery = '';
  activeConversationId: string | null = null;
  activeConversationTitle = 'Nova conversa';

  messages: ChatMessage[] = [];

  constructor(
    private sanitizer: DomSanitizer,
    private cdr: ChangeDetectorRef,
    @Inject(PLATFORM_ID) private platformId: object
  ) {
    this.applyWelcomeContent(null);
  }

  ngOnInit(): void {
    if (!isPlatformBrowser(this.platformId)) {
      return;
    }

    this.updateMobileState();
    this.restorePanelLayout();
    this.settings = this.settingsService.load();
    this.voiceSupported = isVoiceCaptureSupported();

    this.chatbotLauncher.requests$
      .pipe(takeUntil(this.destroy$))
      .subscribe((mode) => this.handleLaunchRequest(mode));

    const pendingLaunch = this.chatbotLauncher.consumePendingOpenAndSend();
    if (pendingLaunch) {
      this.handleLaunchRequest(pendingLaunch);
    }

    const pendingNewConversation = this.chatbotLauncher.consumePendingOpenNewConversation();
    if (pendingNewConversation) {
      this.handleLaunchRequest(pendingNewConversation);
    }

    const pendingAutomationRun = this.chatbotLauncher.consumePendingOpenAutomationRun();
    if (pendingAutomationRun) {
      this.handleLaunchRequest(pendingAutomationRun);
    }

    this.router.events
      .pipe(
        filter((event): event is NavigationEnd => event instanceof NavigationEnd),
        takeUntil(this.destroy$)
      )
      .subscribe(() => {
        this.syncChatAvailability();
        this.cdr.markForCheck();
      });

    this.userService.user$
      .pipe(takeUntil(this.destroy$))
      .subscribe((user) => {
        this.applyWelcomeContent(user);
        this.syncRemoteAssistant();
        if (!this.canUseAgentCommands) {
          this.closeSlashMenu();
          this.closeSettings();
          this.closeModelPicker();
        }
        this.syncChatAvailability();
        this.flushDeferredLaunch();
        this.cdr.markForCheck();
      });

    if (this.authService.isAuthenticated() && !this.userService.getUser()) {
      this.userService
        .loadUser()
        .pipe(takeUntil(this.destroy$))
        .subscribe();
    }

    this.chatAccess.changes$
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        this.syncChatAvailability();
        this.syncRemoteAssistant();
        this.flushDeferredLaunch();
        this.cdr.markForCheck();
      });
    this.chatAccess.resolved$
      .pipe(
        filter((ready) => ready),
        takeUntil(this.destroy$)
      )
      .subscribe(() => {
        this.syncRemoteAssistant();
        this.flushDeferredLaunch();
        this.cdr.markForCheck();
      });
    this.chatAccess.ensureLoaded().pipe(takeUntil(this.destroy$)).subscribe();

    this.bindDocumentInteractionListeners();
  }

  ngOnDestroy(): void {
    this.haltSpeech();
    this.clearImagePreviewLoadWatchers();
    this.abortAllConversationJobs();
    this.abortVoiceSession();
    this.stopAllReportPollers();
    this.stopAllAutomationRunPollers();
    this.clearCopiedTimer();
    this.persistActiveConversation();
    this.persistPanelLayout();
    this.destroy$.next();
    this.destroy$.complete();
    this.chatbotLauncher.setPanelOpen(false);

    if (isPlatformBrowser(this.platformId)) {
      this.unbindDocumentInteractionListeners();
      this.updateBodyScrollLock(false);
    }
  }

  get canUseAgentCommands(): boolean {
    return (
      this.authService.isAuthenticated() &&
      this.userService.getUser()?.hierarchy === 'admin'
    );
  }

  get selectedChatModel(): ChatModelOption {
    return resolveChatModelOption(this.settings.preferredChatModel);
  }

  toggleModelPicker(event?: MouseEvent): void {
    event?.stopPropagation();
    if (!this.canUseAgentCommands || this.readOnlyShare) return;
    if (this.modelPickerOpen) {
      this.closeModelPicker();
      return;
    }
    this.closeSlashMenu();
    this.modelPickerOpen = true;
    this.blurClickTarget(event);
  }

  selectChatModel(id: string, event?: MouseEvent): void {
    event?.stopPropagation();
    if (!this.canUseAgentCommands) return;
    this.patchSettings({ preferredChatModel: resolveChatModelOption(id).id });
    this.closeModelPicker();
    this.blurClickTarget(event);
  }

  closeModelPicker(): void {
    this.modelPickerOpen = false;
  }

  /** FAB segue a liberação por hierarquia. Admin permanece sempre ligado. */
  get isChatAvailable(): boolean {
    if (!this.chatAccess.isResolved()) return false;
    const hierarchy = this.userService.getUser()?.hierarchy ?? null;
    return this.chatAccess.isEnabledFor(hierarchy);
  }

  /**
   * /assistant/* (exceto chat-access) só depois do GET de liberação.
   * Com JWT e sem hierarquia ainda, não chuta anônimo — senão o browser loga 403.
   */
  private canCallAssistantApis(): boolean {
    if (!isPlatformBrowser(this.platformId)) return false;
    if (!this.isChatAvailable) return false;
    if (this.authService.isAuthenticated() && !this.userService.getUser()?.hierarchy) {
      return false;
    }
    return true;
  }

  private syncRemoteAssistant(): void {
    if (!this.canCallAssistantApis()) {
      if (this.chatAccess.isResolved() && !this.isChatAvailable) {
        if (this.assistantSyncKey === 'off') return;
        this.assistantSyncKey = 'off';
        this.abortAllConversationJobs();
        this.quickQuestionsRequestId += 1;
        this.quickQuestionsLoading = false;
      }
      return;
    }

    const key = this.userService.getUser()?.hierarchy ?? 'anonymous';
    if (this.assistantSyncKey === key) return;
    this.assistantSyncKey = key;

    void this.bootstrapConversations();
    if (this.shouldRefreshQuickQuestions()) {
      this.refreshQuickQuestions();
    }
    this.loadSlashCatalog();
    if (this.canUseAgentCommands) {
      this.openSharedConversationFromUrl();
    }
  }

  get isLoggedIn(): boolean {
    return this.authService.isAuthenticated();
  }

  /** No /minha-conta mobile o launcher some — o dock da conta abre o chat. */
  get isDockedInAccountNav(): boolean {
    if (!this.isMobile || !this.isLoggedIn) return false;
    return this.router.url.startsWith('/minha-conta');
  }

  /** Stop só quando a IA pensa e o composer está vazio — draft preenchido prioriza enviar/fila. */
  get canStopGeneration(): boolean {
    return this.isTyping && !this.isVoiceBusy && !this.canSend;
  }

  get composerFollowUps(): ChatFollowUp[] {
    return [];
  }

  get fontScale(): number {
    return CHAT_FONT_SIZE_SCALE[this.settings.fontSize];
  }

  get isComposerCompact(): boolean {
    return !this.composerExpanded;
  }

  get composerPlaceholder(): string {
    if (this.readOnlyShare) {
      return 'Conversa compartilhada — somente leitura';
    }
    if (this.editingMessageId) {
      return 'Edite a mensagem e envie de novo…';
    }
    return 'Pergunte qualquer coisa…';
  }

  get isTyping(): boolean {
    return (
      this.isConversationBusy(this.activeConversationId) ||
      Boolean(this.automationRunAwaitingConversation)
    );
  }

  /** Rastro Beautiful UI acima da conversa enquanto o turno está em andamento. */
  get showThinkingTrace(): boolean {
    if (!this.isTyping) return false;
    if (this.automationRunAwaitingConversation) return true;

    const conversationId = this.activeConversationId ?? '';
    const job = this.pendingJobs.get(conversationId);
    if (!job && !this.liveAutomationRunIds.has(conversationId)) return false;

    const lastAssistant = [...this.messages]
      .reverse()
      .find((message) => message.role === 'assistant');
    if (
      this.liveAutomationRunIds.has(conversationId) &&
      lastAssistant?.process?.length &&
      !job
    ) {
      return false;
    }

    return true;
  }

  get thinkingTraceStartedAt(): number | null {
    const job = this.pendingJobs.get(this.activeConversationId ?? '');
    if (job?.thinkingStartedAt) return job.thinkingStartedAt;
    if (this.isTyping) return this.thinkingTraceFallbackStartedAt;
    return null;
  }

  /** Shimmer ativo até a bolha de resposta começar a streamar. */
  get isThinkingTraceActive(): boolean {
    if (this.automationRunAwaitingConversation) return true;
    const job = this.pendingJobs.get(this.activeConversationId ?? '');
    if (job?.streamingMessageId) return false;
    if (this.automationLiveStreamingMessageId) return false;
    return this.isTyping;
  }

  shouldShowPendingThinkingDock(): boolean {
    return this.showThinkingTrace && !this.activeStreamingMessageId;
  }

  shouldShowThinkingDock(message: ChatMessage): boolean {
    if (message.role !== 'assistant') return false;
    if (this.isLiveThinkingMessage(message)) return true;
    return Boolean(message.process?.length);
  }

  thinkingActivityLog(message: ChatMessage): ChatActivityStatus[] {
    if (this.isLiveThinkingMessage(message)) {
      return this.activityLog.length ? this.activityLog : message.process ?? [];
    }
    return message.process ?? [];
  }

  thinkingCurrentActivity(message: ChatMessage): ChatActivityStatus | null {
    if (this.isImageToolRunning(message)) {
      return resolveImageGenerationActivity(
        this.thinkingActivityLog(message),
        this.currentActivity
      );
    }
    if (this.hasGeneratedImageLoading(message)) {
      return resolveImageGenerationActivity(
        this.thinkingActivityLog(message),
        {
          toolName: 'admin_generate_blog_image_ai',
          phase: 'tool',
          label: IMAGE_LOADING_STATUS_LABEL,
        }
      );
    }
    return this.isLiveThinkingMessage(message) ? this.currentActivity : null;
  }

  isThinkingActiveForMessage(message: ChatMessage): boolean {
    if (this.isGeneratingImage(message)) return true;
    return this.isLiveThinkingMessage(message) && this.isThinkingTraceActive;
  }

  thinkingStartedAtForMessage(message: ChatMessage): number | null {
    if (!this.isLiveThinkingMessage(message)) return null;
    return this.thinkingTraceStartedAt;
  }

  thinkingSettledDuration(message: ChatMessage): number | null {
    if (this.isLiveThinkingMessage(message)) return null;
    if (message.thinkingDurationSeconds) return message.thinkingDurationSeconds;
    return message.process?.length ? 1 : null;
  }

  private isLiveThinkingMessage(message: ChatMessage): boolean {
    return this.showThinkingTrace && this.isStreamingMessage(message.id);
  }

  /** @deprecated Use shouldShowThinkingDock / shouldShowPendingThinkingDock. */
  shouldShowThinkingBeforeMessage(_message: ChatMessage): boolean {
    return false;
  }

  /** @deprecated Use shouldShowPendingThinkingDock. */
  shouldShowThinkingAtEnd(): boolean {
    return false;
  }

  /** @deprecated Substituído por showThinkingTrace — mantido para specs legadas. */
  get showLiveTypingBubble(): boolean {
    return false;
  }

  get activeStreamingMessageId(): string | null {
    const job = this.pendingJobs.get(this.activeConversationId ?? '');
    return job?.streamingMessageId ?? null;
  }

  isStreamingMessage(messageId: string): boolean {
    if (this.activeStreamingMessageId === messageId) return true;
    return this.automationLiveStreamingMessageId === messageId;
  }

  trackByMessageId(_index: number, message: ChatMessage): string {
    return message.id;
  }

  get conversationAttentionCount(): number {
    return this.conversations.filter(
      (conversation) =>
        conversation.id !== this.activeConversationId &&
        (conversation.unread === true || this.isConversationBusy(conversation.id))
    ).length;
  }

  get hasBackgroundBusy(): boolean {
    return [...this.pendingJobs.keys()].some(
      (conversationId) => conversationId !== this.activeConversationId
    );
  }

  get sidebarToggleLabel(): string {
    const base = this.showSidebar ? 'Ocultar conversas' : 'Mostrar conversas';
    const count = this.conversationAttentionCount;
    if (!count) return base;
    if (count === 1) return `${base}, 1 conversa com novidade`;
    return `${base}, ${count} conversas com novidades`;
  }

  get conversationAttentionBadge(): string {
    return this.conversationAttentionCount > 9
      ? '9+'
      : String(this.conversationAttentionCount);
  }

  get canSend(): boolean {
    if (this.isUploadingAttachments || this.isVoiceBusy) return false;
    return Boolean(this.draft.trim()) || this.readyAttachments.length > 0;
  }

  get queuedOutgoing(): QueuedOutgoing[] {
    if (!this.activeConversationId) return [];
    return this.outgoingQueues.get(this.activeConversationId) ?? [];
  }

  get queueHoldLabel(): string {
    if (this.hasUnconsumedConfirmation()) return 'Aguardando confirmação';
    if (this.isTyping) return 'Envia após esta resposta';
    return 'Na fila';
  }

  get isQueueHeld(): boolean {
    return this.hasUnconsumedConfirmation();
  }

  get readyAttachments(): PendingChatUpload[] {
    return this.pendingUploads.filter((item) => item.status === 'ready' && item.objectKey);
  }

  get isUploadingAttachments(): boolean {
    return this.pendingUploads.some(
      (item) => item.status === 'queued' || item.status === 'uploading'
    );
  }

  get isVoiceBusy(): boolean {
    return this.isVoiceStarting || this.isVoiceRecording || this.isVoiceTranscribing;
  }

  get voiceTimerLabel(): string {
    return formatVoiceTimer(this.voiceElapsedMs);
  }

  get voiceStageLabel(): string {
    if (this.isVoiceTranscribing) return 'Transcrevendo em português do Brasil…';
    if (this.isVoiceStarting) return 'Permita o microfone para falar em português';
    if (this.voiceElapsedMs >= VOICE_MAX_DURATION_MS - 5_000) {
      return `Gravando em português · ${this.voiceTimerLabel}`;
    }
    return `Gravando em português ${this.voiceTimerLabel}`;
  }

  get panelStyle(): Record<string, string> | null {
    if (!this.panelLayout || this.matchesMobileMedia() || this.isExpanded) {
      return null;
    }

    const zoom = this.getAppZoom();
    const style: Record<string, string> = {
      left: `${this.panelLayout.x / zoom}px`,
      top: `${this.panelLayout.y / zoom}px`,
      right: 'auto',
      bottom: 'auto',
      transform: 'none',
    };

    if (this.panelLayout.width && this.panelLayout.height) {
      style['width'] = `${this.panelLayout.width / zoom}px`;
      style['height'] = `${this.panelLayout.height / zoom}px`;
      style['maxWidth'] = 'none';
      style['maxHeight'] = 'none';
    }

    return style;
  }

  onWindowResize(): void {
    this.updateMobileState();
    this.reclampSavedLayout();
  }

  private readonly onWindowResizeNative = (): void => {
    const nextMobile = this.matchesMobileMedia();
    const mobileChanged = nextMobile !== this.isMobile;
    if (!mobileChanged && !(this.isOpen && !nextMobile)) return;
    this.ngZone.run(() => this.onWindowResize());
  };

  private readonly onDocumentPointerDownNative = (event: PointerEvent): void => {
    if (!this.isOpen && !this.headerMenuOpen && !this.modelPickerOpen) return;
    this.ngZone.run(() => this.onDocumentPointerDown(event));
  };

  private bindDocumentInteractionListeners(): void {
    this.ngZone.runOutsideAngular(() => {
      window.addEventListener('resize', this.onWindowResizeNative, { passive: true });
      document.addEventListener('pointerdown', this.onDocumentPointerDownNative);
    });
  }

  private unbindDocumentInteractionListeners(): void {
    window.removeEventListener('resize', this.onWindowResizeNative);
    document.removeEventListener('pointerdown', this.onDocumentPointerDownNative);
  }

  get sortedConversations(): ChatConversationRecord[] {
    return [...this.conversations].sort((a, b) => b.updatedAt - a.updatedAt);
  }

  get filteredConversations(): ChatConversationRecord[] {
    const query = this.conversationSearchQuery.trim();
    if (!query) return this.sortedConversations;

    return this.sortedConversations.filter((conversation) =>
      this.conversationsService.matchesConversationSearch(conversation, query)
    );
  }

  get hasConversationSearch(): boolean {
    return this.conversationSearchQuery.trim().length > 0;
  }

  formatConversationDate(timestamp: number): string {
    return this.conversationsService.formatRelativeDate(timestamp);
  }

  conversationPreview(conversation: ChatConversationRecord): string {
    if (this.isConversationBusy(conversation.id)) {
      const job = this.pendingJobs.get(conversation.id);
      if (job?.currentActivity) {
        return processStepActionLabel(job.currentActivity);
      }
      return 'Pensando…';
    }

    return this.conversationsService.buildConversationPreview(conversation);
  }

  isConversationBusy(conversationId: string | null | undefined): boolean {
    if (!conversationId) return false;
    if (this.pendingJobs.has(conversationId)) return true;
    return this.liveAutomationRunIds.has(conversationId);
  }

  isConversationUnread(conversation: ChatConversationRecord): boolean {
    if (conversation.id === this.activeConversationId) return false;
    return conversation.unread === true;
  }

  clearConversationSearch(event?: MouseEvent): void {
    this.conversationSearchQuery = '';
    this.blurClickTarget(event);
  }

  private applyWelcomeContent(user: UserProfile | null): void {
    this.quickQuestionsCache.refreshScope();
    const pageContext = this.pageContextService.capture();
    const pageKey = this.pageContextService.pageCacheKey(pageContext);
    const cached = this.quickQuestionsCache.load(pageKey);
    if (cached && this.quickQuestionsCache.isFresh(cached, pageKey)) {
      this.applyCachedQuickQuestions(cached);
      this.quickQuestionsLoading = false;
      return;
    }

    const content = getChatWelcomeContent(
      this.authService.isAuthenticated(),
      user,
      pageContext.screen
    );
    this.welcomeHeadline = content.headline;
    this.welcomeMessage = content.message;
    this.welcomeSubtitle = content.subtitle;
    this.quickQuestions = content.quickQuestions;
    this.quickQuestionsGenerated = false;
    this.quickQuestionsLoading = isPlatformBrowser(this.platformId);
  }

  quickQuestionIcon(index: number): string {
    return QUICK_QUESTION_ICONS[index % QUICK_QUESTION_ICONS.length];
  }

  private shouldRefreshQuickQuestions(): boolean {
    return this.messages.length === 0 && !this.isTyping;
  }

  private refreshQuickQuestions(): void {
    if (!isPlatformBrowser(this.platformId)) return;
    if (!this.canCallAssistantApis()) {
      this.quickQuestionsLoading = false;
      return;
    }
    if (!this.shouldRefreshQuickQuestions()) return;

    this.quickQuestionsCache.refreshScope();
    const pageContext = this.pageContextService.capture();
    const pageKey = this.pageContextService.pageCacheKey(pageContext);
    const cached = this.quickQuestionsCache.load(pageKey);
    const samePageFresh = this.quickQuestionsCache.isFresh(cached, pageKey);

    if (samePageFresh && cached) {
      this.applyCachedQuickQuestions(cached);
      this.quickQuestionsLoading = false;
      this.cdr.markForCheck();
      return;
    }

    const requestId = ++this.quickQuestionsRequestId;
    this.quickQuestionsGenerated = false;
    this.quickQuestionsLoading = true;
    this.cdr.markForCheck();

    this.chatbotService
      .getQuickQuestions(pageContext)
      .pipe(
        catchError(() => of(null)),
        takeUntil(this.destroy$)
      )
      .subscribe((response) => {
        if (requestId !== this.quickQuestionsRequestId) return;
        if (!this.shouldRefreshQuickQuestions()) {
          this.quickQuestionsLoading = false;
          this.cdr.markForCheck();
          return;
        }

        if (response?.quickQuestions?.length) {
          this.applyCachedQuickQuestions({
            version: 5,
            pageKey,
            headline: response.headline?.trim() || this.welcomeHeadline,
            message: response.message?.trim() || this.welcomeMessage,
            subtitle: response.subtitle?.trim() || this.welcomeSubtitle,
            quickQuestions: response.quickQuestions.slice(0, 4),
            generated: response.generated === true,
            cachedAt: Date.now(),
          });
          this.quickQuestionsCache.save(response, pageKey);
        } else {
          this.applyFallbackQuickQuestions(pageContext.screen);
        }

        this.quickQuestionsLoading = false;
        this.cdr.markForCheck();
      });
  }

  private applyCachedQuickQuestions(entry: QuickQuestionsCacheEntry): void {
    if (entry.headline?.trim()) {
      this.welcomeHeadline = entry.headline.trim();
    }
    if (entry.message?.trim()) {
      this.welcomeMessage = entry.message.trim();
    }
    if (entry.subtitle?.trim()) {
      this.welcomeSubtitle = entry.subtitle.trim();
    }
    if (entry.quickQuestions?.length) {
      this.quickQuestions = entry.quickQuestions.slice(0, 4);
    }
    this.quickQuestionsGenerated = entry.generated === true;
  }

  private applyFallbackQuickQuestions(screen?: string | null): void {
    const content = getChatWelcomeContent(
      this.authService.isAuthenticated(),
      this.userService.getUser(),
      screen ?? this.pageContextService.capture().screen
    );
    this.welcomeHeadline = content.headline;
    this.welcomeMessage = content.message;
    this.welcomeSubtitle = content.subtitle;
    this.quickQuestions = content.quickQuestions;
    this.quickQuestionsGenerated = false;
  }

  private async openAutomationRunConversation(runId: string): Promise<void> {
    if (!isPlatformBrowser(this.platformId)) return;

    this.setOpen(true);
    this.showSidebar = false;

    try {
      const run = await firstValueFrom(this.automationsApi.getRun(runId));

      if (run.status === 'running' || run.status === 'queued') {
        this.startAutomationRunPolling(runId);
      }

      const record = this.buildAutomationRunRecord(run);
      this.openEphemeralConversation(record);

      if (run.status === 'running' || run.status === 'queued') {
        this.markAutomationRunLive(run);
      }
    } catch {
      this.messages = [
        this.toUiMessage({
          id: `automation-open-error-${runId}`,
          role: 'assistant',
          text: 'Não foi possível carregar esta execução. Tente novamente em instantes.',
          actions: [],
          confirmations: [],
          createdAt: Date.now(),
        }),
      ];
      this.activeConversationId = null;
      this.activeConversationTitle = 'Execução';
      this.cdr.detectChanges();
    }
  }

  private buildAutomationRunRecord(run: AssistantAutomationRun): ChatConversationRecord {
    const now = Date.now();
    const conversationId = this.automationRunConversationId(run);
    return {
      id: conversationId,
      title: run.title?.trim() || run.automationName?.trim() || 'Execução',
      messages: this.normalizeAutomationRunMessages(run.messages ?? [], run),
      createdAt: run.startedAt ? new Date(run.startedAt).getTime() : now,
      updatedAt: run.finishedAt ? new Date(run.finishedAt).getTime() : now,
      unread: false,
    };
  }

  private automationRunConversationId(run: AssistantAutomationRun): string {
    return run.conversationId || `run-${run.id}`;
  }

  private normalizeAutomationRunMessages(
    messages: StoredChatMessage[],
    run: AssistantAutomationRun
  ): StoredChatMessage[] {
    if (!messages.length) return messages;
    return messages.map((message, index) => {
      if (index === 0 && message.role === 'user' && !message.displayText?.trim()) {
        return {
          ...message,
          displayText: run.eventDisplayText?.trim() || message.displayText,
        };
      }
      return message;
    });
  }

  private startAutomationRunPolling(runId: string): void {
    if (!isPlatformBrowser(this.platformId)) return;
    if (this.automationRunPollers.has(runId)) return;

    const sub = interval(1500)
      .pipe(
        startWith(0),
        switchMap(() =>
          this.automationsApi.getRun(runId).pipe(catchError(() => of(null)))
        ),
        takeUntil(this.destroy$),
        takeWhile(
          (run) =>
            Boolean(run) &&
            (run!.status === 'running' || run!.status === 'queued'),
          true
        )
      )
      .subscribe((run) => {
        if (!run) return;
        this.applyAutomationRunSnapshot(run);
        if (run.status !== 'running' && run.status !== 'queued') {
          this.stopAutomationRunPolling(runId);
        }
        this.cdr.detectChanges();
      });

    this.automationRunPollers.set(runId, sub);
  }

  private applyAutomationRunSnapshot(run: AssistantAutomationRun): void {
    if (this.automationRunAwaitingConversation === run.id) {
      this.automationRunAwaitingConversation = null;
    }

    if (!run.messages?.length && !run.conversationId) {
      if (run.status === 'running' || run.status === 'queued') {
        this.automationRunAwaitingConversation = run.id;
        if (!this.currentActivity) {
          this.currentActivity = {
            phase: 'thinking',
            label: 'Preparando execução…',
            icon: 'hourglass_top',
          };
        }
        if (!this.thinkingTraceFallbackStartedAt) {
          this.thinkingTraceFallbackStartedAt = Date.now();
        }
      }
      return;
    }

    const record = this.buildAutomationRunRecord(run);
    const isActive = this.activeConversationId === record.id;

    if (!isActive && !this.activeConversationId) {
      this.openEphemeralConversation(record);
    } else {
      this.ephemeralConversations.set(record.id, record);
      if (isActive) {
        this.messages = this.collapseStackedAssistants(
          record.messages.map((message) => this.toUiMessage(message))
        );
        this.syncProcessExpansion();
        this.trackProcessingReports();
        this.scrollToBottomSoon(true);
      }
    }

    if (run.status === 'running' || run.status === 'queued') {
      this.markAutomationRunLive(run);
    } else {
      this.liveAutomationRunIds.delete(record.id);
      this.automationLiveStreamingMessageId = null;
      if (isActive) {
        this.currentActivity = null;
      }
    }
  }

  private markAutomationRunLive(run: AssistantAutomationRun): void {
    const conversationId = this.automationRunConversationId(run);

    if (run.status === 'running' || run.status === 'queued') {
      this.liveAutomationRunIds.add(conversationId);
      this.syncLiveAutomationActivity(run);
      if (run.messages?.length) {
        this.updateAutomationLiveStreaming(run.messages);
      } else if (this.activeConversationId === conversationId) {
        const lastAssistant = [...this.messages]
          .reverse()
          .find((message) => message.role === 'assistant');
        this.automationLiveStreamingMessageId = lastAssistant?.text?.trim()
          ? lastAssistant.id
          : null;
      }
      return;
    }

    this.liveAutomationRunIds.delete(conversationId);
    this.automationLiveStreamingMessageId = null;
    if (this.activeConversationId === conversationId) {
      this.currentActivity = null;
    }
  }

  private syncLiveAutomationActivity(run: AssistantAutomationRun): void {
    const conversationId = this.automationRunConversationId(run);
    if (!this.liveAutomationRunIds.has(conversationId)) {
      return;
    }
    if (this.activeConversationId !== conversationId) return;

    const assistantMessages = (run.messages ?? [])
      .filter((message) => message.role === 'assistant')
      .map((message) => this.toUiMessage(message));
    const lastAssistant = assistantMessages.at(-1);
    const lastStep = lastAssistant?.process?.at(-1) ?? null;

    if (lastStep && !this.isHiddenLiveThought(lastStep)) {
      this.currentActivity = lastStep;
      return;
    }

    if (!lastAssistant?.text?.trim()) {
      this.currentActivity = {
        phase: 'thinking',
        label: 'Pensando…',
        icon: 'psychology',
      };
    }
  }

  private updateAutomationLiveStreaming(messages: StoredChatMessage[]): void {
    const lastAssistant = [...messages].reverse().find((message) => message.role === 'assistant');
    if (!lastAssistant?.text?.trim()) {
      this.automationLiveStreamingMessageId = null;
      return;
    }

    const uiMessage = this.toUiMessage(lastAssistant);
    this.automationLiveStreamingMessageId = uiMessage.id;
  }

  private stopAutomationRunPolling(runId: string): void {
    const sub = this.automationRunPollers.get(runId);
    sub?.unsubscribe();
    this.automationRunPollers.delete(runId);
    if (this.automationRunAwaitingConversation === runId) {
      this.automationRunAwaitingConversation = null;
      if (!this.pendingJobs.has(this.activeConversationId ?? '')) {
        this.currentActivity = null;
      }
    }
  }

  private stopAllAutomationRunPollers(): void {
    for (const sub of this.automationRunPollers.values()) {
      sub.unsubscribe();
    }
    this.automationRunPollers.clear();
    this.liveAutomationRunIds.clear();
    this.automationRunAwaitingConversation = null;
    this.automationLiveStreamingMessageId = null;
  }

  private openEphemeralConversation(conversation: ChatConversationRecord): void {
    const previousId = this.activeConversationId;
    if (previousId && previousId !== conversation.id) {
      this.flushTypewriter(previousId);
    }

    this.ephemeralConversations.set(conversation.id, conversation);
    this.activeConversationId = conversation.id;
    this.activeConversationTitle = conversation.title;
    this.messages = this.collapseStackedAssistants(
      conversation.messages.map((message) => this.toUiMessage(message))
    );
    this.expandedProcessIds.clear();
    this.syncProcessExpansion();
    this.pendingReport = null;
    this.pendingChatFile = null;
    this.pendingUploads = [];
    this.trackProcessingReports();
    this.syncTypingUiFromJob(conversation.id);
    this.registerImagePreviewLoads();
    this.cdr.detectChanges();
    this.scrollToBottomSoon(true);
    this.focusInputSoon();
  }

  private async bootstrapConversations(): Promise<void> {
    if (!isPlatformBrowser(this.platformId)) return;
    if (!this.canCallAssistantApis()) {
      if (this.chatAccess.isResolved() && !this.isChatAvailable) {
        this.abortAllConversationJobs();
      }
      return;
    }

    this.conversationsBootstrapped = false;
    this.abortAllConversationJobs();
    this.conversationsService.refreshScope();

    try {
      const store = await this.conversationsService.hydrateFromRemote();
      this.conversations = store.conversations;
      this.dedupeBlankConversations();

      if (!this.conversations.length) {
        const created = this.conversationsService.createConversation();
        this.conversations = [created];
        this.activeConversationId = created.id;
        this.activeConversationTitle = created.title;
        this.messages = [];
        this.persistStore();
        this.refreshQuickQuestions();
        return;
      }

      const activeId =
        store.activeConversationId ?? this.conversations[0]?.id ?? null;
      if (activeId && this.conversations.some((item) => item.id === activeId)) {
        this.loadConversation(activeId, false);
      } else if (this.conversations[0]) {
        this.loadConversation(this.conversations[0].id, false);
      }
    } finally {
      this.conversationsBootstrapped = true;
      this.flushPendingLaunchText();
    }
  }

  toggleOpen(event?: MouseEvent): void {
    if (!this.isChatAvailable) {
      if (this.isOpen) this.close(event);
      return;
    }

    const opening = !this.isOpen;
    this.setOpen(opening, event);
    if (opening && event) {
      this.ensureFreshConversation();
    }
  }

  private setOpen(open: boolean, event?: MouseEvent): void {
    if (!this.isChatAvailable) return;
    if (this.isOpen === open) {
      return;
    }

    this.isOpen = open;
    this.chatbotLauncher.setPanelOpen(this.isOpen);
    if (this.isOpen) {
      this.clearDragFlags();
      this.reclampSavedLayout();
      this.applyOpenPreferences();
      this.loadSlashCatalog();
      this.scrollToBottomSoon(true);
      this.focusInputSoon();
      if (this.messages.length === 0) {
        this.refreshQuickQuestions();
      }
      if (this.messages.length > 0) {
        setTimeout(() => this.scrollToBottomSoon(true), 80);
      }
    } else {
      this.settingsOpen = false;
      this.haltSpeech();
      this.abortVoiceSession();
      this.persistActiveConversation();
      this.persistPanelLayout();
      this.clearDragFlags();
    }
    this.updateBodyScrollLock();
    this.blurClickTarget(event);
  }

  private flushDeferredLaunch(): void {
    if (!this.deferredLaunch || !this.isChatAvailable) {
      return;
    }
    const request = this.deferredLaunch;
    this.deferredLaunch = null;
    this.handleLaunchRequest(request);
  }

  private handleLaunchRequest(request: ChatbotLaunchRequest): void {
    if (!this.isChatAvailable) {
      this.deferredLaunch = request;
      return;
    }

    if (request.mode === 'openAndSend') {
      this.openPanelAndSend(request.text, {
        mentions: request.mentions,
        expand: request.expand,
        conversationTitle: request.conversationTitle,
        attachments: request.attachments,
      });
      return;
    }

    if (request.mode === 'openAutomationRun') {
      this.setOpen(true);
      if (!this.conversationsBootstrapped) {
        this.pendingAutomationRunId = request.runId;
        return;
      }
      void this.openAutomationRunConversation(request.runId);
      return;
    }

    switch (request.mode) {
      case 'toggle':
        this.toggleOpen();
        break;
      case 'open':
      case 'openAndFocus':
        this.setOpen(true);
        break;
      case 'openNewConversation':
        this.setOpen(true);
        this.ensureFreshConversation();
        break;
    }
  }

  private openPanelAndSend(
    text: string,
    options: {
      mentions?: ChatMention[];
      expand?: boolean;
      conversationTitle?: string;
      attachments?: ChatAttachmentRef[];
    } = {}
  ): void {
    const prompt = text.trim();
    if (!prompt) return;

    this.setOpen(true);
    if (options.expand && !this.matchesMobileMedia()) {
      this.setExpanded(true);
    }

    if (!this.conversationsBootstrapped) {
      this.pendingLaunch = { text: prompt, ...options };
      return;
    }

    this.dispatchLaunchPrompt(prompt, options);
  }

  private flushPendingLaunchText(): void {
    const pending = this.pendingLaunch;
    if (pending) {
      this.pendingLaunch = null;
      this.dispatchLaunchPrompt(pending.text, pending);
    }

    const runId = this.pendingAutomationRunId;
    if (runId) {
      this.pendingAutomationRunId = null;
      this.pendingFreshConversation = false;
      void this.openAutomationRunConversation(runId);
      return;
    }

    if (this.pendingFreshConversation) {
      this.pendingFreshConversation = false;
      this.ensureFreshConversation();
    }
  }

  private ensureFreshConversation(): void {
    if (!this.settings.openNewConversationOnLaunch) {
      this.pendingFreshConversation = false;
      return;
    }

    if (!this.conversationsBootstrapped) {
      this.pendingFreshConversation = true;
      return;
    }

    const isEphemeral = Boolean(
      this.activeConversationId &&
        this.ephemeralConversations.has(this.activeConversationId)
    );
    if (!isEphemeral && this.messages.length === 0) {
      this.refreshQuickQuestions();
      return;
    }

    const existingBlank = this.findBlankConversation(this.activeConversationId);
    if (existingBlank) {
      this.focusBlankConversation(existingBlank);
      return;
    }

    this.startNewConversation();
  }

  private isActiveEphemeralConversation(): boolean {
    return Boolean(
      this.activeConversationId &&
        this.ephemeralConversations.has(this.activeConversationId)
    );
  }

  private isBlankConversationRecord(record: ChatConversationRecord): boolean {
    return (
      !this.ephemeralConversations.has(record.id) && record.messages.length === 0
    );
  }

  private findBlankConversation(
    excludeId?: string | null
  ): ChatConversationRecord | undefined {
    return this.conversations.find(
      (item) => item.id !== excludeId && this.isBlankConversationRecord(item)
    );
  }

  /** Mantém no máximo uma conversa em branco — reutiliza em vez de duplicar. */
  private dedupeBlankConversations(): void {
    const blanks = this.conversations.filter((item) =>
      this.isBlankConversationRecord(item)
    );
    if (blanks.length <= 1) return;

    blanks.sort((left, right) => right.updatedAt - left.updatedAt);
    const keepId = blanks[0].id;
    const removeIds = new Set(blanks.slice(1).map((item) => item.id));

    for (const id of removeIds) {
      this.abortConversationJob(id);
      this.outgoingQueues.delete(id);
      this.conversationsService.syncDelete(id);
    }

    this.conversations = this.conversations.filter((item) => !removeIds.has(item.id));

    if (this.activeConversationId && removeIds.has(this.activeConversationId)) {
      this.activeConversationId = keepId;
      this.loadConversation(keepId, false);
    }

    this.persistStore();
  }

  private resetComposerState(): void {
    this.draft = '';
    this.draftMentions = [];
    this.refreshDraftHighlight();
    this.closeSlashMenu();
    this.resetComposerHeight();
    this.pendingUploads = [];
    this.expandedProcessIds.clear();
    this.stopAllReportPollers();
  }

  private focusBlankConversation(
    record: ChatConversationRecord,
    event?: MouseEvent
  ): void {
    this.abortVoiceSession();
    this.headerMenuOpen = false;
    this.settingsOpen = false;
    this.pendingConfirmation = null;
    this.pendingReport = null;
    this.pendingChatFile = null;
    if (this.activeConversationId) {
      this.flushTypewriter(this.activeConversationId);
    }
    this.persistActiveConversation();

    if (this.activeConversationId !== record.id) {
      this.loadConversation(record.id, true);
    } else {
      this.messages = [];
      this.syncTypingUiFromJob(record.id);
      this.markConversationRead(record.id);
    }

    this.activeConversationTitle = record.title;
    this.resetComposerState();
    this.persistActiveConversation();
    this.cdr.detectChanges();
    this.refreshQuickQuestions();
    this.focusInputSoon();
    this.blurClickTarget(event);
  }

  private dispatchLaunchPrompt(
    prompt: string,
    options: {
      mentions?: ChatMention[];
      expand?: boolean;
      conversationTitle?: string;
      attachments?: ChatAttachmentRef[];
    } = {}
  ): void {
    if (options.expand && !this.matchesMobileMedia()) {
      this.setExpanded(true);
    }

    if (this.messages.length > 0) {
      this.startNewConversation();
    }

    if (options.conversationTitle?.trim()) {
      this.activeConversationTitle = options.conversationTitle.trim();
    }

    this.dispatchOrQueue(prompt, false, options.mentions, options.attachments);
  }

  /** Rótulo do atalho principal (SO-aware) para tooltips e hints. */
  get chatOpenShortcutLabel(): string {
    if (!isPlatformBrowser(this.platformId)) return 'Ctrl+Shift+I';
    const isMac = /Mac|iPhone|iPad/i.test(navigator.platform);
    return isMac ? '⌘⇧I' : 'Ctrl+Shift+I';
  }

  close(event?: MouseEvent): void {
    if (this.ignoreCloseFromDrag) {
      event?.preventDefault();
      event?.stopPropagation();
      return;
    }

    this.persistActiveConversation();
    this.haltSpeech();
    this.abortVoiceSession();
    if (this.showSidebar && this.panelLayout?.width) {
      this.adjustPanelWidthForSidebar(false);
      this.showSidebar = false;
    }
    this.persistPanelLayout();
    this.isOpen = false;
    this.chatbotLauncher.setPanelOpen(false);
    this.headerMenuOpen = false;
    this.settingsOpen = false;
    this.closeSlashMenu();
    this.pendingConfirmation = null;
    this.clearDragFlags();
    this.setExpanded(false);
    this.updateBodyScrollLock();
    this.blurClickTarget(event);
  }

  toggleExpanded(event?: MouseEvent): void {
    if (this.matchesMobileMedia()) return;

    this.headerMenuOpen = false;
    this.clearDragFlags();
    this.setExpanded(!this.isExpanded);
    if (!this.isExpanded) {
      this.reclampSavedLayout();
    }
    this.scrollToBottomSoon(true);
    this.focusInputSoon();
    this.blurClickTarget(event);
  }

  onDragHandlePointerDown(event: PointerEvent): void {
    if (!this.isOpen || this.matchesMobileMedia() || this.isExpanded || this.isResizing || this.isResizingSidebar) return;

    const handle = event.currentTarget as HTMLElement | null;
    const panel = this.chatPanel?.nativeElement;
    if (!handle || !panel) return;

    this.dragPending = true;
    this.dragMoved = false;
    this.dragStartPoint = { x: event.clientX, y: event.clientY };
    handle.setPointerCapture(event.pointerId);
    event.preventDefault();
  }

  onDragHandlePointerMove(event: PointerEvent): void {
    if (!this.dragPending) return;

    const panel = this.chatPanel?.nativeElement;
    if (!panel) return;

    if (!this.isDragging) {
      const deltaX = event.clientX - this.dragStartPoint.x;
      const deltaY = event.clientY - this.dragStartPoint.y;
      if (Math.hypot(deltaX, deltaY) < DRAG_THRESHOLD_PX) return;

      const rect = panel.getBoundingClientRect();
      this.dragOffset = {
        x: this.dragStartPoint.x - rect.left,
        y: this.dragStartPoint.y - rect.top,
      };
      this.isDragging = true;
      this.dragMoved = true;
    }

    this.applyPanelLayout(
      event.clientX - this.dragOffset.x,
      event.clientY - this.dragOffset.y,
      this.panelLayout?.width,
      this.panelLayout?.height
    );
    this.cdr.markForCheck();
  }

  onDragHandlePointerUp(event: PointerEvent): void {
    if (!this.dragPending) return;

    const handle = event.currentTarget as HTMLElement | null;
    handle?.releasePointerCapture(event.pointerId);

    if (this.dragMoved) {
      this.syncLayoutFromDom();
      this.persistPanelLayout();
      this.ignoreCloseFromDrag = true;
      setTimeout(() => {
        this.ignoreCloseFromDrag = false;
      }, 0);
    }

    this.dragPending = false;
    this.isDragging = false;
    this.dragMoved = false;
  }

  onResizeHandlePointerDown(edge: ResizeEdge, event: PointerEvent): void {
    if (!this.isOpen || this.matchesMobileMedia() || this.isExpanded || this.isDragging || this.isResizingSidebar) return;

    const handle = event.currentTarget as HTMLElement | null;
    const panel = this.chatPanel?.nativeElement;
    if (!handle || !panel) return;

    const rect = panel.getBoundingClientRect();
    this.resizeEdge = edge;
    this.isResizing = true;
    this.resizeStart = {
      x: rect.left,
      y: rect.top,
      width: rect.width,
      height: rect.height,
      pointerX: event.clientX,
      pointerY: event.clientY,
    };
    handle.setPointerCapture(event.pointerId);
    event.preventDefault();
    event.stopPropagation();
  }

  onResizeHandlePointerMove(event: PointerEvent): void {
    if (!this.isResizing || !this.resizeEdge) return;

    const deltaX = event.clientX - this.resizeStart.pointerX;
    const deltaY = event.clientY - this.resizeStart.pointerY;
    const edge = this.resizeEdge;

    let x = this.resizeStart.x;
    let y = this.resizeStart.y;
    let width = this.resizeStart.width;
    let height = this.resizeStart.height;

    if (edge.includes('e')) width = this.resizeStart.width + deltaX;
    if (edge.includes('s')) height = this.resizeStart.height + deltaY;
    if (edge.includes('w')) {
      width = this.resizeStart.width - deltaX;
      x = this.resizeStart.x + deltaX;
    }
    if (edge.includes('n')) {
      height = this.resizeStart.height - deltaY;
      y = this.resizeStart.y + deltaY;
    }

    const size = this.clampPanelSize(width, height);
    if (edge.includes('w')) {
      x = this.resizeStart.x + (this.resizeStart.width - size.width);
    }
    if (edge.includes('n')) {
      y = this.resizeStart.y + (this.resizeStart.height - size.height);
    }

    this.applyPanelLayout(x, y, size.width, size.height);
    this.cdr.markForCheck();
  }

  onResizeHandlePointerUp(event: PointerEvent): void {
    if (!this.isResizing) return;

    const handle = event.currentTarget as HTMLElement | null;
    handle?.releasePointerCapture(event.pointerId);
    this.persistPanelLayout();
    this.isResizing = false;
    this.resizeEdge = null;
  }

  onSidebarResizePointerDown(event: PointerEvent): void {
    if (!this.isOpen || this.matchesMobileMedia() || this.isExpanded || this.isDragging || this.isResizing) {
      return;
    }

    const handle = event.currentTarget as HTMLElement | null;
    if (!handle) return;

    this.headerMenuOpen = false;
    this.isResizingSidebar = true;
    this.sidebarResizeStart = {
      pointerX: event.clientX,
      width: this.sidebarWidth,
    };
    handle.setPointerCapture(event.pointerId);
    event.preventDefault();
    event.stopPropagation();
  }

  onSidebarResizePointerMove(event: PointerEvent): void {
    if (!this.isResizingSidebar) return;

    const zoom = this.getAppZoom();
    const delta = (event.clientX - this.sidebarResizeStart.pointerX) / zoom;
    this.sidebarWidth = this.clampSidebarWidth(this.sidebarResizeStart.width + delta);
    this.cdr.markForCheck();
  }

  onSidebarResizePointerUp(event: PointerEvent): void {
    if (!this.isResizingSidebar) return;

    const handle = event.currentTarget as HTMLElement | null;
    handle?.releasePointerCapture(event.pointerId);
    this.isResizingSidebar = false;
    this.scheduleSyncLayoutFromDom();
  }

  toggleHeaderMenu(event?: MouseEvent): void {
    event?.stopPropagation();
    this.headerMenuOpen = !this.headerMenuOpen;
    this.blurClickTarget(event);
  }

  openSettings(event?: MouseEvent): void {
    event?.stopPropagation();
    if (!this.canUseAgentCommands) return;
    this.headerMenuOpen = false;
    this.settingsOpen = true;
    this.closeSlashMenu();
    this.closeModelPicker();
    this.loadMemories();
    this.blurClickTarget(event);
  }

  toggleSettings(event?: MouseEvent): void {
    if (!this.canUseAgentCommands) return;
    if (this.settingsOpen) {
      this.closeSettings(event);
      return;
    }
    this.openSettings(event);
  }

  closeSettings(event?: MouseEvent): void {
    this.settingsOpen = false;
    this.focusInputSoon();
    this.blurClickTarget(event);
  }

  setFontSize(fontSize: ChatFontSize, event?: MouseEvent): void {
    this.patchSettings({ fontSize });
    this.blurClickTarget(event);
  }

  setResponseStyle(responseStyle: ChatResponseStyle, event?: MouseEvent): void {
    this.patchSettings({ responseStyle });
    this.blurClickTarget(event);
  }

  toggleSetting(
    key:
      | 'openWithSidebar'
      | 'openNewConversationOnLaunch'
      | 'keepSidebarOnSelect'
      | 'enterToSend'
      | 'showKeyboardHints'
      | 'compactDensity'
      | 'showTimestamps'
      | 'expandProcessByDefault'
      | 'ttsEnabled'
      | 'voiceLoopEnabled'
  ): void {
    this.patchSettings({ [key]: !this.settings[key] });
    if (key === 'ttsEnabled' && !this.settings.ttsEnabled) {
      this.haltSpeech();
    }
    if (key === 'voiceLoopEnabled' && !this.settings.voiceLoopEnabled) {
      this.voiceLoopArmed = false;
      this.abortVoiceSession();
    }
  }

  resetSettings(event?: MouseEvent): void {
    this.settings = this.settingsService.reset();
    this.applySettingsSideEffects({
      openWithSidebar: this.settings.openWithSidebar,
      expandProcessByDefault: this.settings.expandProcessByDefault,
    });
    this.blurClickTarget(event);
  }

  formatMessageTime(timestamp: number): string {
    const date = new Date(timestamp);
    const now = new Date();
    const time = date.toLocaleTimeString('pt-BR', {
      hour: '2-digit',
      minute: '2-digit',
    });

    if (this.isSameCalendarDay(date, now)) {
      return `Hoje · ${time}`;
    }

    const yesterday = new Date(now);
    yesterday.setDate(now.getDate() - 1);
    if (this.isSameCalendarDay(date, yesterday)) {
      return `Ontem · ${time}`;
    }

    const dateOptions: Intl.DateTimeFormatOptions = {
      day: '2-digit',
      month: '2-digit',
    };
    if (date.getFullYear() !== now.getFullYear()) {
      dateOptions.year = 'numeric';
    }
    return `${date.toLocaleDateString('pt-BR', dateOptions)} · ${time}`;
  }

  isCopied(key: string): boolean {
    return this.copiedKey === key;
  }

  copyMessage(message: ChatMessage, event?: Event): void {
    event?.preventDefault();
    event?.stopPropagation();
    void this.copyToClipboard(`msg:${message.id}`, this.messageCopyText(message));
  }

  copyReport(report: ChatReportJob, event?: Event): void {
    event?.preventDefault();
    event?.stopPropagation();
    void this.copyToClipboard(`report:${report.id}`, this.reportCopyText(report));
  }

  copyConfirmation(confirmation: ChatConfirmation, event?: Event): void {
    event?.preventDefault();
    event?.stopPropagation();
    void this.copyToClipboard(
      `confirm:${confirmation.id}`,
      this.confirmationCopyText(confirmation)
    );
  }

  private isSameCalendarDay(left: Date, right: Date): boolean {
    return (
      left.getFullYear() === right.getFullYear() &&
      left.getMonth() === right.getMonth() &&
      left.getDate() === right.getDate()
    );
  }

  private messageCopyText(message: ChatMessage): string {
    return (message.displayText ?? message.text ?? '').trim();
  }

  private reportCopyText(report: ChatReportJob): string {
    const lines = [
      this.reportStatusLabel(report),
      report.name?.trim(),
      this.reportCountLabel(report),
      report.description?.trim(),
      ...(report.appliedFilters ?? []).map(
        (filter) => `${filter.label}: ${filter.value}`
      ),
    ];
    return lines.filter((line): line is string => Boolean(line)).join('\n');
  }

  private confirmationCopyText(confirmation: ChatConfirmation): string {
    const lines = [
      confirmation.agentName ? `Solicitado por ${confirmation.agentName}` : '',
      confirmation.title?.trim() || confirmation.label?.trim(),
      confirmation.summary?.trim(),
      ...(confirmation.details ?? []).map(
        (detail) => `${detail.label}: ${detail.value}`
      ),
    ];
    return lines.filter((line) => Boolean(line)).join('\n');
  }

  private async copyToClipboard(key: string, text: string): Promise<void> {
    if (!isPlatformBrowser(this.platformId)) return;

    const value = text.trim();
    if (!value) return;

    try {
      await navigator.clipboard.writeText(value);
      this.copiedKey = key;
      this.clearCopiedTimer();
      this.copiedTimer = setTimeout(() => {
        if (this.copiedKey === key) {
          this.copiedKey = null;
          this.cdr.markForCheck();
        }
      }, 1600);
      this.cdr.markForCheck();
    } catch {
      this.copiedKey = null;
      this.cdr.markForCheck();
    }
  }

  private clearCopiedTimer(): void {
    if (this.copiedTimer) {
      clearTimeout(this.copiedTimer);
      this.copiedTimer = null;
    }
  }

  formatMessageIso(timestamp: number): string {
    return new Date(timestamp).toISOString();
  }

  private patchSettings(patch: Partial<ChatbotSettings>): void {
    this.settings = this.settingsService.save(patch);
    this.applySettingsSideEffects(patch);
    this.cdr.markForCheck();
  }

  private applySettingsSideEffects(patch: Partial<ChatbotSettings>): void {
    if ('openWithSidebar' in patch && this.isOpen && !this.matchesMobileMedia()) {
      this.setSidebarOpen(!!patch.openWithSidebar);
    }

    if (patch.expandProcessByDefault === false) {
      this.expandedProcessIds.clear();
    } else if (patch.expandProcessByDefault === true) {
      this.syncProcessExpansion();
    }
  }

  private applyOpenPreferences(): void {
    if (this.matchesMobileMedia()) {
      this.setSidebarOpen(false);
      return;
    }
    if (this.settings.openWithSidebar) {
      this.setSidebarOpen(true);
    }
  }

  private syncProcessExpansion(): void {
    if (!this.settings.expandProcessByDefault) return;

    for (const message of this.messages) {
      if (message.process?.length) {
        this.expandedProcessIds.add(message.id);
      }
    }
  }

  onDocumentPointerDown(event: PointerEvent): void {
    const target = event.target;

    if (this.headerMenuOpen) {
      const root = this.headerMore?.nativeElement;
      if (!(root && target instanceof Node && root.contains(target))) {
        this.headerMenuOpen = false;
        this.cdr.markForCheck();
      }
    }

    if (this.modelPickerOpen && target instanceof Node) {
      const picker = this.hostElement.nativeElement.querySelector(
        '#chatbot-model-picker'
      );
      if (!(picker instanceof HTMLElement && picker.contains(target))) {
        this.closeModelPicker();
        this.cdr.markForCheck();
      }
    }

    this.closeIfPointerOutside(event);
  }

  onBackdropClick(event?: MouseEvent): void {
    if (this.shouldIgnoreOutsideClose()) return;
    this.close(event);
  }

  private clearDragFlags(): void {
    this.dragPending = false;
    this.isDragging = false;
    this.dragMoved = false;
    this.isResizing = false;
    this.resizeEdge = null;
    this.isResizingSidebar = false;
    this.ignoreCloseFromDrag = false;
  }

  private closeIfPointerOutside(event: PointerEvent): void {
    if (!this.isOpen || event.button !== 0) return;
    if (this.shouldIgnoreOutsideClose()) return;
    if (!(event.target instanceof Node)) return;
    if (this.hostElement.nativeElement.contains(event.target)) return;

    this.close();
  }

  private shouldIgnoreOutsideClose(): boolean {
    return (
      this.isDragging ||
      this.isResizing ||
      this.isResizingSidebar ||
      this.dragPending ||
      this.ignoreCloseFromDrag ||
      Date.now() < this.ignoreOutsideCloseUntil
    );
  }

  private armOutsideCloseGuard(ms = 800): void {
    this.ignoreOutsideCloseUntil = Date.now() + ms;
  }

  private matchesMobileMedia(): boolean {
    if (!isPlatformBrowser(this.platformId)) return false;
    return window.matchMedia(`(max-width: ${MOBILE_BREAKPOINT_PX - 1}px)`).matches;
  }

  private getAppZoom(): number {
    if (!isPlatformBrowser(this.platformId)) return 1;

    const root = document.querySelector('app-root');
    const raw = String(getComputedStyle(root ?? document.body).zoom || '1').trim();
    if (raw.endsWith('%')) {
      const percent = Number.parseFloat(raw);
      return Number.isFinite(percent) && percent > 0 ? percent / 100 : 1;
    }

    const value = Number.parseFloat(raw);
    return Number.isFinite(value) && value > 0 ? value : 1;
  }

  private viewportSize(): { width: number; height: number } {
    return {
      width: window.visualViewport?.width ?? window.innerWidth,
      height: window.visualViewport?.height ?? window.innerHeight,
    };
  }

  private clampPanelSize(width: number, height: number): { width: number; height: number } {
    const { width: viewportWidth, height: viewportHeight } = this.viewportSize();
    const maxWidth = Math.max(
      PANEL_MIN_WIDTH_PX,
      viewportWidth - PANEL_POSITION_MARGIN_PX * 2
    );
    const maxHeight = Math.max(
      PANEL_MIN_HEIGHT_PX,
      viewportHeight - PANEL_POSITION_MARGIN_PX * 2
    );

    return {
      width: Math.min(Math.max(PANEL_MIN_WIDTH_PX, width), maxWidth),
      height: Math.min(Math.max(PANEL_MIN_HEIGHT_PX, height), maxHeight),
    };
  }

  private clampSidebarWidth(width: number): number {
    return Math.min(Math.max(SIDEBAR_MIN_WIDTH_PX, Math.round(width)), SIDEBAR_MAX_WIDTH_PX);
  }

  private clampVisualPosition(
    x: number,
    y: number,
    width?: number,
    height?: number
  ): { x: number; y: number } {
    const rect = this.chatPanel?.nativeElement?.getBoundingClientRect();
    const panelWidth = width ?? this.panelLayout?.width ?? rect?.width ?? 500;
    const panelHeight = height ?? this.panelLayout?.height ?? rect?.height ?? 600;
    const { width: viewportWidth, height: viewportHeight } = this.viewportSize();
    const maxX = Math.max(
      PANEL_POSITION_MARGIN_PX,
      viewportWidth - panelWidth - PANEL_POSITION_MARGIN_PX
    );
    const maxY = Math.max(
      PANEL_POSITION_MARGIN_PX,
      viewportHeight - panelHeight - PANEL_POSITION_MARGIN_PX
    );

    return {
      x: Math.min(Math.max(PANEL_POSITION_MARGIN_PX, x), maxX),
      y: Math.min(Math.max(PANEL_POSITION_MARGIN_PX, y), maxY),
    };
  }

  private applyPanelLayout(
    x: number,
    y: number,
    width?: number,
    height?: number
  ): void {
    const hasSize = typeof width === 'number' && typeof height === 'number';
    const size = hasSize
      ? this.clampPanelSize(width, height)
      : { width: this.panelLayout?.width, height: this.panelLayout?.height };
    const position = this.clampVisualPosition(x, y, size.width, size.height);

    this.panelLayout = {
      ...position,
      width: size.width,
      height: size.height,
      sidebarWidth: this.sidebarWidth,
    };
  }

  private syncLayoutFromDom(): void {
    const rect = this.chatPanel?.nativeElement?.getBoundingClientRect();
    if (!rect) return;

    this.applyPanelLayout(rect.left, rect.top, rect.width, rect.height);
  }

  private scheduleSyncLayoutFromDom(): void {
    if (!isPlatformBrowser(this.platformId) || this.matchesMobileMedia()) return;

    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        this.syncLayoutFromDom();
        this.persistPanelLayout();
        this.cdr.markForCheck();
      });
    });
  }

  private restorePanelLayout(): void {
    if (!isPlatformBrowser(this.platformId) || this.matchesMobileMedia()) return;

    try {
      const layoutRaw = localStorage.getItem(PANEL_LAYOUT_STORAGE_KEY);
      const positionRaw = localStorage.getItem(PANEL_POSITION_STORAGE_KEY);
      const raw = layoutRaw ?? positionRaw;
      if (!raw) return;

      const parsed = JSON.parse(raw) as PanelLayout;
      if (typeof parsed?.sidebarWidth === 'number') {
        this.sidebarWidth = this.clampSidebarWidth(parsed.sidebarWidth);
      }

      if (typeof parsed?.x !== 'number' || typeof parsed?.y !== 'number') return;

      this.applyPanelLayout(parsed.x, parsed.y, parsed.width, parsed.height);
    } catch {
      this.panelLayout = null;
    }
  }

  private persistPanelLayout(): void {
    if (!isPlatformBrowser(this.platformId) || this.matchesMobileMedia()) {
      return;
    }

    const layout = this.layoutForPersistence();
    if (!layout) return;

    localStorage.setItem(PANEL_LAYOUT_STORAGE_KEY, JSON.stringify(layout));
  }

  private reclampSavedLayout(): void {
    if (!this.panelLayout || this.matchesMobileMedia()) return;

    this.applyPanelLayout(
      this.panelLayout.x,
      this.panelLayout.y,
      this.panelLayout.width,
      this.panelLayout.height
    );
    this.persistPanelLayout();
  }

  toggleSidebar(event?: MouseEvent): void {
    this.setSidebarOpen(!this.showSidebar, event);
  }

  private setSidebarOpen(open: boolean, event?: MouseEvent): void {
    if (this.showSidebar === open) {
      this.blurClickTarget(event);
      return;
    }

    this.headerMenuOpen = false;
    this.showSidebar = open;

    if (!this.matchesMobileMedia() && !this.isExpanded) {
      if (this.panelLayout?.width) {
        this.adjustPanelWidthForSidebar(open);
        this.persistPanelLayout();
        this.cdr.markForCheck();
      } else {
        this.scheduleSyncLayoutFromDom();
      }
    }

    this.blurClickTarget(event);
  }

  private shouldExpandPanelLeft(width: number): boolean {
    const { width: viewportWidth } = this.viewportSize();
    const margin = PANEL_POSITION_MARGIN_PX;

    if (typeof this.panelLayout?.x !== 'number') {
      return true;
    }

    const rightEdge = this.panelLayout.x + width;
    return rightEdge >= viewportWidth - margin - 32;
  }

  private adjustPanelWidthForSidebar(opening: boolean): void {
    if (this.matchesMobileMedia() || this.isExpanded) return;
    if (!this.panelLayout?.width || !this.panelLayout.height) return;

    const delta = opening ? this.sidebarWidth : -this.sidebarWidth;
    const nextWidth = this.panelLayout.width + delta;
    const expandLeft = this.shouldExpandPanelLeft(this.panelLayout.width);
    const nextX = expandLeft ? this.panelLayout.x - delta : this.panelLayout.x;

    this.applyPanelLayout(
      nextX,
      this.panelLayout.y,
      nextWidth,
      this.panelLayout.height
    );
  }

  private layoutForPersistence(): PanelLayout | null {
    if (!this.panelLayout) return null;

    const layout: PanelLayout = {
      ...this.panelLayout,
      sidebarWidth: this.sidebarWidth,
    };

    if (!this.showSidebar || !layout.width) return layout;

    const expandLeft = this.shouldExpandPanelLeft(layout.width);
    return {
      ...layout,
      width: layout.width - this.sidebarWidth,
      x: expandLeft ? layout.x + this.sidebarWidth : layout.x,
    };
  }

  startNewConversation(event?: MouseEvent): void {
    const activeRecord = this.conversations.find(
      (item) => item.id === this.activeConversationId
    );

    if (
      this.messages.length === 0 &&
      !this.isActiveEphemeralConversation() &&
      activeRecord
    ) {
      this.focusBlankConversation(activeRecord, event);
      return;
    }

    const existingBlank = this.findBlankConversation();
    if (existingBlank) {
      this.focusBlankConversation(existingBlank, event);
      return;
    }

    this.abortVoiceSession();
    this.headerMenuOpen = false;
    this.settingsOpen = false;
    this.pendingConfirmation = null;
    this.pendingReport = null;
    this.pendingChatFile = null;
    if (this.activeConversationId) {
      this.flushTypewriter(this.activeConversationId);
    }
    this.persistActiveConversation();

    const created = this.conversationsService.createConversation();
    this.conversations = [created, ...this.conversations];
    this.activeConversationId = created.id;
    this.activeConversationTitle = created.title;
    this.messages = [];
    this.resetComposerState();
    this.syncTypingUiFromJob(created.id);
    this.persistActiveConversation();
    this.cdr.detectChanges();
    this.refreshQuickQuestions();
    this.focusInputSoon();
    this.blurClickTarget(event);
  }

  selectConversation(conversationId: string, event?: MouseEvent): void {
    if (conversationId === this.activeConversationId) {
      this.blurClickTarget(event);
      return;
    }

    this.persistActiveConversation();
    this.abortVoiceSession();
    this.pendingConfirmation = null;
    this.pendingReport = null;
    this.pendingChatFile = null;
    this.loadConversation(conversationId, true);
    if (!this.settings.keepSidebarOnSelect) {
      this.setSidebarOpen(false);
    }
    this.blurClickTarget(event);
  }

  deleteConversation(conversationId: string, event: MouseEvent): void {
    event.stopPropagation();
    this.abortConversationJob(conversationId);
    this.outgoingQueues.delete(conversationId);

    const store = this.conversationsService.loadStore();
    const nextStore = this.conversationsService.deleteConversation(
      store,
      conversationId
    );

    if (!nextStore.conversations.length) {
      const created = this.conversationsService.createConversation();
      nextStore.conversations = [created];
      nextStore.activeConversationId = created.id;
    }

    this.conversationsService.saveStore(nextStore);
    this.conversationsService.syncDelete(conversationId);
    this.conversations = nextStore.conversations;

    if (this.activeConversationId === conversationId) {
      const nextId = nextStore.activeConversationId!;
      this.loadConversation(nextId, true);
    }

    this.cdr.detectChanges();
  }

  private loadConversation(conversationId: string, scroll: boolean): void {
    const conversation = this.conversations.find(
      (item) => item.id === conversationId
    );
    if (!conversation) return;

    const previousId = this.activeConversationId;
    if (previousId && previousId !== conversationId) {
      this.flushTypewriter(previousId);
    }

    this.activeConversationId = conversation.id;
    this.activeConversationTitle = conversation.title;
    this.messages = this.collapseStackedAssistants(
      conversation.messages.map((message) => this.toUiMessage(message))
    );
    this.expandedProcessIds.clear();
    this.syncProcessExpansion();
    this.pendingReport = null;
    this.pendingChatFile = null;
    this.pendingUploads = [];
    this.trackProcessingReports();
    this.syncTypingUiFromJob(conversation.id);
    this.markConversationRead(conversation.id);
    this.persistStore();
    this.registerImagePreviewLoads();

    if (this.messages.length === 0) {
      this.refreshQuickQuestions();
    }

    if (scroll) {
      this.cdr.detectChanges();
      this.scrollToBottomSoon(true);
      this.focusInputSoon();
    }

    this.flushQueuedOutgoing();
  }

  private blurClickTarget(event?: MouseEvent): void {
    const target = event?.currentTarget;
    if (target instanceof HTMLElement) {
      target.blur();
    }
  }

  send(): void {
    if (this.isVoiceBusy || this.readOnlyShare) return;
    if (this.editingMessageId) {
      this.commitEditedMessage();
      return;
    }
    if (!this.canSend) return;
    this.closeSlashMenu();
    this.closeModelPicker();
    this.dispatchOrQueue(this.draft, true, this.draftMentions);
  }

  private commitEditedMessage(): void {
    const editingId = this.editingMessageId;
    if (!editingId) return;
    const index = this.messages.findIndex((item) => item.id === editingId);
    const text = this.draft.trim();
    this.editingMessageId = null;
    if (index < 0 || !text) return;
    this.messages = this.messages.slice(0, index);
    this.persistActiveConversation();
    this.dispatchOrQueue(text, true, this.draftMentions);
  }

  sendQuick(question: string): void {
    this.dispatchOrQueue(question, false);
  }

  queuedPreview(item: QueuedOutgoing): string {
    const text = item.text.replace(/\s+/g, ' ').trim();
    const autoCaption = /^(Analise o|Segue o)/.test(text);
    if (item.attachments.length && (!text || autoCaption)) {
      if (item.attachments.length === 1) return item.attachments[0].fileName;
      return `${item.attachments.length} arquivos`;
    }
    return text.length > 88 ? `${text.slice(0, 87)}…` : text;
  }

  removeQueuedOutgoing(id: string, event?: Event): void {
    event?.preventDefault();
    event?.stopPropagation();
    const conversationId = this.activeConversationId;
    if (!conversationId) return;
    const queue = this.outgoingQueues.get(conversationId);
    if (!queue?.length) return;
    const next = queue.filter((item) => item.id !== id);
    if (next.length) this.outgoingQueues.set(conversationId, next);
    else this.outgoingQueues.delete(conversationId);
  }

  toggleVoiceCapture(): void {
    if (this.isVoiceTranscribing || this.voiceStopping || this.isUploadingAttachments) {
      return;
    }

    if (this.isVoiceRecording || this.isVoiceStarting) {
      void this.stopVoiceAndTranscribe();
      return;
    }

    this.voiceLoopArmed = this.settings.voiceLoopEnabled;
    void this.startVoiceCapture();
  }

  cancelVoice(): void {
    this.voiceLoopArmed = false;
    this.haltSpeech();
    this.transcribeSub?.unsubscribe();
    this.transcribeSub = undefined;
    this.voiceRecorder?.cancel();
    this.voiceRecorder = null;
    this.voiceStopping = false;
    this.resetVoiceUi();
    this.cdr.detectChanges();
  }

  dismissVoiceError(): void {
    this.voiceError = null;
  }

  trackVoiceBar(index: number): number {
    return index;
  }

  private async startVoiceCapture(): Promise<void> {
    if (!isPlatformBrowser(this.platformId)) return;

    this.closeSlashMenu();
    this.voiceError = null;
    this.armOutsideCloseGuard(2000);

    if (!window.isSecureContext) {
      this.voiceError =
        'A fala precisa de conexão segura (HTTPS). Abra o site em um endereço seguro e tente de novo.';
      return;
    }

    if (!isVoiceCaptureSupported()) {
      this.voiceError =
        'Seu navegador não grava áudio. Use Chrome, Safari ou Edge atualizado.';
      this.voiceSupported = false;
      return;
    }

    this.isVoiceStarting = true;
    this.isVoiceRecording = false;
    this.voiceElapsedMs = 0;
    this.voiceLevels = idleVoiceLevels();

    const recorder = new ChatbotVoiceRecorder();
    this.voiceRecorder = recorder;

    let lastUiMs = -50;

    try {
      await this.ngZone.runOutsideAngular(() =>
        recorder.start((tick) => {
          if (
            tick.elapsedMs - lastUiMs < 50 &&
            tick.elapsedMs < VOICE_MAX_DURATION_MS
          ) {
            return;
          }
          lastUiMs = tick.elapsedMs;

          this.ngZone.run(() => {
            this.voiceElapsedMs = tick.elapsedMs;
            this.voiceLevels = tick.levels;

            if (
              tick.elapsedMs >= VOICE_MAX_DURATION_MS &&
              this.isVoiceRecording &&
              !this.voiceStopping
            ) {
              void this.stopVoiceAndTranscribe();
            }
          });
        })
      );

      this.isVoiceStarting = false;
      this.isVoiceRecording = true;
      this.cdr.detectChanges();
    } catch (error) {
      this.voiceRecorder = null;
      this.resetVoiceUi();
      this.voiceError = this.mapVoiceError(error);
      this.cdr.detectChanges();
    }
  }

  private async stopVoiceAndTranscribe(): Promise<void> {
    if (!this.voiceRecorder || this.voiceStopping || this.isVoiceTranscribing) {
      return;
    }

    this.voiceStopping = true;
    this.isVoiceStarting = false;
    this.isVoiceRecording = false;
    this.isVoiceTranscribing = true;
    this.cdr.detectChanges();

    try {
      const result = await this.voiceRecorder.stop();
      this.voiceRecorder = null;

      if (result.durationMs < VOICE_MIN_DURATION_MS || result.blob.size < 256) {
        this.voiceError = 'Fale um pouco mais e toque no microfone de novo.';
        this.resetVoiceUi();
        this.cdr.detectChanges();
        this.focusInputSoon();
        return;
      }

      if (result.blob.size > VOICE_MAX_BYTES) {
        this.voiceError = 'A gravação passou do limite. Fale em até 1 minuto.';
        this.resetVoiceUi();
        this.cdr.detectChanges();
        this.focusInputSoon();
        return;
      }

      const audioBase64 = await blobToBase64(result.blob);
      this.transcribeSub?.unsubscribe();
      this.transcribeSub = this.chatbotService
        .transcribe(audioBase64, result.mimeType)
        .pipe(takeUntil(this.destroy$))
        .subscribe({
          next: (response) => {
            this.applyTranscript(response.text);
          },
          error: (error) => {
            this.voiceError = this.mapVoiceError(error);
            this.resetVoiceUi();
            this.cdr.detectChanges();
            this.focusInputSoon();
          },
        });
    } catch (error) {
      this.voiceRecorder = null;
      this.voiceError = this.mapVoiceError(error);
      this.resetVoiceUi();
      this.cdr.detectChanges();
      this.focusInputSoon();
    }
  }

  private applyTranscript(text: string): void {
    const piece = text.trim();
    this.resetVoiceUi();

    if (!piece) {
      this.voiceError =
        'Não consegui entender. Fale de novo, mais perto do microfone.';
      this.cdr.detectChanges();
      this.focusInputSoon();
      return;
    }

    if (this.voiceLoopArmed && this.settings.voiceLoopEnabled) {
      this.dispatchOrQueue(piece, false);
      this.cdr.detectChanges();
      return;
    }

    const needsSpace = this.draft.length > 0 && !/\s$/.test(this.draft);
    this.draft = needsSpace ? `${this.draft} ${piece}` : `${this.draft}${piece}`;
    this.draftMentions = syncMentions(this.draft, this.draftMentions);
    this.refreshDraftHighlight();
    this.cdr.detectChanges();

    const textarea = this.messageInput?.nativeElement;
    if (textarea) {
      textarea.value = this.draft;
      this.autoResizeComposer(textarea);
    }

    this.focusInputSoon();
  }

  private abortVoiceSession(): void {
    this.transcribeSub?.unsubscribe();
    this.transcribeSub = undefined;
    this.voiceRecorder?.cancel();
    this.voiceRecorder = null;
    this.resetVoiceUi();
    this.voiceError = null;
  }

  private resetVoiceUi(): void {
    this.isVoiceStarting = false;
    this.isVoiceRecording = false;
    this.isVoiceTranscribing = false;
    this.voiceStopping = false;
    this.voiceElapsedMs = 0;
    this.voiceLevels = idleVoiceLevels();
  }

  private mapVoiceError(error: unknown): string {
    if (error instanceof HttpErrorResponse) {
      if (error.status === 429) {
        return 'Muitas falas em sequência. Aguarde um momento e tente de novo.';
      }
      if (error.status === 413) {
        return 'A gravação passou do limite. Fale em até 1 minuto.';
      }
      if (error.status === 400) {
        return (
          error.error?.message ||
          'Não consegui transcrever esse áudio. Tente falar de novo.'
        );
      }
      return 'Não foi possível transcrever agora. Tente de novo.';
    }

    const name = (error as { name?: string })?.name ?? '';
    const message = String((error as { message?: string })?.message ?? '');

    if (name === 'NotAllowedError' || name === 'PermissionDeniedError') {
      return 'Permita o microfone neste site nas configurações do navegador ou do celular.';
    }
    if (name === 'NotFoundError' || name === 'DevicesNotFoundError') {
      return 'Nenhum microfone encontrado. Conecte um microfone e tente de novo.';
    }
    if (name === 'NotReadableError' || name === 'TrackStartError') {
      return 'O microfone está em uso em outro aplicativo. Feche-o e tente de novo.';
    }
    if (name === 'SecurityError' || name === 'NotSupportedError') {
      return 'Seu navegador bloqueou a gravação neste contexto.';
    }
    if (name === 'TimeoutError' || message.toLowerCase().includes('timeout')) {
      return 'A transcrição demorou demais. Tente uma fala mais curta.';
    }
    if (message.includes('Nenhuma gravação ativa')) {
      return 'A gravação foi interrompida. Toque no microfone para tentar de novo.';
    }

    return 'Não foi possível gravar o áudio. Tente de novo.';
  }

  onDraftInput(event: Event): void {
    const textarea = event.target as HTMLTextAreaElement;
    this.draft = textarea.value;
    this.draftMentions = syncMentions(this.draft, this.draftMentions);
    this.refreshDraftHighlight();
    this.autoResizeComposer(textarea);
    this.refreshSlashMenu(textarea.selectionStart ?? this.draft.length);
  }

  onComposerScroll(event: Event): void {
    this.syncComposerHighlightScroll(event.target as HTMLTextAreaElement);
  }

  get showScrollToLatest(): boolean {
    return !this.followMessagesScroll && this.messages.length > 0 && this.isOpen;
  }

  scrollMessagesToLatest(): void {
    this.scrollToBottomSoon(true);
  }

  onMessagesScroll(): void {
    if (this.suppressMessagesScrollTracking) return;

    const el = this.messagesViewport?.nativeElement;
    if (!el) return;

    if (this.isMessagesNearBottom(el)) {
      this.followMessagesScroll = true;
      return;
    }

    this.releaseMessagesScrollPin();
  }

  onMessagesWheel(event: WheelEvent): void {
    if (event.deltaY < 0) {
      this.releaseMessagesScrollPin();
    }
  }

  onMessagesPointerDown(event: PointerEvent): void {
    if (event.pointerType === 'mouse' && event.button !== 0) return;
    this.messagesPointerStartY = event.clientY;
  }

  onMessagesPointerMove(event: PointerEvent): void {
    if (this.messagesPointerStartY == null) return;

    const delta = event.clientY - this.messagesPointerStartY;
    if (Math.abs(delta) < 6) return;

    // Arrastar para baixo revela mensagens acima → desprende do auto-scroll.
    if (delta > 0) {
      this.releaseMessagesScrollPin();
    }
    this.messagesPointerStartY = event.clientY;
  }

  onMessagesPointerUp(): void {
    this.messagesPointerStartY = null;
  }

  private releaseMessagesScrollPin(): void {
    if (!this.followMessagesScroll) return;
    this.followMessagesScroll = false;
    this.messagesScrollPinGeneration += 1;
  }

  onDraftSelect(event: Event): void {
    if (event instanceof KeyboardEvent && this.isSlashNavigationKey(event.key)) {
      return;
    }

    const textarea = event.target as HTMLTextAreaElement;
    this.ensureComposerCaretVisible(textarea);
    this.refreshSlashMenu(textarea.selectionStart ?? this.draft.length);
  }

  onInputKeydown(event: KeyboardEvent): void {
    if (this.handleSlashKeydown(event)) {
      return;
    }

    if (event.key !== 'Enter') return;

    if (this.settings.enterToSend) {
      if (!event.shiftKey) {
        event.preventDefault();
        this.send();
      }
      return;
    }

    if (event.ctrlKey || event.metaKey) {
      event.preventDefault();
      this.send();
    }
  }

  applySlashSelection(item?: SlashCommandItem): void {
    const selected = item ?? this.flatSlashItems[this.slashIndex];
    if (!selected) return;

    if (selected.kind === 'upload') {
      if (this.isSlashItemDisabled(selected)) return;
      this.removeSlashQueryFromDraft();
      this.closeSlashMenu();
      this.openFilePicker();
      return;
    }

    if (!this.canUseAgentCommands) return;

    const mention: ChatMention = {
      type: 'agent',
      id: selected.id,
      name: selected.mentionName ?? selected.title,
    };
    const caret =
      this.messageInput?.nativeElement.selectionStart ?? this.draft.length;
    let text = this.draft;
    let start = this.slashQuery?.start ?? caret;
    let end = caret;

    if (!this.slashQuery && caret > 0 && !/\s/.test(this.draft[caret - 1] ?? '')) {
      text = `${this.draft.slice(0, caret)} ${this.draft.slice(caret)}`;
      start = caret + 1;
      end = caret + 1;
    }

    const next = insertMentionToken(text, start, end, mention);

    this.draft = next.text;
    this.draftMentions = syncMentions(this.draft, [...this.draftMentions, mention]);
    this.refreshDraftHighlight();
    this.closeSlashMenu();

    this.cdr.detectChanges();

    const textarea = this.messageInput?.nativeElement;
    if (textarea) {
      textarea.value = this.draft;
      textarea.focus();
      textarea.setSelectionRange(next.caret, next.caret);
      this.autoResizeComposer(textarea);
    }
  }

  togglePlusMenu(): void {
    if (this.isVoiceBusy || this.readOnlyShare) return;
    if (this.slashOpen && this.slashSource === 'plus') {
      this.closeSlashMenu();
      this.focusComposer();
      return;
    }

    this.closeModelPicker();
    this.slashSource = 'plus';
    this.slashQuery = null;
    this.slashGroups = this.buildPlusMenuGroups();
    this.slashOpen = true;
    this.slashIndex = 0;
    this.focusComposer();
  }

  closeComposerMenu(): void {
    this.closeSlashMenu();
    this.closeModelPicker();
    this.focusComposer();
  }

  get flatSlashItems(): SlashCommandItem[] {
    return this.slashGroups.flatMap((group) => group.items);
  }

  get slashQueryLabel(): string {
    const trigger = this.slashQuery?.trigger ?? '/';
    const query = this.slashQuery?.query?.trim();
    return query ? `${trigger}${query}` : trigger;
  }

  get hasActiveSlashQuery(): boolean {
    return (
      (this.slashSource === 'slash' || this.slashSource === 'at') &&
      this.slashQuery !== null
    );
  }

  get paletteAriaLabel(): string {
    switch (this.slashSource) {
      case 'at':
        return 'Arquivos e comandos';
      case 'plus':
        return 'Adicionar ao chat';
      default:
        return 'Agentes do assistente';
    }
  }

  get paletteSearchHint(): string {
    switch (this.slashSource) {
      case 'at':
        return 'Digite para buscar arquivos e comandos';
      case 'plus':
        return 'Digite para buscar ou use / e @ no campo';
      default:
        return 'Digite para buscar agentes';
    }
  }

  get showPaletteGroupTitles(): boolean {
    return this.slashSource === 'plus' || this.slashGroups.length > 1;
  }

  get slashEmptyLabel(): string {
    if (this.slashSource === 'at') {
      return 'Nenhuma fonte encontrada.';
    }
    if (!this.slashItems.length && this.slashSource !== 'plus') {
      return 'Nenhum agente cadastrado.';
    }
    if (!this.availableSlashItems().length && this.slashSource === 'slash') {
      return 'Todos os agentes já estão nesta mensagem.';
    }
    if (this.slashSource === 'plus') {
      return 'Nenhum resultado encontrado.';
    }
    return `Nenhum agente para “${this.slashQueryLabel}”.`;
  }

  paletteItemTitle(item: SlashCommandItem): string {
    if (this.slashSource === 'slash' && item.kind === 'agent') {
      return `/${item.title}`;
    }
    return item.title;
  }

  isSlashItemDisabled(item: SlashCommandItem): boolean {
    return (
      item.kind === 'upload' &&
      item.id === 'upload-files' &&
      this.pendingUploads.length >= this.maxChatAttachments
    );
  }

  slashItemSubtitle(item: SlashCommandItem): string {
    return compactSlashSubtitle(item.subtitle);
  }

  get activeSlashItemId(): string | undefined {
    return this.flatSlashItems[this.slashIndex]?.id;
  }

  get activeSlashOptionId(): string | null {
    const id = this.activeSlashItemId;
    return id ? `chatbot-slash-option-${id}` : null;
  }

  @HostListener('document:keydown', ['$event'])
  onDocumentKeydown(event: KeyboardEvent): void {
    if (this.handleAdminShortcuts(event)) {
      return;
    }

    if (!this.isOpen) return;

    const fromComposer = this.messageInput?.nativeElement === event.target;
    if (this.slashOpen && !fromComposer && this.handleSlashKeydown(event)) {
      return;
    }

    if (event.key === 'Escape') {
      if (this.isVoiceBusy) {
        this.cancelVoice();
        return;
      }
      if (this.slashOpen) {
        this.closeSlashMenu();
        return;
      }
      if (this.modelPickerOpen) {
        this.closeModelPicker();
        return;
      }
      if (this.settingsOpen) {
        this.closeSettings();
        return;
      }
      if (this.pendingConfirmation) {
        this.closeConfirmationModal();
        return;
      }
      if (this.pendingReport) {
        this.closeReportModal();
        return;
      }
      if (this.pendingChatFile) {
        this.closeFileModal();
        return;
      }
      if (this.isExpanded) {
        this.setExpanded(false);
        return;
      }
      this.close();
    }
  }

  private setExpanded(expanded: boolean): void {
    this.isExpanded = expanded;
    this.updateBodyScrollLock();
  }

  private updateMobileState(): void {
    if (!isPlatformBrowser(this.platformId)) return;

    const wasMobile = this.isMobile;
    this.isMobile = this.matchesMobileMedia();

    if (wasMobile === this.isMobile) return;

    if (this.isMobile) {
      this.headerMenuOpen = false;
      this.clearDragFlags();
      if (this.isExpanded) {
        this.isExpanded = false;
      }
    } else {
      this.restorePanelLayout();
    }

    this.updateBodyScrollLock();
    this.cdr.markForCheck();
  }

  private updateBodyScrollLock(forceOff = false): void {
    if (!isPlatformBrowser(this.platformId)) return;

    const mobile = this.matchesMobileMedia();
    const lock = !forceOff && this.isOpen && (mobile || this.isExpanded);

    document.body.style.overflow = lock ? 'hidden' : '';
    // html { overflow-x: hidden } clipeia o calc(100vw / --app-scale) antes do zoom
    document.documentElement.style.overflow = lock && mobile ? 'visible' : lock ? 'hidden' : '';
  }

  private focusInputSoon(): void {
    if (!isPlatformBrowser(this.platformId)) return;
    setTimeout(() => this.messageInput?.nativeElement?.focus(), 0);
  }

  private scrollToBottomSoon(force = false): void {
    if (!isPlatformBrowser(this.platformId)) return;

    if (force) {
      this.followMessagesScroll = true;
      this.messagesScrollPinGeneration += 1;
    } else if (!this.followMessagesScroll) {
      return;
    }

    const generation = this.messagesScrollPinGeneration;

    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        if (generation !== this.messagesScrollPinGeneration) return;
        if (!this.followMessagesScroll) return;

        const el = this.messagesViewport?.nativeElement;
        if (!el) return;

        this.suppressMessagesScrollTracking = true;
        el.scrollTop = el.scrollHeight;

        requestAnimationFrame(() => {
          this.suppressMessagesScrollTracking = false;
        });
      });
    });
  }

  private isMessagesNearBottom(el: HTMLElement): boolean {
    const remaining = el.scrollHeight - el.scrollTop - el.clientHeight;
    return remaining <= this.messagesScrollBottomThreshold;
  }

  private dispatchOrQueue(
    rawText: string,
    clearDraft: boolean,
    mentions?: ChatMention[],
    attachments?: ChatAttachmentRef[]
  ): void {
    if (this.shouldQueueOutgoing()) {
      this.enqueueOutgoing(rawText, clearDraft, mentions, attachments);
      return;
    }
    this.sendText(rawText, clearDraft, undefined, mentions, attachments);
  }

  private shouldQueueOutgoing(): boolean {
    return this.isTyping || this.hasUnconsumedConfirmation();
  }

  private hasUnconsumedConfirmation(): boolean {
    const last = this.messages[this.messages.length - 1];
    return Boolean(
      last?.role === 'assistant' &&
        last.confirmations?.some((item) => !item.consumed)
    );
  }

  private enqueueOutgoing(
    rawText: string,
    clearDraft: boolean,
    mentions?: ChatMention[],
    presetAttachments?: ChatAttachmentRef[]
  ): void {
    const typed = rawText.trim();
    const attachments = presetAttachments ?? (clearDraft ? this.consumeReadyAttachments() : []);
    const text =
      typed ||
      (attachments.length
        ? attachments.length > 1
          ? 'Segue os arquivos anexados.'
          : 'Segue o arquivo anexado.'
        : '');
    if (!text) return;

    this.ensureActiveConversation();
    const conversationId = this.activeConversationId;
    if (!conversationId) return;

    if (clearDraft) {
      this.draft = '';
      this.draftMentions = [];
      this.refreshDraftHighlight();
      this.resetComposerHeight();
    }

    const queue = this.outgoingQueues.get(conversationId) ?? [];
    this.outgoingQueues.set(conversationId, [
      ...queue,
      {
        id: this.newId(),
        text,
        mentions: mentions?.length ? [...mentions] : undefined,
        attachments,
      },
    ]);
    this.cdr.detectChanges();
  }

  private flushQueuedOutgoing(): void {
    if (this.isTyping || this.hasUnconsumedConfirmation()) return;

    const conversationId = this.activeConversationId;
    if (!conversationId) return;

    const queue = this.outgoingQueues.get(conversationId);
    if (!queue?.length) return;

    const [next, ...rest] = queue;
    if (rest.length) this.outgoingQueues.set(conversationId, rest);
    else this.outgoingQueues.delete(conversationId);

    this.sendText(next.text, false, undefined, next.mentions, next.attachments);
  }

  private sendText(
    rawText: string,
    clearDraft: boolean,
    displayText?: string,
    mentions?: ChatMention[],
    presetAttachments?: ChatAttachmentRef[]
  ): void {
    const typed = rawText.trim();
    const attachments =
      presetAttachments ?? (clearDraft ? this.consumeReadyAttachments() : []);
    const text =
      typed ||
      (attachments.length
        ? attachments.length > 1
          ? 'Segue os arquivos anexados.'
          : 'Segue o arquivo anexado.'
        : '');
    if (!text || this.isTyping) return;

    this.ensureActiveConversation();

    const visibleText = (displayText ?? typed).trim();
    const files = attachments.map((item) => this.attachmentToChatFile(item));

    this.messages = [
      ...this.messages,
      {
        id: this.newId(),
        role: 'user',
        text,
        displayText: displayText?.trim() || undefined,
        html: this.renderUserHtml(visibleText || text, mentions),
        actions: [],
        confirmations: [],
        files: files.length ? files : undefined,
        attachments: attachments.length ? attachments : undefined,
        mentions: mentions?.length ? [...mentions] : undefined,
        createdAt: Date.now(),
      },
    ];

    if (clearDraft) {
      this.draft = '';
      this.draftMentions = [];
      this.refreshDraftHighlight();
      this.resetComposerHeight();
    }
    this.persistActiveConversation();
    this.cdr.detectChanges();
    this.scrollToBottomSoon(true);

    const conversationId = this.activeConversationId;
    if (!conversationId) return;

    const snapshotMessages = [...this.messages];
    const snapshotTitle = this.activeConversationTitle;
    const job = this.beginConversationJob(conversationId);
    this.scrollToBottomSoon(true);

    this.getAssistantReply(
      conversationId,
      snapshotMessages,
      snapshotTitle,
      job.abort.signal
    ).then((assistantMessage) => {
      this.completeConversationJob(
        conversationId,
        assistantMessage,
        job.abort.signal
      );
    });
  }

  processSummary(steps: ChatActivityStatus[] | undefined): string {
    if (!steps?.length) return 'Processo da IA';

    const tools = steps.filter((step) => step.phase === 'tool').length;
    const searches = steps.filter((step) => step.phase === 'searching').length;
    const memories = steps.filter((step) => step.phase === 'memory').length;
    const depthStep = [...steps]
      .reverse()
      .find((step) => step.phase === 'depth' && (step.depth || step.label));
    const namedAgents = [
      ...new Set(
        steps
          .filter((step) => !SILENT_THOUGHT_PHASES.has(step.phase))
          .map((step) => step.agentName?.trim())
          .filter((name): name is string => Boolean(name))
      ),
    ];
    const agentStarts = steps.filter((step) => step.phase === 'agent');
    const parts: string[] = [];

    if (depthStep) {
      const depthLabel =
        (depthStep.depth && DEPTH_SUMMARY[depthStep.depth]) ||
        depthStep.label ||
        '';
      if (depthLabel && depthLabel !== 'Calibrando profundidade…') {
        parts.push(depthLabel);
      }
    }

    if (namedAgents.length > 1) {
      parts.push(`${namedAgents.length} agentes em paralelo`);
    } else if (namedAgents.length === 1) {
      parts.push(`agente ${namedAgents[0]}`);
    } else if (agentStarts.length === 1) {
      parts.push(
        agentStarts[0].detail ? `agente ${agentStarts[0].detail}` : '1 agente'
      );
    } else if (agentStarts.length > 1) {
      parts.push(`${agentStarts.length} agentes em paralelo`);
    }
    if (tools) {
      parts.push(tools === 1 ? '1 ferramenta' : `${tools} ferramentas`);
    }
    if (searches) {
      parts.push(searches === 1 ? '1 busca' : `${searches} buscas`);
    }
    if (memories) {
      parts.push(memories === 1 ? '1 memória' : `${memories} memórias`);
    }

    return parts.join(' · ') || 'Processo da IA';
  }

  get liveAgentLanes(): ChatActivityStatus[] {
    const byAgent = new Map<string, ChatActivityStatus>();

    for (const step of this.activityLog) {
      if (!step.agentId || SILENT_THOUGHT_PHASES.has(step.phase)) continue;
      byAgent.set(step.agentId, step);
    }

    if (
      this.currentActivity?.agentId &&
      !SILENT_THOUGHT_PHASES.has(this.currentActivity.phase)
    ) {
      byAgent.set(this.currentActivity.agentId, this.currentActivity);
    }

    return [...byAgent.values()];
  }

  get previousThoughts(): ChatActivityStatus[] {
    if (!this.settings.expandProcessByDefault) return [];
    if (!this.activityLog.length) return [];

    const current = this.currentActivity;
    const visibleLog = this.activityLog.filter(
      (step) => !this.isHiddenProcessStep(step)
    );
    if (!current || this.isHiddenProcessStep(current)) return visibleLog;

    const last = visibleLog[visibleLog.length - 1];
    const same =
      last?.phase === current.phase &&
      last?.toolName === current.toolName &&
      last?.label === current.label &&
      last?.detail === current.detail &&
      last?.agentId === current.agentId;

    return same ? visibleLog.slice(0, -1) : visibleLog;
  }

  /** Rastro ao vivo empilhado (config) vs um passo por vez. */
  get showLiveProcessStack(): boolean {
    return this.settings.expandProcessByDefault;
  }

  get liveThoughtLabel(): string {
    if (!this.currentActivity || this.isHiddenLiveThought(this.currentActivity)) {
      return 'Pensando…';
    }
    return processStepLabel(this.currentActivity, { includeDetail: false });
  }

  get liveThoughtTitle(): string {
    if (!this.currentActivity || this.isHiddenLiveThought(this.currentActivity)) {
      return 'Pensando…';
    }
    return processStepActionLabel(this.currentActivity);
  }

  get liveThoughtAction(): string {
    if (!this.currentActivity || this.isHiddenLiveThought(this.currentActivity)) {
      return 'Pensando…';
    }
    return processStepActionLabel(this.currentActivity, { includeDetail: false });
  }

  get currentThoughtAgent(): string | null {
    if (this.isHiddenLiveThought(this.currentActivity)) return null;
    if (this.currentActivity?.phase === 'reflect') {
      return this.currentActivity.agentName?.trim() || 'Pensador Profundo';
    }
    if (this.liveAgentLanes.length !== 1) return null;
    return (
      this.currentActivity?.agentName?.trim() ||
      this.liveAgentLanes[0]?.agentName?.trim() ||
      null
    );
  }

  formatProcessStepLabel(step: ChatActivityStatus | null | undefined): string {
    if (!step) return '';
    return processStepLabel(step, { includeDetail: false });
  }

  formatLiveThoughtLabel(step: ChatActivityStatus | null | undefined): string {
    if (!step) return '';
    return processStepActionLabel(step, { includeDetail: false });
  }

  compactLiveProcessAriaLabel(message: ChatMessage): string {
    const step = this.compactLiveProcessStep(message);
    return step ? this.formatProcessStepLabel(step) : 'Processo da IA';
  }

  showActivityDetail(step: ChatActivityStatus | null | undefined): boolean {
    return hasDistinctActivityDetail(step);
  }

  isSilentThoughtStep(step: ChatActivityStatus | null | undefined): boolean {
    return Boolean(step && SILENT_THOUGHT_PHASES.has(step.phase));
  }

  /** Calibrador fica só no resumo colapsado — não aparece no rastro expandido. */
  isHiddenProcessStep(step: ChatActivityStatus | null | undefined): boolean {
    return step?.phase === 'depth' || step?.phase === 'writing';
  }

  /** Titulador / Calibrador: nunca pintam o pensamento ao vivo. */
  private isHiddenLiveThought(
    step: ChatActivityStatus | null | undefined
  ): boolean {
    return step?.phase === 'title' || step?.phase === 'depth';
  }

  visibleProcessSteps(
    steps: ChatActivityStatus[] | undefined
  ): ChatActivityStatus[] {
    return (steps ?? []).filter((step) => !this.isHiddenProcessStep(step));
  }

  isProcessOpen(messageId: string): boolean {
    return this.expandedProcessIds.has(messageId);
  }

  /** Só auto-abre o accordion persistido com a config ligada. */
  private shouldKeepProcessOpen(): boolean {
    return this.settings.expandProcessByDefault;
  }

  showCompactLiveProcess(_message: ChatMessage): boolean {
    return false;
  }

  showFullProcessTrace(_message: ChatMessage): boolean {
    return false;
  }

  compactLiveProcessStep(message: ChatMessage): ChatActivityStatus | null {
    if (!this.showCompactLiveProcess(message)) return null;

    if (this.liveAutomationRunIds.has(this.activeConversationId ?? '')) {
      const lastStep = message.process?.at(-1) ?? null;
      if (lastStep && !this.isHiddenLiveThought(lastStep)) {
        return lastStep;
      }
      if (this.currentActivity && !this.isHiddenLiveThought(this.currentActivity)) {
        return this.currentActivity;
      }
      return null;
    }

    const job = this.pendingJobs.get(this.activeConversationId ?? '');
    if (!job) return null;
    const current = this.isHiddenLiveThought(job.currentActivity)
      ? null
      : job.currentActivity;
    if (current) return current;

    for (let index = job.activityLog.length - 1; index >= 0; index -= 1) {
      const step = job.activityLog[index];
      if (!this.isHiddenLiveThought(step)) return step;
    }

    return null;
  }

  processStepAgent(step: ChatActivityStatus | null | undefined): string | null {
    if (!step || this.isHiddenProcessStep(step)) return null;
    if (step.phase === 'reflect') {
      return step.agentName?.trim() || 'Pensador Profundo';
    }
    if (step.phase === 'title') {
      return step.agentName?.trim() || 'Titulador de Conversas';
    }
    return step.agentName?.trim() || null;
  }

  private keepProcessOpen(messageId: string, process?: ChatActivityStatus[]): void {
    if (!process?.length || !this.shouldKeepProcessOpen()) return;
    this.expandedProcessIds.add(messageId);
  }

  toggleProcess(messageId: string, event?: Event): void {
    event?.preventDefault();
    event?.stopPropagation();

    if (this.expandedProcessIds.has(messageId)) {
      this.expandedProcessIds.delete(messageId);
    } else {
      this.expandedProcessIds.add(messageId);
    }
  }

  openAction(action: ChatAction | string): void {
    if (!isPlatformBrowser(this.platformId)) return;

    const target = (typeof action === 'string' ? action : action.url)?.trim();
    if (!target) return;

    if (this.isExternalAction(action)) {
      window.open(target, '_blank', 'noopener,noreferrer');
      return;
    }

    const path = target.startsWith('/') ? target : `/${target}`;
    this.close();
    void this.router.navigateByUrl(path);
  }

  onBubbleLinkClick(event: MouseEvent): void {
    if (!isPlatformBrowser(this.platformId)) return;

    const anchor = (event.target as HTMLElement).closest('a');
    if (!anchor || anchor.classList.contains('chatbot__cite')) return;

    const href = anchor.getAttribute('href')?.trim();
    if (!href || href.startsWith('#') || href.startsWith('mailto:')) return;

    if (
      anchor.getAttribute('target') === '_blank' &&
      this.isExternalAction(href)
    ) {
      return;
    }

    const internalPath = this.resolveInternalChatLink(href);
    if (!internalPath) return;

    event.preventDefault();
    event.stopPropagation();
    this.openAction(internalPath);
  }

  private resolveInternalChatLink(href: string): string | null {
    const trimmed = href.trim();
    if (trimmed.startsWith('/')) return trimmed;

    try {
      const url = new URL(trimmed);
      if (!this.isWosiAppHost(url.hostname)) return null;
      const path = `${url.pathname}${url.search}${url.hash}` || '/';
      return path;
    } catch {
      return null;
    }
  }

  private isWosiAppHost(hostname: string): boolean {
    const host = hostname.trim().toLowerCase();
    if (!host) return false;
    return (
      host === 'localhost' ||
      host === '127.0.0.1' ||
      host === 'wosi.com.br' ||
      host.endsWith('.wosi.com.br')
    );
  }

  isExternalAction(
    action: ChatAction | string | Pick<ChatAction, 'url' | 'target'>
  ): boolean {
    if (typeof action === 'string') {
      return /^https?:\/\//i.test(action.trim());
    }
    if (action.target === 'external') return true;
    if (action.target === 'internal') return false;
    return /^https?:\/\//i.test(action.url?.trim() ?? '');
  }

  openConfirmationModal(confirmation: ChatConfirmation): void {
    if (confirmation.consumed || this.isTyping) return;
    this.pendingConfirmation = confirmation;
  }

  closeConfirmationModal(): void {
    this.pendingConfirmation = null;
  }

  openReportModal(report: ChatReportJob): void {
    this.pendingReport = report;
  }

  closeReportModal(): void {
    this.pendingReport = null;
  }

  downloadReport(path: string | null | undefined): void {
    this.openCdnPath(path);
  }

  openFileModal(file: ChatFile): void {
    this.pendingChatFile = file;
  }

  closeFileModal(): void {
    this.pendingChatFile = null;
  }

  openChatFile(file: ChatFile | null | undefined): void {
    this.openCdnPath(file?.url);
  }

  downloadChatFile(file: ChatFile | null | undefined): void {
    if (!isPlatformBrowser(this.platformId) || !file?.url) return;

    const href = this.chatFilePreviewUrl(file);
    const link = document.createElement('a');
    link.href = href;
    link.download = file.name || 'arquivo';
    link.rel = 'noopener noreferrer';
    link.target = '_blank';
    link.click();
  }

  isChatImageFile(file: ChatFile): boolean {
    return isGeneratedImageFile(file);
  }

  readonly imageGenerationStatusLabel = IMAGE_GENERATION_STATUS_LABEL;
  readonly imageLoadingStatusLabel = IMAGE_LOADING_STATUS_LABEL;

  imagePreviewAttrKey(messageId: string, file: ChatFile): string {
    return this.imagePreviewKey(messageId, file);
  }

  isImageToolRunning(message: ChatMessage): boolean {
    if (message.role !== 'assistant') return false;
    const activity =
      this.isStreamingMessage(message.id) ||
      this.activeStreamingMessageId === message.id
        ? this.currentActivity
        : null;
    return isImageToolAwaitingFile(message.process, message.files, activity);
  }

  hasGeneratedImageLoading(message: ChatMessage): boolean {
    if (message.role !== 'assistant') return false;
    return (message.files ?? []).some(
      (file) => isGeneratedImageFile(file) && this.isImagePreviewPending(message, file)
    );
  }

  isGeneratingImage(message: ChatMessage): boolean {
    return this.isImageToolRunning(message) || this.hasGeneratedImageLoading(message);
  }

  isImagePreviewPending(message: ChatMessage, file: ChatFile): boolean {
    if (!isGeneratedImageFile(file)) return false;
    const key = this.imagePreviewKey(message.id, file);
    const state = this.imagePreviewLoadStates.get(key);
    return state !== 'loaded';
  }

  isImagePreviewError(message: ChatMessage, file: ChatFile): boolean {
    return (
      this.imagePreviewLoadStates.get(this.imagePreviewKey(message.id, file)) ===
      'error'
    );
  }

  imagePreviewStatusLabel(message: ChatMessage, file: ChatFile): string {
    if (this.isImagePreviewError(message, file)) {
      return 'Não foi possível carregar. Use Abrir.';
    }
    return IMAGE_LOADING_STATUS_LABEL;
  }

  onGeneratedImagePreviewLoad(
    messageId: string,
    file: ChatFile,
    img: HTMLImageElement
  ): void {
    if (img.naturalWidth === 0) {
      this.onGeneratedImagePreviewError(messageId, file, img);
      return;
    }
    this.markImagePreviewLoaded(messageId, file);
  }

  onGeneratedImagePreviewError(
    messageId: string,
    file: ChatFile,
    img: HTMLImageElement
  ): void {
    this.retryImagePreview(messageId, file, img);
  }

  private imagePreviewKey(messageId: string, file: ChatFile): string {
    return `${messageId}::${file.url}`;
  }

  private markImagePreviewLoaded(messageId: string, file: ChatFile): void {
    const key = this.imagePreviewKey(messageId, file);
    this.clearImagePreviewLoadWatcher(key);
    if (this.imagePreviewLoadStates.get(key) === 'loaded') return;
    this.imagePreviewLoadStates.set(key, 'loaded');
    this.cdr.detectChanges();
  }

  private retryImagePreview(
    messageId: string,
    file: ChatFile,
    img?: HTMLImageElement
  ): void {
    const key = this.imagePreviewKey(messageId, file);
    const retries = (this.imagePreviewLoadRetries.get(key) ?? 0) + 1;
    this.imagePreviewLoadRetries.set(key, retries);

    if (retries <= ChatbotIaComponent.IMAGE_PREVIEW_MAX_RETRIES) {
      const base = this.chatFilePreviewUrl(file);
      const busted = `${base}${base.includes('?') ? '&' : '?'}_cb=${Date.now()}`;
      if (img) img.src = busted;
      this.imagePreviewLoadStates.set(key, 'loading');
      this.scheduleImagePreviewWatch(messageId, file);
      this.cdr.detectChanges();
      return;
    }

    this.imagePreviewLoadStates.set(key, 'error');
    this.clearImagePreviewLoadWatcher(key);
    this.cdr.detectChanges();
  }

  private scheduleImagePreviewWatch(messageId: string, file: ChatFile): void {
    if (!isPlatformBrowser(this.platformId)) return;
    const key = this.imagePreviewKey(messageId, file);
    this.clearImagePreviewLoadWatcher(key);
    const timeout = setTimeout(() => {
      this.imagePreviewLoadWatchers.delete(key);
      if (this.imagePreviewLoadStates.get(key) === 'loaded') return;

      const escaped = typeof CSS !== 'undefined' && CSS.escape ? CSS.escape(key) : key;
      const img = document.querySelector(
        `img[data-chat-image-preview="${escaped}"]`
      ) as HTMLImageElement | null;

      if (img?.complete && img.naturalWidth > 0) {
        this.markImagePreviewLoaded(messageId, file);
        return;
      }

      this.retryImagePreview(messageId, file, img ?? undefined);
    }, ChatbotIaComponent.IMAGE_PREVIEW_LOAD_TIMEOUT_MS);
    this.imagePreviewLoadWatchers.set(key, timeout);
  }

  private clearImagePreviewLoadWatcher(key: string): void {
    const watcher = this.imagePreviewLoadWatchers.get(key);
    if (!watcher) return;
    clearTimeout(watcher);
    this.imagePreviewLoadWatchers.delete(key);
  }

  private clearImagePreviewLoadWatchers(): void {
    for (const watcher of this.imagePreviewLoadWatchers.values()) {
      clearTimeout(watcher);
    }
    this.imagePreviewLoadWatchers.clear();
  }

  private registerImagePreviewLoads(): void {
    if (!isPlatformBrowser(this.platformId)) return;

    for (const message of this.messages) {
      if (message.role !== 'assistant') continue;
      for (const file of message.files ?? []) {
        if (!isGeneratedImageFile(file)) continue;
        const key = this.imagePreviewKey(message.id, file);
        if (!this.imagePreviewLoadStates.has(key)) {
          this.imagePreviewLoadStates.set(key, 'loading');
          this.scheduleImagePreviewWatch(message.id, file);
        }
      }
    }

    requestAnimationFrame(() => this.probeCachedImagePreviews());
  }

  private probeCachedImagePreviews(): void {
    if (!isPlatformBrowser(this.platformId)) return;

    for (const message of this.messages) {
      if (message.role !== 'assistant') continue;
      for (const file of message.files ?? []) {
        if (!isGeneratedImageFile(file) || !this.isImagePreviewPending(message, file)) {
          continue;
        }

        const key = this.imagePreviewKey(message.id, file);
        const escaped = typeof CSS !== 'undefined' && CSS.escape ? CSS.escape(key) : key;
        const img = document.querySelector(
          `img[data-chat-image-preview="${escaped}"]`
        ) as HTMLImageElement | null;

        if (img?.complete && img.naturalWidth > 0) {
          this.markImagePreviewLoaded(message.id, file);
        }
      }
    }
  }

  private isGeneratingImageFromJob(job: PendingChatJob): boolean {
    const files = job.pendingReply?.files ?? undefined;
    const toolPending = isImageGenerationInProgress(
      job.activityLog,
      files,
      job.currentActivity ?? this.currentActivity
    );
    if (toolPending) return true;

    if (!job.streamingMessageId || !files?.length) return false;
    return files.some(
      (file) =>
        isGeneratedImageFile(file) &&
        this.isImagePreviewPending(
          { id: job.streamingMessageId!, role: 'assistant' } as ChatMessage,
          file
        )
    );
  }

  private formatAssistantReplyText(
    text: string,
    files?: ChatFile[],
    process?: ChatActivityStatus[],
    streaming = false
  ): string {
    if (
      isImageGenerationInProgress(
        process,
        files,
        streaming ? this.currentActivity : null
      )
    ) {
      return '';
    }
    return compactGeneratedImageReply(text, files);
  }

  chatFilePreviewUrl(file: ChatFile | null | undefined): string {
    if (!file?.url) return '';
    if (/^https?:\/\//i.test(file.url)) return file.url;
    return `${environment.urlCDN}${file.url}`;
  }

  hasBubbleText(message: ChatMessage): boolean {
    const raw = (message.displayText ?? message.text).trim();
    const visible =
      message.role === 'assistant'
        ? this.formatAssistantReplyText(
            raw,
            message.files,
            message.process,
            this.isStreamingMessage(message.id)
          )
        : raw;
    if (message.role === 'user' && message.files?.length && !message.displayText) {
      return Boolean(message.text) && !this.isAutoAttachmentCaption(message.text);
    }
    return visible.length > 0;
  }

  private isAutoAttachmentCaption(text: string): boolean {
    return /^(Analise o|Segue o)/.test(text.trim());
  }

  shouldShowBubble(message: ChatMessage): boolean {
    if (message.role === 'assistant') return true;
    return this.hasBubbleText(message);
  }

  chatFileIcon(file: ChatFile): string {
    if (file.kind === 'nota_fiscal') return 'receipt_long';
    if (file.kind === 'chassi') return 'pin';
    if (file.mimeType?.includes('pdf') || file.kind === 'quote_pdf') {
      return 'picture_as_pdf';
    }
    if (file.mimeType?.startsWith('image/')) return 'image';
    return 'attach_file';
  }

  chatFileKindLabel(file: ChatFile): string {
    if (file.kind === 'generated_image') {
      return formatGeneratedImageLabel(file.label);
    }
    if (file.kind === 'nota_fiscal') return 'Nota fiscal';
    if (file.kind === 'chassi') return 'Chassi';
    if (file.kind === 'quote_pdf') return 'Cotação PDF';
    if (file.label && file.label !== file.name) return file.label;
    return 'Arquivo';
  }

  chatFileSizeLabel(file: ChatFile): string {
    if (!file.fileSize) return file.mimeType || 'Arquivo';
    if (file.fileSize < 1024) return `${file.fileSize} B`;
    if (file.fileSize < 1024 * 1024) {
      return `${Math.round(file.fileSize / 1024)} KB`;
    }
    return `${(file.fileSize / (1024 * 1024)).toFixed(1)} MB`;
  }

  openFilePicker(): void {
    if (this.pendingUploads.length >= this.maxChatAttachments) {
      return;
    }
    this.closeSlashMenu();
    this.armOutsideCloseGuard();
    this.fileInput?.nativeElement.click();
  }

  onFilesSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    const files = input.files ? Array.from(input.files) : [];
    input.value = '';
    void this.enqueueFiles(files);
  }

  onComposerDragOver(event: DragEvent): void {
    if (this.isVoiceBusy) return;
    event.preventDefault();
    this.isComposerDragOver = true;
  }

  onComposerDragLeave(event: DragEvent): void {
    event.preventDefault();
    this.isComposerDragOver = false;
  }

  onComposerDrop(event: DragEvent): void {
    event.preventDefault();
    this.isComposerDragOver = false;
    const files = event.dataTransfer?.files
      ? Array.from(event.dataTransfer.files)
      : [];
    void this.enqueueFiles(files);
  }

  onComposerPaste(event: ClipboardEvent): void {
    const files = event.clipboardData?.files
      ? Array.from(event.clipboardData.files)
      : [];
    if (!files.length) return;
    event.preventDefault();
    void this.enqueueFiles(files);
  }

  removePendingUpload(id: string): void {
    this.pendingUploads = this.pendingUploads.filter((item) => item.id !== id);
  }

  pendingUploadLabel(item: PendingChatUpload): string {
    if (item.status === 'ready') return 'Pronto para enviar';
    if (item.status === 'error') return item.error || 'Falha no envio';
    if (item.status === 'uploading') return 'Enviando…';
    return 'Na fila';
  }

  private openCdnPath(path: string | null | undefined): void {
    if (!isPlatformBrowser(this.platformId) || !path) return;
    if (/^https?:\/\//i.test(path)) {
      window.open(path, '_blank', 'noopener,noreferrer');
      return;
    }
    window.open(`${environment.urlCDN}${path}`, '_blank', 'noopener,noreferrer');
  }

  private consumeReadyAttachments(): ChatAttachmentRef[] {
    const ready = this.readyAttachments
      .filter((item) => item.objectKey)
      .map((item) => ({
        objectKey: item.objectKey!,
        fileName: item.fileName,
        mimeType: item.mimeType,
        fileSize: item.fileSize,
      }));
    if (ready.length) {
      this.pendingUploads = this.pendingUploads.filter(
        (item) => item.status !== 'ready'
      );
    }
    return ready;
  }

  private attachmentToChatFile(item: ChatAttachmentRef): ChatFile {
    return {
      id: item.objectKey,
      name: item.fileName,
      kind: 'attachment',
      mimeType: item.mimeType,
      url: item.objectKey,
      label: 'Arquivo',
      fileSize: item.fileSize,
    };
  }

  private async enqueueFiles(files: File[]): Promise<void> {
    if (!files.length) return;

    const slots = this.maxChatAttachments - this.pendingUploads.length;
    if (slots <= 0) return;

    const { accepted, rejected } = partitionValidatedFiles(
      files.slice(0, slots),
      CHAT_ATTACHMENT_UPLOAD_RULES
    );

    for (const item of rejected) {
      this.pendingUploads = [
        ...this.pendingUploads,
        {
          id: this.newId(),
          file: item.file,
          fileName: item.file.name,
          mimeType: resolveUploadContentType(item.file),
          fileSize: item.file.size,
          status: 'error',
          error: item.message,
        },
      ];
    }

    for (const file of accepted) {
      let materialized = file;
      try {
        materialized = await materializeUploadFile(file);
      } catch {
        this.pendingUploads = [
          ...this.pendingUploads,
          {
            id: this.newId(),
            file,
            fileName: file.name,
            mimeType: resolveUploadContentType(file),
            fileSize: file.size,
            status: 'error',
            error: 'Não foi possível ler o arquivo.',
          },
        ];
        continue;
      }

      const pending: PendingChatUpload = {
        id: this.newId(),
        file: materialized,
        fileName: materialized.name,
        mimeType: resolveUploadContentType(materialized),
        fileSize: materialized.size,
        status: 'queued',
      };
      this.pendingUploads = [...this.pendingUploads, pending];
      void this.uploadPending(pending.id);
    }

    this.cdr.detectChanges();
  }

  private async uploadPending(id: string): Promise<void> {
    const current = this.pendingUploads.find((item) => item.id === id);
    if (!current) return;

    this.patchPending(id, { status: 'uploading' });

    try {
      const presigned = await firstValueFrom(
        this.chatbotService.requestAttachmentPresigned(current.file)
      );
      await firstValueFrom(
        this.s3Upload.uploadWithVerification(
          presigned.url,
          current.file,
          presigned.objectKey,
          {
            endpoint: this.chatbotService.attachmentVerifyEndpoint,
            withAuth: Boolean(this.authService.getToken()),
          }
        )
      );
      this.patchPending(id, {
        status: 'ready',
        objectKey: presigned.objectKey,
        mimeType: presigned.fileType || current.mimeType,
      });
    } catch (error) {
      const message =
        error instanceof HttpErrorResponse
          ? error.error?.message || 'Falha no envio do anexo.'
          : error instanceof Error
            ? error.message
            : 'Falha no envio do anexo.';
      this.patchPending(id, { status: 'error', error: message });
    }

    this.cdr.detectChanges();
  }

  private patchPending(
    id: string,
    patch: Partial<PendingChatUpload>
  ): void {
    this.pendingUploads = this.pendingUploads.map((item) =>
      item.id === id ? { ...item, ...patch } : item
    );
  }

  reportStatusLabel(report: ChatReportJob): string {
    if (report.status === 'ready') return 'Relatório pronto';
    if (report.status === 'error') return 'Falha na geração';
    return 'Gerando relatório';
  }

  reportCountLabel(report: ChatReportJob): string {
    if (report.countItems == null) return 'Contando itens…';
    if (report.countItems === 1) return '1 item no arquivo';
    return `${report.countItems} itens no arquivo`;
  }

  private trackProcessingReports(
    incoming?: ChatReportJob[],
    openModal = false
  ): void {
    const jobs = incoming?.length
      ? incoming
      : this.messages.flatMap((message) => message.reports ?? []);

    for (const job of jobs) {
      if (job.status === 'processing') {
        this.startReportPolling(job);
      }
    }

    if (openModal) {
      const firstProcessing = (incoming ?? []).find(
        (job) => job.status === 'processing'
      );
      if (firstProcessing && !this.pendingConfirmation) {
        this.pendingReport = firstProcessing;
      }
    }
  }

  private startReportPolling(job: ChatReportJob): void {
    if (!isPlatformBrowser(this.platformId)) return;
    if (job.status !== 'processing' || this.reportPollers.has(job.id)) return;

    const sub = interval(3500)
      .pipe(
        startWith(0),
        switchMap(() =>
          this.chatbotService.getReport(job.id).pipe(
            catchError(() => of(null))
          )
        ),
        takeUntil(this.destroy$),
        takeWhile((update) => !update || update.status === 'processing', true)
      )
      .subscribe((update) => {
        if (!update) return;
        this.patchReportJob(job.id, update);
        if (update.status !== 'processing') {
          this.reportPollers.get(job.id)?.unsubscribe();
          this.reportPollers.delete(job.id);
          this.persistActiveConversation();
        }
        this.cdr.detectChanges();
      });

    this.reportPollers.set(job.id, sub);
  }

  private patchReportJob(reportId: string, update: ChatReportJob): void {
    this.messages = this.messages.map((message) => ({
      ...message,
      reports: (message.reports ?? []).map((report) =>
        report.id === reportId
          ? {
              ...report,
              status: update.status,
              countItems: update.countItems,
              fileXLSX: update.fileXLSX,
              fileCSV: update.fileCSV,
              name: update.name || report.name,
              description: update.description || report.description,
            }
          : report
      ),
    }));

    if (this.pendingReport?.id === reportId) {
      const next = this.messages
        .flatMap((message) => message.reports ?? [])
        .find((report) => report.id === reportId);
      this.pendingReport = next ?? this.pendingReport;
    }
  }

  private stopAllReportPollers(): void {
    for (const sub of this.reportPollers.values()) {
      sub.unsubscribe();
    }
    this.reportPollers.clear();
  }

  confirmationIcon(confirmation: ChatConfirmation): string {
    if (confirmation.toolName === 'create_listing_report') return 'table_chart';
    return 'verified_user';
  }

  confirmationBadgeLabel(confirmation: ChatConfirmation): string {
    if (confirmation.toolName === 'create_listing_report') {
      return 'Confirme a geração do relatório';
    }
    return 'Confirmação necessária';
  }

  confirmationSubmitLabel(confirmation: ChatConfirmation): string {
    if (confirmation.toolName === 'create_listing_report') {
      return 'Confirmar geração';
    }
    return 'Confirmar ação';
  }

  confirmationPreviewDetails(
    confirmation: ChatConfirmation,
    limit = 3
  ): ChatConfirmationDetail[] {
    return (confirmation.details ?? []).slice(0, limit);
  }

  confirmationHasMoreDetails(confirmation: ChatConfirmation, limit = 3): boolean {
    return (confirmation.details?.length ?? 0) > limit;
  }

  confirmationExtraDetailsCount(confirmation: ChatConfirmation, limit = 3): number {
    return Math.max(0, (confirmation.details?.length ?? 0) - limit);
  }

  isReportConfirmation(confirmation: ChatConfirmation): boolean {
    return confirmation.toolName === 'create_listing_report';
  }

  refuseConfirmationInline(confirmation: ChatConfirmation, event?: Event): void {
    event?.preventDefault();
    event?.stopPropagation();
    if (confirmation.consumed || this.isTyping) return;
    this.pendingConfirmation = confirmation;
    this.refusePendingAction();
  }

  confirmPendingAction(): void {
    const confirmation = this.pendingConfirmation;
    if (!confirmation || this.isTyping) return;

    this.pendingConfirmation = null;
    this.consumeConfirmation(confirmation, 'confirmed');
    this.sendText(
      confirmation.confirmMessage,
      false,
      this.buildConfirmationDisplay(confirmation, 'confirmed')
    );
  }

  refusePendingAction(): void {
    const confirmation = this.pendingConfirmation;
    if (!confirmation || this.isTyping) return;

    this.pendingConfirmation = null;
    this.consumeConfirmation(confirmation, 'refused');
    this.sendText(
      this.buildRefuseMessage(confirmation),
      false,
      this.buildConfirmationDisplay(confirmation, 'refused')
    );
  }

  private consumeConfirmation(
    confirmation: ChatConfirmation,
    outcome: 'confirmed' | 'refused' = 'confirmed'
  ): void {
    this.messages = this.messages.map((message) => ({
      ...message,
      confirmations: message.confirmations.map((item) =>
        item.id === confirmation.id ||
        item.confirmMessage === confirmation.confirmMessage
          ? { ...item, consumed: true, refused: outcome === 'refused' }
          : item
      ),
    }));
  }

  private buildRefuseMessage(confirmation: ChatConfirmation): string {
    return confirmation.confirmMessage.replace(/^CONFIRMO:/i, 'RECUSO:');
  }

  private buildConfirmationDisplay(
    confirmation: ChatConfirmation,
    outcome: 'confirmed' | 'refused' = 'confirmed'
  ): string {
    const verb = outcome === 'refused' ? 'Recusei' : 'Confirmei';
    const title = confirmation.title?.trim() ?? '';
    if (
      title &&
      !/^(admin_|create_|get_|build_)/.test(title) &&
      !/^[a-z][a-z0-9_]*$/.test(title)
    ) {
      return `${verb}: ${title}`;
    }
    return outcome === 'refused' ? 'Recusei a ação.' : 'Confirmei a ação.';
  }

  private ensureActiveConversation(): void {
    if (this.activeConversationId) return;

    const existingBlank = this.findBlankConversation();
    if (existingBlank) {
      this.activeConversationId = existingBlank.id;
      this.activeConversationTitle = existingBlank.title;
      return;
    }

    const created = this.conversationsService.createConversation();
    this.conversations = [created, ...this.conversations];
    this.activeConversationId = created.id;
    this.activeConversationTitle = created.title;
  }

  private persistActiveConversation(): void {
    if (!isPlatformBrowser(this.platformId) || !this.activeConversationId) return;

    const now = Date.now();
    const existing =
      this.conversations.find((item) => item.id === this.activeConversationId) ??
      this.ephemeralConversations.get(this.activeConversationId!);
    const title = this.activeConversationTitle || 'Nova conversa';
    const collapsed = this.collapseStackedAssistants(this.messages);
    if (collapsed.length !== this.messages.length) {
      this.messages = collapsed;
    }
    const messages = collapsed.map((message) => this.toStoredMessage(message));
    const contentUnchanged = this.conversationsService.hasSameContent(existing, {
      title,
      messages,
    });

    const record: ChatConversationRecord = {
      id: this.activeConversationId,
      title,
      messages,
      createdAt: existing?.createdAt ?? now,
      updatedAt: contentUnchanged && existing ? existing.updatedAt : now,
      unread: false,
      parentConversationId: existing?.parentConversationId,
      branchedFromMessageId: existing?.branchedFromMessageId,
    };

    if (this.ephemeralConversations.has(record.id)) {
      this.ephemeralConversations.set(record.id, record);
      this.conversationsService.syncConversation(record);
      return;
    }

    this.writeConversation(record, { activate: true });
  }

  private persistStore(): void {
    if (!isPlatformBrowser(this.platformId)) return;

    const store = this.conversationsService.loadStore();
    store.activeConversationId = this.activeConversationId;
    this.conversationsService.saveStore(store);
  }

  private applyLiveConversationTitle(
    conversationId: string,
    title: string
  ): void {
    const nextTitle = title.trim();
    if (!nextTitle) return;

    if (this.activeConversationId === conversationId) {
      this.activeConversationTitle = nextTitle;
    }

    const ephemeral = this.ephemeralConversations.get(conversationId);
    if (ephemeral) {
      const next = { ...ephemeral, title: nextTitle, updatedAt: Date.now() };
      this.ephemeralConversations.set(conversationId, next);
      this.conversationsService.syncConversation(next);
      return;
    }

    const store = this.conversationsService.loadStore();
    const existing = store.conversations.find((item) => item.id === conversationId);
    if (!existing || existing.title === nextTitle) return;

    this.writeConversation(
      { ...existing, title: nextTitle, updatedAt: Date.now() },
      { activate: false }
    );
  }

  private writeConversation(
    conversation: ChatConversationRecord,
    options?: { activate?: boolean }
  ): void {
    if (this.ephemeralConversations.has(conversation.id)) {
      this.ephemeralConversations.set(conversation.id, conversation);
      this.conversationsService.syncConversation(conversation);
      if (options?.activate !== false) {
        this.activeConversationId = conversation.id;
      }
      return;
    }

    const store = this.conversationsService.loadStore();
    const nextStore = this.conversationsService.upsertConversation(
      store,
      conversation,
      options
    );
    this.conversations = nextStore.conversations;
    this.conversationsService.saveStore(nextStore);
    this.conversationsService.syncConversation(conversation);
  }

  private markConversationRead(conversationId: string): void {
    const existing = this.conversations.find((item) => item.id === conversationId);
    if (!existing?.unread) return;

    this.writeConversation(
      { ...existing, unread: false },
      { activate: conversationId === this.activeConversationId }
    );
  }

  private beginConversationJob(conversationId: string): PendingChatJob {
    this.abortConversationJob(conversationId);

    const currentActivity: ChatActivityStatus = {
      phase: 'thinking',
      label: 'Pensando…',
      icon: 'psychology',
    };
    const job: PendingChatJob = {
      conversationId,
      abort: new AbortController(),
      currentActivity,
      activityLog: [],
      thinkingStartedAt: Date.now(),
    };
    this.pendingJobs.set(conversationId, job);

    if (this.activeConversationId === conversationId) {
      this.currentActivity = currentActivity;
      this.activityLog = [];
      this.thinkingTraceFallbackStartedAt = job.thinkingStartedAt;
    }

    return job;
  }

  private completeConversationJob(
    conversationId: string,
    assistantMessage: AssistantReply | null,
    signal: AbortSignal
  ): void {
    const job = this.pendingJobs.get(conversationId);
    if (!job || job.abort.signal !== signal) {
      return;
    }

    if (!assistantMessage) {
      this.clearTypewriter(job);
      this.pendingJobs.delete(conversationId);
      this.finalizeInterruptedJob(conversationId, job);
      return;
    }

    const isActive = this.activeConversationId === conversationId;
    const reply = assistantMessage.text ?? '';
    job.pendingReply = assistantMessage;
    if (reply) {
      job.pendingText = reply;
    }

    if (
      isActive &&
      (assistantMessage.files?.length || assistantMessage.reports?.length)
    ) {
      this.applyPendingReplyAttachments(conversationId, job);
    }

    const alreadyTyped = job.typedText ?? '';
    const canType =
      isActive && !signal.aborted && reply.length > alreadyTyped.length;

    if (canType) {
      this.ensureTypewriterPump(conversationId);
      return;
    }

    this.finishConversationJob(conversationId, assistantMessage, job);
  }

  private applyLateFollowUps(conversationId: string, followUps: ChatFollowUp[]): void {
    if (!followUps.length) return;

    const job = this.pendingJobs.get(conversationId);
    if (job) {
      job.lateFollowUps = followUps;
      if (job.pendingReply) {
        job.pendingReply = { ...job.pendingReply, followUps };
      }
    }

    if (this.activeConversationId === conversationId) {
      let lastId: string | null = null;
      for (let index = this.messages.length - 1; index >= 0; index -= 1) {
        if (this.messages[index].role === 'assistant') {
          lastId = this.messages[index].id;
          break;
        }
      }
      if (!lastId) return;
      this.messages = this.messages.map((item) =>
        item.id === lastId ? { ...item, followUps } : item
      );
      this.persistActiveConversation();
      this.cdr.detectChanges();
      return;
    }

    const store = this.conversationsService.loadStore();
    const record = store.conversations.find((item) => item.id === conversationId);
    if (!record) return;
    const messages = record.messages.map((item) => ({ ...item }));
    for (let index = messages.length - 1; index >= 0; index -= 1) {
      if (messages[index].role === 'assistant') {
        messages[index] = { ...messages[index], followUps };
        break;
      }
    }
    this.writeConversation({ ...record, messages }, { activate: false });
  }

  private finishConversationJob(
    conversationId: string,
    assistantMessage: AssistantReply,
    job: PendingChatJob
  ): void {
    this.clearTypewriter(job);
    this.pendingJobs.delete(conversationId);

    const process = job.activityLog.length ? [...job.activityLog] : undefined;
    const assistantId = job.streamingMessageId ?? this.newId();
    const revisions = this.mergeRevisionHistory(
      job.pendingRevisions,
      assistantMessage.text
    );
    const isActive = this.activeConversationId === conversationId;
    const existing = isActive
      ? this.messages.find((item) => item.id === assistantId)
      : undefined;
    const html = this.renderMarkdown(
      this.formatAssistantReplyText(
        assistantMessage.text,
        assistantMessage.files,
        process,
        false
      ),
      this.actionableSources(assistantMessage.sources, assistantMessage.actions)
    );
    const thinkingDurationSeconds = job.thinkingStartedAt
      ? Math.max(1, Math.round((Date.now() - job.thinkingStartedAt) / 1000))
      : undefined;
    const assistant: ChatMessage = {
      id: assistantId,
      role: 'assistant',
      text: assistantMessage.text,
      html,
      actions: finalizeChatActions(assistantMessage.actions, assistantMessage.files),
      confirmations: this.primaryConfirmations(assistantMessage.confirmations),
      reports: assistantMessage.reports,
      files: assistantMessage.files,
      sources: this.actionableSources(
        assistantMessage.sources,
        assistantMessage.actions
      ),
      followUps: job.lateFollowUps ?? assistantMessage.followUps,
      interrupted: assistantMessage.interrupted,
      revisions,
      revisionIndex: revisions ? revisions.length - 1 : undefined,
      process,
      thinkingDurationSeconds,
      createdAt: existing?.createdAt ?? Date.now(),
    };

    if (isActive) {
      if (existing) {
        this.messages = this.messages.map((item) =>
          item.id === assistantId ? { ...item, ...assistant } : item
        );
      } else {
        this.messages = this.replaceTrailingAssistants(this.messages, assistant);
      }
      this.messages = this.collapseStackedAssistants(this.messages);
      if (!existing) {
        this.keepProcessOpen(assistantId, process);
      } else if (process?.length && this.settings.expandProcessByDefault) {
        this.expandedProcessIds.add(assistantId);
      }
      this.trackProcessingReports(assistantMessage.reports, true);
      if (assistantMessage.conversationTitle) {
        this.activeConversationTitle = assistantMessage.conversationTitle;
      }
      this.currentActivity = null;
      this.activityLog = [];
      this.thinkingTraceFallbackStartedAt = null;
      this.persistActiveConversation();
      this.registerImagePreviewLoads();
      this.cdr.detectChanges();
      this.scrollToBottomSoon();
      this.focusInputSoon();
      this.speakAssistantIfNeeded(assistant);
      this.flushQueuedOutgoing();
      return;
    }

    const store = this.conversationsService.loadStore();
    const record = store.conversations.find((item) => item.id === conversationId);
    if (!record) {
      this.cdr.detectChanges();
      return;
    }

    this.writeConversation(
      {
        ...record,
        title: assistantMessage.conversationTitle?.trim() || record.title,
        messages: this.replaceTrailingAssistants(
          record.messages,
          this.toStoredMessage(assistant)
        ),
        unread: true,
        updatedAt: Date.now(),
      },
      { activate: false }
    );
    this.cdr.detectChanges();
  }

  private abortConversationJob(conversationId: string): void {
    const job = this.pendingJobs.get(conversationId);
    if (!job) return;

    this.clearTypewriter(job);
    this.finalizeInterruptedJob(conversationId, job);
    job.abort.abort();
    this.pendingJobs.delete(conversationId);

    if (this.activeConversationId === conversationId) {
      this.currentActivity = null;
      this.activityLog = [];
    }
  }

  private abortAllConversationJobs(): void {
    for (const conversationId of [...this.pendingJobs.keys()]) {
      this.abortConversationJob(conversationId);
    }
    this.outgoingQueues.clear();
  }

  private syncTypingUiFromJob(conversationId: string): void {
    const job = this.pendingJobs.get(conversationId);
    if (job) {
      this.currentActivity = job.currentActivity;
      this.activityLog = job.activityLog;
      return;
    }

    this.currentActivity = null;
    this.activityLog = [];
  }

  private async getAssistantReply(
    conversationId: string,
    messages: ChatMessage[],
    conversationTitle: string,
    signal: AbortSignal
  ): Promise<AssistantReply | null> {
    if (!isPlatformBrowser(this.platformId)) {
      return {
        text: 'Inteligência Wosi indisponível neste ambiente.',
        actions: [],
        confirmations: [],
        reports: [],
        files: [],
      };
    }

    const payload: ChatMessagePayload[] = this.conversationsService.toPayload(
      messages.map((message) => this.toStoredMessage(message))
    );

    try {
      const pageContext = this.pageContextService.capture();
      const response = await this.chatbotService.askStream(
        payload,
        pageContext,
        conversationTitle,
        (status) => this.applyActivityStatus(conversationId, status),
        this.lastListingUrl(messages),
        this.lastMentionedAgentIds(messages),
        this.settings.responseStyle,
        signal,
        conversationId,
        (delta) => this.applyStreamDelta(conversationId, delta),
        this.canUseAgentCommands ? this.settings.preferredChatModel : null,
        (followUps) => this.applyLateFollowUps(conversationId, followUps)
      );
      if (signal.aborted) return null;
      return {
        text: response.reply,
        actions: finalizeChatActions(response.actions, response.files),
        confirmations: this.isLastUserMentionOnly(messages)
          ? []
          : this.primaryConfirmations(response.confirmations),
        reports: response.reports ?? [],
        files: response.files ?? [],
        sources: response.sources ?? [],
        followUps: response.followUps ?? [],
        conversationTitle: response.conversationTitle,
      };
    } catch (error) {
      if (signal.aborted || this.isAbortError(error)) {
        return null;
      }
      return {
        text: 'Ops, deu um errinho aqui. Tenta de novo em instantes?',
        actions: [],
        confirmations: [],
        reports: [],
        files: [],
      };
    }
  }

  private applyActivityStatus(
    conversationId: string,
    status: ChatActivityStatus
  ): void {
    const job = this.pendingJobs.get(conversationId);
    if (!job || job.abort.signal.aborted) return;

    if (status.phase === 'tool_done' || status.phase === 'agent_done') {
      this.cdr.detectChanges();
      if (this.activeConversationId === conversationId) {
        this.scrollToBottomSoon();
      }
      return;
    }

    if (this.isHiddenLiveThought(status)) {
      if (status.phase === 'title') {
        const liveTitle = status.conversationTitle?.trim();
        if (liveTitle) {
          try {
            this.applyLiveConversationTitle(conversationId, liveTitle);
          } catch {
            /* título é side-quest — não pode matar o rastro de tools */
          }
        }
      }
      if (PIPELINE_PHASES.has(status.phase)) {
        job.pipelineStarted = true;
      }
      job.activityLog = this.nextProcessLog(job.activityLog, status);
      /* Titulador / Calibrador não viram o passo ao vivo */
      this.syncActivityUi(conversationId, job);
      return;
    }

    if (PIPELINE_PHASES.has(status.phase)) {
      job.pipelineStarted = true;
    }

    job.currentActivity = status;

    if (status.phase !== 'title' && PROCESS_PHASES.has(status.phase)) {
      job.activityLog = this.nextProcessLog(job.activityLog, status);
    }

    this.syncActivityUi(conversationId, job);
  }

  private syncActivityUi(conversationId: string, job: PendingChatJob): void {
    if (this.activeConversationId === conversationId) {
      this.currentActivity = job.currentActivity;
      this.activityLog = job.activityLog;
      if (job.streamingMessageId && job.activityLog.length) {
        this.messages = this.messages.map((item) =>
          item.id === job.streamingMessageId
            ? { ...item, process: [...job.activityLog] }
            : item
        );
      } else if (this.isGeneratingImageFromJob(job)) {
        this.paintTypedText(conversationId, job);
      }
      this.scrollToBottomSoon();
    }
    this.cdr.detectChanges();
  }

  private shouldReplaceActorStep(
    prev: ChatActivityStatus,
    next: ChatActivityStatus
  ): boolean {
    if (IN_PLACE_PROCESS_PHASES.has(next.phase)) {
      return prev.phase === next.phase;
    }

    const preservedPhases = new Set<ChatActivityStatus['phase']>([
      'searching',
      'tool',
      'tool_done',
      'confirm',
    ]);
    if (preservedPhases.has(prev.phase) && !preservedPhases.has(next.phase)) {
      return false;
    }

    const isThought = (step: ChatActivityStatus) =>
      step.toolName === 'agent_thought' ||
      (step.phase === 'agent' && step.label === 'Pensando…');

    if (isThought(next)) {
      return isThought(prev);
    }

    if (prev.phase === 'agent' && next.phase === 'agent') {
      if (prev.label === 'Entrando no contexto…') return true;
      return (
        prev.label === next.label &&
        prev.toolName === next.toolName &&
        prev.detail === next.detail
      );
    }

    return prev.phase === next.phase && prev.toolName === next.toolName;
  }

  private nextProcessLog(
    log: ChatActivityStatus[],
    status: ChatActivityStatus
  ): ChatActivityStatus[] {
    if (status.phase === 'title' && status.titleIdle) {
      return log.filter((step) => step.phase !== 'title');
    }

    const actorKey = this.resolveActorKey(status);
    if (actorKey) {
      let replaceIndex = -1;
      for (let index = log.length - 1; index >= 0; index -= 1) {
        if (this.resolveActorKey(log[index]) === actorKey) {
          if (this.shouldReplaceActorStep(log[index], status)) {
            replaceIndex = index;
          }
          break;
        }
      }
      if (replaceIndex >= 0) {
        const next = [...log];
        next[replaceIndex] = status;
        return next;
      }
    }

    if (IN_PLACE_PROCESS_PHASES.has(status.phase)) {
      let replaceIndex = -1;
      for (let index = log.length - 1; index >= 0; index -= 1) {
        if (log[index].phase === status.phase) {
          replaceIndex = index;
          break;
        }
      }
      if (replaceIndex >= 0) {
        const next = [...log];
        next[replaceIndex] = status;
        return next;
      }
      if (status.phase === 'title') {
        return [status, ...log].slice(-MAX_PROCESS_STEPS);
      }
    }

    const last = log[log.length - 1];
    const isThought = (step: ChatActivityStatus | undefined) =>
      Boolean(
        step &&
          (step.toolName === 'agent_thought' ||
            (step.phase === 'agent' && step.label === 'Pensando…'))
      );
    if (isThought(status) && isThought(last)) {
      const next = [...log];
      next[next.length - 1] = status;
      return next;
    }
    if (
      status.phase === 'thinking' &&
      last?.phase === 'thinking' &&
      last.label === 'Pensando…'
    ) {
      const next = [...log];
      next[next.length - 1] = status;
      return next;
    }
    if (
      last &&
      last.phase === status.phase &&
      last.toolName === status.toolName &&
      last.label === status.label &&
      last.detail === status.detail &&
      last.agentId === status.agentId
    ) {
      return log;
    }

    return [...log, status].slice(-MAX_PROCESS_STEPS);
  }

  private resolveActorKey(
    step: ChatActivityStatus | null | undefined
  ): string | null {
    if (!step) return null;
    return (
      step.actorId?.trim() ||
      step.agentId?.trim() ||
      null
    );
  }

  actorDisplayName(step: ChatActivityStatus | null | undefined): string | null {
    if (!step) return null;
    return step.agentName?.trim() || null;
  }

  private isAbortError(error: unknown): boolean {
    return (
      (error instanceof DOMException && error.name === 'AbortError') ||
      (error instanceof Error && error.name === 'AbortError')
    );
  }

  private toStoredMessage(message: ChatMessage): StoredChatMessage {
    return {
      id: message.id,
      role: message.role,
      text: message.text,
      displayText: message.displayText,
      actions: message.actions,
      confirmations: message.confirmations,
      process: message.process,
      reports: message.reports,
      files: message.files,
      attachments: message.attachments,
      mentions: message.mentions,
      sources: message.sources,
      followUps: message.followUps,
      revisions: message.revisions,
      revisionIndex: message.revisionIndex,
      interrupted: message.interrupted,
      thinkingDurationSeconds: message.thinkingDurationSeconds,
      createdAt: message.createdAt,
    };
  }

  private toUiMessage(message: StoredChatMessage): ChatMessage {
    const displayText =
      message.role === 'user'
        ? this.resolveUserVisibleText(message)
        : this.sanitizeConfirmationDisplay(
            message.displayText?.trim() ||
              this.fallbackConfirmationDisplay(message.text)
          );
    const actions = finalizeChatActions(message.actions, message.files);
    const assistantText =
      message.role === 'assistant'
        ? compactGeneratedImageReply(displayText, message.files)
        : displayText;

    return {
      id: message.id,
      role: message.role,
      text: message.text,
      displayText: message.displayText,
      html:
        message.role === 'user'
          ? this.renderUserHtml(displayText, message.mentions)
          : this.renderMarkdown(
              assistantText,
              this.actionableSources(message.sources, actions)
            ),
      actions,
      confirmations: this.primaryConfirmations(message.confirmations),
      process: message.process,
      reports: message.reports,
      files: message.files,
      attachments: message.attachments,
      mentions: message.mentions,
      sources: this.actionableSources(message.sources, actions),
      followUps: message.followUps,
      revisions: message.revisions,
      revisionIndex: message.revisionIndex,
      interrupted: message.interrupted,
      thinkingDurationSeconds: message.thinkingDurationSeconds,
      createdAt: message.createdAt,
    };
  }

  stopGeneration(event?: Event): void {
    event?.preventDefault();
    event?.stopPropagation();
    if (!this.activeConversationId || !this.isTyping) return;
    this.haltSpeech();
    this.voiceLoopArmed = false;
    this.abortConversationJob(this.activeConversationId);
    this.cdr.detectChanges();
  }

  sourceAgentName(source: ChatSource): string {
    return source.agentName?.trim() || 'Assistente';
  }

  showStreamFoot(message: ChatMessage): boolean {
    return (
      message.role === 'assistant' &&
      this.hasBubbleText(message) &&
      !this.isStreamingMessage(message.id)
    );
  }

  messageFollowUps(message: ChatMessage): ChatFollowUp[] {
    if (!this.showStreamFoot(message)) return [];
    if (this.readOnlyShare || this.isVoiceBusy || this.editingMessageId) return [];
    if (!this.isTrailingAssistantMessage(message)) return [];
    return message.followUps ?? [];
  }

  /** Última bolha assistant no fio — sem user depois. */
  private isTrailingAssistantMessage(message: ChatMessage): boolean {
    if (message.role !== 'assistant') return false;
    const last = this.messages[this.messages.length - 1];
    return last?.role === 'assistant' && last.id === message.id;
  }

  sourcePreview(
    sources: ChatSource[] | undefined,
    actions: ChatAction[] | undefined,
    limit = 3
  ): ChatSource[] {
    return this.actionableSources(sources, actions).slice(0, limit);
  }

  sourceHostLabel(url: string | null | undefined): string {
    if (!url) return '';
    try {
      return new URL(url).hostname.replace(/^www\./i, '');
    } catch {
      return url;
    }
  }

  sourceFaviconUrl(source: ChatSource): string | undefined {
    const key = source.url ?? source.title;
    if (!key || this.sourceFaviconFailures.has(key)) return undefined;
    return faviconUrlForUrl(source.url);
  }

  showSourceFavicon(source: ChatSource): boolean {
    return Boolean(this.sourceFaviconUrl(source));
  }

  isExternalSource(source: ChatSource): boolean {
    return isExternalHttpUrl(source.url);
  }

  onSourceFaviconError(source: ChatSource): void {
    const key = source.url ?? source.title;
    if (!key || this.sourceFaviconFailures.has(key)) return;
    this.sourceFaviconFailures.add(key);
    this.cdr.markForCheck();
  }

  sourceDotHue(source: ChatSource, index: number): number {
    const seed = `${source.url ?? ''}:${source.title}:${index}`;
    let hash = 0;
    for (let i = 0; i < seed.length; i += 1) {
      hash = (hash * 31 + seed.charCodeAt(i)) % 360;
    }
    return hash;
  }

  sourcesCountLabel(count: number): string {
    return count === 1 ? '1 fonte' : `${count} fontes`;
  }

  isSourcesPanelOpen(messageId: string): boolean {
    return this.expandedSourceMessageIds.has(messageId);
  }

  toggleSourcesPanel(messageId: string, event?: Event): void {
    event?.preventDefault();
    event?.stopPropagation();
    if (this.expandedSourceMessageIds.has(messageId)) {
      this.expandedSourceMessageIds.delete(messageId);
    } else {
      this.expandedSourceMessageIds.add(messageId);
    }
  }

  /** Fontes que navegam e não repetem o CTA da bolha (busca/listagem ficam de fora). */
  actionableSources(
    sources: ChatSource[] | undefined,
    actions: ChatAction[] | undefined
  ): ChatSource[] {
    if (!sources?.length) return [];
    const actionUrls = new Set(
      (actions ?? []).map((item) => item.url).filter(Boolean)
    );
    return sources.filter(
      (source) => Boolean(source.url) && !actionUrls.has(source.url as string)
    );
  }

  openSource(source: ChatSource, event?: Event): void {
    event?.stopPropagation();
    if (!source.url) return;

    const mouse = event instanceof MouseEvent ? event : null;
    if (
      mouse &&
      (mouse.metaKey || mouse.ctrlKey || mouse.shiftKey || mouse.button === 1)
    ) {
      return;
    }

    event?.preventDefault();

    if (isExternalHttpUrl(source.url)) {
      if (isPlatformBrowser(this.platformId)) {
        window.open(source.url, '_blank', 'noopener,noreferrer');
      }
      return;
    }

    void this.router.navigateByUrl(source.url);
  }

  sendFollowUp(item: ChatFollowUp, event?: Event): void {
    event?.preventDefault();
    if (this.readOnlyShare) return;
    const catalog = [...this.slashItems, ...this.composerCommandItems];
    const mentions = (item.mentionedAgentIds ?? [])
      .map((id) => catalog.find((entry) => entry.id === id && entry.kind === 'agent'))
      .filter((entry): entry is SlashCommandItem => Boolean(entry))
      .map((entry) => ({
        type: 'agent' as const,
        id: entry.id,
        name: entry.mentionName ?? entry.title,
      }));
    this.dispatchOrQueue(item.prompt, false, mentions.length ? mentions : undefined);
  }

  canRegenerate(message: ChatMessage): boolean {
    if (this.readOnlyShare || message.role !== 'assistant' || this.isTyping) {
      return false;
    }
    const last = [...this.messages].reverse().find((item) => item.role === 'assistant');
    return last?.id === message.id && !this.hasUnconsumedConfirmation();
  }

  canEditMessage(message: ChatMessage): boolean {
    if (this.readOnlyShare || message.role !== 'user' || this.isTyping) return false;
    const text = message.text ?? '';
    return !/^CONFIRMO:/i.test(text) && !/^RECUSO:/i.test(text);
  }

  canBranchFrom(message: ChatMessage): boolean {
    if (this.readOnlyShare || this.isTyping) return false;
    const text = message.text ?? '';
    return !/^CONFIRMO:/i.test(text) && !/^RECUSO:/i.test(text);
  }

  hasRevisionHistory(message: ChatMessage): boolean {
    return this.revisionVersions(message).length > 1;
  }

  revisionVersions(message: ChatMessage): string[] {
    const stored = (message.revisions ?? []).filter(
      (item) => typeof item === 'string'
    );
    const text = message.text ?? '';
    if (!stored.length) return text ? [text] : [];
    const index = message.revisionIndex;
    if (
      typeof index === 'number' &&
      index >= 0 &&
      index < stored.length
    ) {
      return stored;
    }
    if (stored[stored.length - 1] === text) return stored;
    return [...stored, text];
  }

  revisionLabel(message: ChatMessage): string {
    const versions = this.revisionVersions(message);
    const index = this.revisionIndexOf(message, versions);
    return `${index + 1}/${versions.length}`;
  }

  cycleRevision(message: ChatMessage, delta: number, event?: Event): void {
    event?.preventDefault();
    event?.stopPropagation();
    const versions = this.revisionVersions(message);
    if (versions.length < 2) return;
    const current = this.revisionIndexOf(message, versions);
    const next = (current + delta + versions.length) % versions.length;
    const text = versions[next];
    this.messages = this.messages.map((item) =>
      item.id === message.id
        ? {
            ...item,
            text,
            html: this.renderMarkdown(
              text,
              this.actionableSources(item.sources, item.actions)
            ),
            revisions: versions,
            revisionIndex: next,
          }
        : item
    );
    this.persistActiveConversation();
    this.cdr.detectChanges();
  }

  private revisionIndexOf(message: ChatMessage, versions: string[]): number {
    const raw = message.revisionIndex;
    if (typeof raw === 'number' && raw >= 0 && raw < versions.length) {
      return raw;
    }
    const match = versions.lastIndexOf(message.text ?? '');
    return match >= 0 ? match : Math.max(versions.length - 1, 0);
  }

  private mergeRevisionHistory(
    previous: string[] | undefined,
    reply: string
  ): string[] | undefined {
    const prior = (previous ?? []).filter((item) => typeof item === 'string');
    if (!prior.length) return undefined;
    if (prior[prior.length - 1] === reply) return prior;
    return [...prior, reply];
  }

  regenerateMessage(message: ChatMessage, event?: Event): void {
    event?.preventDefault();
    event?.stopPropagation();
    if (!this.canRegenerate(message) || !this.activeConversationId) return;

    const revisions = this.revisionVersions(message);
    this.messages = this.replaceTrailingAssistants(this.messages, null);
    this.persistActiveConversation();

    const conversationId = this.activeConversationId;
    const snapshotMessages = [...this.messages];
    const snapshotTitle = this.activeConversationTitle;
    const job = this.beginConversationJob(conversationId);
    job.streamingMessageId = undefined;
    job.pendingRevisions = revisions;

    this.getAssistantReply(
      conversationId,
      snapshotMessages,
      snapshotTitle,
      job.abort.signal
    ).then((assistantMessage) => {
      this.completeConversationJob(conversationId, assistantMessage, job.abort.signal);
    });
  }

  startEditMessage(message: ChatMessage, event?: Event): void {
    event?.preventDefault();
    event?.stopPropagation();
    if (!this.canEditMessage(message)) return;
    this.editingMessageId = message.id;
    this.draft = message.displayText || message.text;
    this.refreshDraftHighlight();
    this.focusInputSoon();
    setTimeout(() => {
      const textarea = this.messageInput?.nativeElement;
      if (textarea) {
        textarea.value = this.draft;
        this.autoResizeComposer(textarea);
      }
    }, 0);
  }

  cancelEditMessage(): void {
    this.editingMessageId = null;
  }

  async branchFromMessage(message: ChatMessage, event?: Event): Promise<void> {
    event?.preventDefault();
    event?.stopPropagation();
    if (!this.activeConversationId || this.readOnlyShare) return;
    const branched = await this.conversationsService.branchFrom(
      this.activeConversationId,
      message.id,
      `${this.activeConversationTitle} (ramo)`
    );
    if (!branched) return;
    this.persistActiveConversation();
    this.conversations = [branched, ...this.conversations.filter((item) => item.id !== branched.id)];
    this.loadConversation(branched.id, true);
  }

  speakMessage(message: ChatMessage, event?: Event): void {
    event?.preventDefault();
    event?.stopPropagation();
    if (this.speakingMessageId === message.id) {
      this.haltSpeech();
      return;
    }
    this.startSpeech(message.displayText || message.text, message.id);
  }

  isSpeakingMessage(messageId: string): boolean {
    return this.speakingMessageId === messageId;
  }

  parentConversationTitle(conversation: ChatConversationRecord): string | null {
    if (!conversation.parentConversationId) return null;
    const parent = this.conversations.find((item) => item.id === conversation.parentConversationId);
    return parent?.title ?? 'conversa anterior';
  }

  exportActiveConversation(format: 'md' | 'json', event?: Event): void {
    event?.preventDefault();
    this.headerMenuOpen = false;
    if (!this.activeConversationId) return;
    const record = this.conversations.find((item) => item.id === this.activeConversationId);
    if (!record) return;

    if (this.isLoggedIn) {
      this.chatbotService.exportConversation(this.activeConversationId, format).subscribe({
        next: (payload) => {
          if (format === 'json') {
            this.downloadText(
              JSON.stringify((payload as { conversation: unknown }).conversation, null, 2),
              `${this.fileSafeTitle(record.title)}.json`,
              'application/json'
            );
            return;
          }
          const blob = payload as Blob;
          this.downloadBlob(blob, `${this.fileSafeTitle(record.title)}.md`);
        },
        error: () => this.downloadLocalExport(record, format),
      });
      return;
    }

    this.downloadLocalExport(record, format);
  }

  shareActiveConversation(event?: Event): void {
    event?.preventDefault();
    this.headerMenuOpen = false;
    if (!this.canUseAgentCommands || !this.activeConversationId) return;
    this.chatbotService.shareConversation(this.activeConversationId).subscribe({
      next: (response) => {
        const url = `${window.location.origin}${window.location.pathname}?wosiChatShare=${response.token}`;
        void navigator.clipboard?.writeText(url);
        this.shareNotice = 'Link copiado. Válido por 7 dias para outro admin.';
        setTimeout(() => {
          this.shareNotice = null;
          this.cdr.markForCheck();
        }, 3200);
        this.cdr.markForCheck();
      },
      error: () => {
        this.shareNotice = 'Não foi possível criar o link agora.';
        this.cdr.markForCheck();
      },
    });
  }

  loadMemories(): void {
    if (!this.isLoggedIn || !this.canCallAssistantApis()) {
      this.memories = [];
      this.memoriesLoading = false;
      return;
    }
    this.memoriesLoading = true;
    this.chatbotService.listMemories().subscribe({
      next: (response) => {
        this.memories = response.memories ?? [];
        this.memoryDrafts = Object.fromEntries(
          this.memories.map((item) => [item.id, item.content])
        );
        this.memoriesLoading = false;
        this.cdr.markForCheck();
      },
      error: () => {
        this.memoriesLoading = false;
        this.cdr.markForCheck();
      },
    });
  }

  saveMemory(item: ChatMemoryItem): void {
    const content = (this.memoryDrafts[item.id] ?? '').trim();
    if (content.length < 3) return;
    this.memoryBusyId = item.id;
    this.chatbotService.updateMemory(item.id, { content }).subscribe({
      next: (response) => {
        this.memories = this.memories.map((memory) =>
          memory.id === item.id ? response.memory : memory
        );
        this.memoryBusyId = null;
        this.cdr.markForCheck();
      },
      error: () => {
        this.memoryBusyId = null;
        this.cdr.markForCheck();
      },
    });
  }

  forgetMemory(item: ChatMemoryItem): void {
    this.memoryBusyId = item.id;
    this.chatbotService.deleteMemory(item.id).subscribe({
      next: () => {
        this.memories = this.memories.filter((memory) => memory.id !== item.id);
        delete this.memoryDrafts[item.id];
        this.memoryBusyId = null;
        this.cdr.markForCheck();
      },
      error: () => {
        this.memoryBusyId = null;
        this.cdr.markForCheck();
      },
    });
  }

  forgetAllMemories(): void {
    if (!this.memories.length) return;
    if (
      typeof window !== 'undefined' &&
      !window.confirm('Esquecer todos os fatos que o assistente lembra desta conta?')
    ) {
      return;
    }
    this.memoryBusyId = 'all';
    this.chatbotService.deleteAllMemories().subscribe({
      next: () => {
        this.memories = [];
        this.memoryDrafts = {};
        this.memoryBusyId = null;
        this.memoryNotice = 'Memória limpa.';
        this.cdr.markForCheck();
      },
      error: () => {
        this.memoryBusyId = null;
        this.cdr.markForCheck();
      },
    });
  }

  private handleAdminShortcuts(event: KeyboardEvent): boolean {
    if (!this.isChatAvailable) return false;
    const meta = event.ctrlKey || event.metaKey;
    if (!meta || event.altKey) return false;
    const key = event.key.toLowerCase();

    if (event.shiftKey && key === 'i') {
      if (this.isNativeDialogTarget(event.target)) return false;
      event.preventDefault();
      this.chatbotLauncher.openAndFocus();
      return true;
    }

    if (event.shiftKey) return false;
    if (key !== 'j' && key !== 'k') return false;
    if (this.isNativeDialogTarget(event.target)) return false;
    if (this.slashOpen) return false;

    event.preventDefault();
    if (key === 'j') {
      this.chatbotLauncher.toggle();
      return true;
    }
    this.chatbotLauncher.openAndFocus();
    return true;
  }

  private isNativeDialogTarget(target: EventTarget | null): boolean {
    if (!(target instanceof HTMLElement)) return false;
    return Boolean(target.closest('dialog'));
  }

  private applyStreamDelta(conversationId: string, delta: string): void {
    const job = this.pendingJobs.get(conversationId);
    if (!job || job.abort.signal.aborted || !delta) return;

    job.pendingText = `${job.pendingText ?? ''}${delta}`;

    if (this.activeConversationId === conversationId) {
      this.ensureTypewriterPump(conversationId);
      return;
    }

    this.flushTypewriter(conversationId);
  }

  private ensureTypewriterPump(conversationId: string): void {
    const job = this.pendingJobs.get(conversationId);
    if (!job || job.typewriterTimer || job.abort.signal.aborted) return;
    this.tickTypewriter(conversationId);
  }

  private tickTypewriter(conversationId: string): void {
    const job = this.pendingJobs.get(conversationId);
    if (!job || job.abort.signal.aborted) {
      this.clearTypewriter(job);
      return;
    }

    if (this.activeConversationId !== conversationId) {
      this.flushTypewriter(conversationId);
      return;
    }

    const pending = job.pendingText ?? '';
    const typed = job.typedText ?? '';

    if (typed.length < pending.length) {
      const nextLen = this.nextTypewriterOffset(pending, typed.length);
      job.typedText = pending.slice(0, nextLen);
      this.paintTypedText(conversationId, job);
      const delay =
        nextLen < TYPEWRITER_SLOW_UNTIL ? TYPEWRITER_MS_SLOW : TYPEWRITER_MS_FAST;
      job.typewriterTimer = setTimeout(
        () => this.tickTypewriter(conversationId),
        delay
      );
      return;
    }

    job.typewriterTimer = undefined;
    if (job.pendingReply) {
      this.finishConversationJob(conversationId, job.pendingReply, job);
    }
  }

  private nextTypewriterOffset(full: string, current: number): number {
    const remain = full.slice(current);
    const take = current < 40 ? 2 : current < TYPEWRITER_SLOW_UNTIL ? 6 : 14;
    if (remain.length <= take) return full.length;

    const window = remain.slice(0, take + 3);
    const space = window.indexOf(' ');
    if (space >= 0 && space <= take + 1) {
      return current + space + 1;
    }
    return current + take;
  }

  private clearTypewriter(job: PendingChatJob | undefined): void {
    if (!job?.typewriterTimer) return;
    clearTimeout(job.typewriterTimer);
    job.typewriterTimer = undefined;
  }

  private flushTypewriter(conversationId: string): void {
    const job = this.pendingJobs.get(conversationId);
    if (!job) return;

    this.clearTypewriter(job);
    const pending = job.pendingText ?? '';
    const typed = job.typedText ?? '';
    if (pending.length > typed.length) {
      job.typedText = pending;
      this.paintTypedText(conversationId, job);
    }
    if (job.pendingReply) {
      this.finishConversationJob(conversationId, job.pendingReply, job);
    }
  }

  private paintTypedText(conversationId: string, job: PendingChatJob): void {
    const visible = job.typedText ?? '';
    const pending = job.pendingReply;
    const hasPendingAttachments = Boolean(
      pending?.files?.length || pending?.reports?.length
    );
    const process = job.activityLog.length ? [...job.activityLog] : undefined;
    const files = pending?.files;
    const display = this.formatAssistantReplyText(visible, files, process, true);

    if (!display && !job.streamingMessageId && !hasPendingAttachments && !this.isGeneratingImageFromJob(job)) return;

    const isActive = this.activeConversationId === conversationId;

    if (!job.streamingMessageId) {
      const id = this.newId();
      job.streamingMessageId = id;
      const bubble: ChatMessage = {
        id,
        role: 'assistant',
        text: visible,
        html: this.renderStreamingMarkdown(display, job.pendingReply?.sources),
        actions: finalizeChatActions(job.pendingReply?.actions, files),
        confirmations: [],
        reports: job.pendingReply?.reports,
        files: job.pendingReply?.files,
        process,
        createdAt: Date.now(),
      };
      if (isActive) {
        this.keepProcessOpen(id, process);
        this.messages = this.replaceTrailingAssistants(this.messages, bubble);
      } else {
        const store = this.conversationsService.loadStore();
        const existing = store.conversations.find((item) => item.id === conversationId);
        if (existing) {
          this.writeConversation(
            {
              ...existing,
              messages: this.replaceTrailingAssistants(
                existing.messages,
                this.toStoredMessage(bubble)
              ),
              unread: true,
              updatedAt: Date.now(),
            },
            { activate: false }
          );
        }
      }
    } else if (isActive) {
      this.messages = this.messages.map((item) =>
        item.id === job.streamingMessageId
          ? {
              ...item,
              text: visible,
              html: this.renderStreamingMarkdown(display, job.pendingReply?.sources),
              process: process ?? item.process,
              files: job.pendingReply?.files ?? item.files,
              reports: job.pendingReply?.reports ?? item.reports,
              actions: finalizeChatActions(
                job.pendingReply?.actions ?? item.actions,
                job.pendingReply?.files ?? item.files
              ),
            }
          : item
      );
    } else {
      const store = this.conversationsService.loadStore();
      const existing = store.conversations.find((item) => item.id === conversationId);
      if (existing) {
        this.writeConversation(
          {
            ...existing,
            messages: existing.messages.map((item) =>
              item.id === job.streamingMessageId
                ? { ...item, text: visible, process: process ?? item.process }
                : item
            ),
            unread: true,
            updatedAt: Date.now(),
          },
          { activate: false }
        );
      }
    }

    if (isActive) {
      this.registerImagePreviewLoads();
      this.scrollToBottomSoon();
      this.cdr.detectChanges();
    }
  }

  private applyPendingReplyAttachments(
    conversationId: string,
    job: PendingChatJob
  ): void {
    if (this.activeConversationId !== conversationId) return;
    const pending = job.pendingReply;
    if (!pending?.files?.length && !pending?.reports?.length) return;

    if (job.streamingMessageId) {
      this.messages = this.messages.map((item) =>
        item.id === job.streamingMessageId
          ? {
              ...item,
              files: pending.files ?? item.files,
              reports: pending.reports ?? item.reports,
              actions: finalizeChatActions(pending.actions, pending.files),
            }
          : item
      );
      this.registerImagePreviewLoads();
      this.cdr.detectChanges();
      this.scrollToBottomSoon();
      return;
    }

    this.paintTypedText(conversationId, job);
  }

  /** No máximo uma assistant depois do último user — delta e result não empilham. */
  private replaceTrailingAssistants<
    T extends { id: string; role: string; createdAt: number; revisions?: string[] },
  >(messages: T[], assistant: T | null): T[] {
    let lastUser = -1;
    for (let index = messages.length - 1; index >= 0; index -= 1) {
      if (messages[index].role === 'user') {
        lastUser = index;
        break;
      }
    }

    const head = lastUser >= 0 ? messages.slice(0, lastUser + 1) : [];
    const trailing = messages.slice(lastUser + 1);
    const stubs = trailing.filter((item) => item.role === 'assistant');
    const rest = trailing.filter((item) => item.role !== 'assistant');
    if (!assistant) return [...head, ...rest];

    const stub = stubs[0];
    return [
      ...head,
      ...rest,
      stub
        ? {
            ...assistant,
            id: stub.id,
            createdAt: stub.createdAt,
            revisions: assistant.revisions?.length
              ? assistant.revisions
              : stub.revisions,
          }
        : assistant,
    ];
  }

  private collapseStackedAssistants(messages: ChatMessage[]): ChatMessage[] {
    const out: ChatMessage[] = [];
    for (const message of messages) {
      const prev = out[out.length - 1];
      if (prev?.role === 'assistant' && message.role === 'assistant') {
        out[out.length - 1] = this.pickRicherAssistant(prev, message);
        continue;
      }
      out.push(message);
    }
    return out;
  }

  private pickRicherAssistant(left: ChatMessage, right: ChatMessage): ChatMessage {
    const score = (message: ChatMessage) =>
      (message.process?.length ?? 0) * 10 +
      (message.actions?.length ?? 0) * 5 +
      (message.confirmations?.length ?? 0) * 5 +
      (message.reports?.length ?? 0) +
      (message.files?.length ?? 0) +
      (message.text?.length ?? 0);

    const keep = score(right) >= score(left) ? right : left;
    const drop = keep === right ? left : right;
    return {
      ...keep,
      id: left.id,
      createdAt: left.createdAt,
      process: keep.process?.length ? keep.process : drop.process,
      actions: keep.actions?.length ? keep.actions : drop.actions,
      confirmations: keep.confirmations?.length
        ? keep.confirmations
        : drop.confirmations,
      reports: keep.reports?.length ? keep.reports : drop.reports,
      files: keep.files?.length ? keep.files : drop.files,
      followUps: keep.followUps?.length ? keep.followUps : drop.followUps,
      sources: keep.sources?.length ? keep.sources : drop.sources,
      revisions:
        (keep.revisions?.length ?? 0) >= (drop.revisions?.length ?? 0)
          ? keep.revisions
          : drop.revisions,
      revisionIndex: keep.revisionIndex ?? drop.revisionIndex,
    };
  }

  private finalizeInterruptedJob(conversationId: string, job: PendingChatJob): void {
    if (!job.streamingMessageId) {
      if (this.activeConversationId === conversationId) {
        this.currentActivity = null;
        this.activityLog = [];
        this.cdr.detectChanges();
      }
      return;
    }

    const patchInterrupted = (message: ChatMessage): ChatMessage =>
      message.id === job.streamingMessageId
        ? {
            ...message,
            interrupted: true,
            html: this.renderMarkdown(
              message.text.trim()
                ? `${message.text}\n\n_Geração interrompida._`
                : '_Geração interrompida._',
              this.actionableSources(message.sources, message.actions)
            ),
          }
        : message;

    if (this.activeConversationId === conversationId) {
      this.messages = this.messages.map(patchInterrupted);
      this.currentActivity = null;
      this.activityLog = [];
      this.persistActiveConversation();
      this.cdr.detectChanges();
      return;
    }

    const store = this.conversationsService.loadStore();
    const existing = store.conversations.find((item) => item.id === conversationId);
    if (!existing) return;
    this.writeConversation(
      {
        ...existing,
        messages: existing.messages.map((item) =>
          item.id === job.streamingMessageId ? { ...item, interrupted: true } : item
        ),
      },
      { activate: false }
    );
  }

  private speakAssistantIfNeeded(message: ChatMessage): void {
    if (!this.settings.ttsEnabled || message.interrupted) {
      if (this.settings.voiceLoopEnabled && this.voiceLoopArmed) {
        this.startVoiceCaptureForLoop();
      }
      return;
    }

    const spoken = this.startSpeech(message.text, message.id, () => {
      if (this.settings.voiceLoopEnabled && this.voiceLoopArmed) {
        this.startVoiceCaptureForLoop();
      }
    });
    if (!spoken && this.settings.voiceLoopEnabled && this.voiceLoopArmed) {
      this.startVoiceCaptureForLoop();
    }
  }

  private startSpeech(
    text: string,
    messageId: string,
    onend?: () => void
  ): boolean {
    this.speakingMessageId = messageId;
    const started = speakPortuguese(text, {
      onend: () => {
        this.ngZone.run(() => {
          if (this.speakingMessageId === messageId) {
            this.speakingMessageId = null;
          }
          this.cdr.detectChanges();
          onend?.();
        });
      },
    });
    if (!started) {
      this.speakingMessageId = null;
    }
    this.cdr.detectChanges();
    return started;
  }

  private haltSpeech(): void {
    this.speakingMessageId = null;
    cancelSpeech();
    this.cdr.detectChanges();
  }

  private startVoiceCaptureForLoop(): void {
    if (this.isVoiceBusy || this.isTyping) return;
    void this.startVoiceCapture();
  }

  private downloadLocalExport(record: ChatConversationRecord, format: 'md' | 'json'): void {
    if (format === 'json') {
      this.downloadText(
        JSON.stringify(
          {
            id: record.id,
            title: record.title,
            messages: record.messages.map((item) => ({
              id: item.id,
              role: item.role,
              text: item.displayText || item.text,
              sources: item.sources ?? [],
              createdAt: item.createdAt,
            })),
          },
          null,
          2
        ),
        `${this.fileSafeTitle(record.title)}.json`,
        'application/json'
      );
      return;
    }

    const lines = [`# ${record.title}`, ''];
    for (const message of record.messages) {
      const role = message.role === 'assistant' ? 'Assistente' : 'Você';
      const text = (message.displayText || message.text || '').trim();
      if (!text) continue;
      lines.push(`## ${role}`, '', text, '');
    }
    this.downloadText(lines.join('\n'), `${this.fileSafeTitle(record.title)}.md`, 'text/markdown');
  }

  private fileSafeTitle(title: string): string {
    return (title || 'conversa').replace(/[^\p{L}\p{N}]+/gu, '-').slice(0, 60);
  }

  private downloadText(content: string, filename: string, type: string): void {
    this.downloadBlob(new Blob([content], { type }), filename);
  }

  private downloadBlob(blob: Blob, filename: string): void {
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    link.click();
    URL.revokeObjectURL(url);
  }

  private syncChatAvailability(): void {
    if (this.isChatAvailable) return;
    if (this.isOpen) this.close();
    this.cdr.markForCheck();
  }

  private openSharedConversationFromUrl(): void {
    if (!isPlatformBrowser(this.platformId) || !this.canUseAgentCommands) return;
    if (!this.canCallAssistantApis()) return;
    if (this.readOnlyShare) return;
    const token = new URLSearchParams(window.location.search).get('wosiChatShare');
    if (!token) return;
    this.chatbotService.getSharedConversation(token).subscribe({
      next: (response) => {
        const record = this.conversationsService.fromApiRecord(response.conversation);
        this.readOnlyShare = true;
        this.conversations = [
          record,
          ...this.conversations.filter((item) => item.id !== record.id),
        ];
        this.loadConversation(record.id, true);
        this.isOpen = true;
        this.chatbotLauncher.setPanelOpen(true);
        this.cdr.markForCheck();
      },
    });
  }

  primaryConfirmations(
    items: ChatConfirmation[] | undefined
  ): ChatConfirmation[] {
    if (!items?.length) return [];
    if (items.some((item) => item.agentId)) return items;
    if (items.length === 1) return items;

    const nonReport = [...items]
      .reverse()
      .find((item) => item.toolName !== 'create_listing_report');
    return [nonReport ?? items[items.length - 1]];
  }

  private lastListingUrl(messages: ChatMessage[] = this.messages): string | undefined {
    if (this.isLastUserMentionOnly(messages)) return undefined;

    const listingPath =
      /\/minha-conta\/(clientes|parceiros|usuarios|produtos|sinistro|solicitacoes|reboques|comissoes|financeiro|lojas-e-vendedores|cupons-de-desconto|auditoria)/;

    for (let index = messages.length - 1; index >= 0; index -= 1) {
      const message = messages[index];
      if (message.role !== 'assistant') continue;

      for (let actionIndex = (message.actions?.length ?? 0) - 1; actionIndex >= 0; actionIndex -= 1) {
        const url = message.actions[actionIndex]?.url;
        if (url && listingPath.test(url)) return url;
      }
    }

    return undefined;
  }

  private fallbackConfirmationDisplay(text: string): string {
    if (/^RECUSO:\s*/i.test(text)) return 'Recusei a ação.';
    if (!/^CONFIRMO:\s*/i.test(text)) return text;
    return 'Confirmei a ação.';
  }

  private isLegacyAutomationPrompt(text: string): boolean {
    return (
      /Contexto da execução/i.test(text) ||
      (/evento:\s*[\w.]+/i.test(text) && /\{\s*"/.test(text))
    );
  }

  private resolveUserVisibleText(message: StoredChatMessage): string {
    if (message.displayText?.trim()) {
      return this.sanitizeConfirmationDisplay(message.displayText.trim());
    }
    if (this.isLegacyAutomationPrompt(message.text)) {
      const head = message.text.split('---')[0]?.trim();
      if (head && head.length <= 240 && !/\{\s*"/.test(head)) {
        return head;
      }
      return 'Disparo de automação';
    }
    return this.sanitizeConfirmationDisplay(
      this.fallbackConfirmationDisplay(message.text)
    );
  }

  private sanitizeConfirmationDisplay(text: string): string {
    const value = text?.trim() ?? '';
    if (!value) return value;
    if (/payload\s*:/i.test(value) && /\{[\s\S]*\}/.test(value)) {
      return 'Confirmei a ação.';
    }
    if (/Recusei:\s*(admin_|create_|get_|build_)[a-z0-9_]+/i.test(value)) {
      return 'Recusei a ação.';
    }
    if (/Confirmei:\s*(admin_|create_|get_|build_)[a-z0-9_]+/i.test(value)) {
      return 'Confirmei a ação.';
    }
    return value;
  }

  private newId(): string {
    const uuid = globalThis.crypto?.randomUUID?.();
    return uuid ?? `${Date.now()}-${Math.random().toString(16).slice(2)}`;
  }

  private lastMentionedAgentIds(
    messages: ChatMessage[] = this.messages
  ): string[] | undefined {
    const lastUser = [...messages]
      .reverse()
      .find((message) => message.role === 'user');
    const ids = lastUser?.mentions
      ?.filter((mention) => mention.type === 'agent')
      .map((mention) => mention.id);
    return ids?.length ? ids : undefined;
  }

  private isLastUserMentionOnly(
    messages: ChatMessage[] = this.messages
  ): boolean {
    const lastUser = [...messages]
      .reverse()
      .find((message) => message.role === 'user');
    return isMentionOnlyMessage(
      lastUser?.text ?? '',
      lastUser?.mentions,
      Boolean(lastUser?.attachments?.length)
    );
  }

  private loadSlashCatalog(): void {
    if (!isPlatformBrowser(this.platformId)) return;
    if (!this.canCallAssistantApis() || !this.canUseAgentCommands) {
      this.slashItems = [];
      this.composerCommandItems = [];
      this.closeSlashMenu();
      return;
    }

    this.chatbotService
      .listActiveAgents()
      .pipe(
        takeUntil(this.destroy$),
        catchError(() => of([]))
      )
      .subscribe((agents) => {
        this.slashItems = buildAgentSlashItems(agents);
        this.composerCommandItems = buildComposerCommandItems(agents);
        if (this.slashOpen && this.slashSource === 'plus') {
          this.refreshPlusMenu(true);
        } else if (this.slashOpen) {
          const caret = this.messageInput?.nativeElement.selectionStart ?? this.draft.length;
          this.refreshSlashMenu(caret);
        }
        this.cdr.markForCheck();
      });
  }

  private isSlashNavigationKey(key: string): boolean {
    return (
      key === 'ArrowDown' ||
      key === 'ArrowUp' ||
      key === 'Enter' ||
      key === 'Tab' ||
      key === 'Escape'
    );
  }

  private handleSlashKeydown(event: KeyboardEvent): boolean {
    if (!this.slashOpen) return false;

    if (event.key === 'ArrowDown') {
      event.preventDefault();
      event.stopPropagation();
      this.moveSlashIndex(1);
      return true;
    }

    if (event.key === 'ArrowUp') {
      event.preventDefault();
      event.stopPropagation();
      this.moveSlashIndex(-1);
      return true;
    }

    if (event.key === 'Enter' || event.key === 'Tab') {
      event.preventDefault();
      event.stopPropagation();
      this.applySlashSelection();
      return true;
    }

    if (event.key === 'Escape') {
      event.preventDefault();
      event.stopPropagation();
      this.closeSlashMenu();
      return true;
    }

    return false;
  }

  private refreshSlashMenu(caret: number): void {
    const query = findSlashQuery(this.draft, caret);
    if (query) {
      if (query.trigger === '/' && !this.canUseAgentCommands) {
        this.closeSlashMenu();
        return;
      }

      const sameQuery =
        this.slashOpen &&
        (this.slashSource === 'slash' || this.slashSource === 'at') &&
        this.slashQuery?.start === query.start &&
        this.slashQuery?.query === query.query &&
        this.slashQuery?.trigger === query.trigger;
      const previousId = this.flatSlashItems[this.slashIndex]?.id;

      if (query.trigger === '@') {
        this.slashSource = 'at';
        this.slashQuery = query;
        this.slashGroups = this.buildAtMenuGroups(query.query);
      } else {
        this.slashSource = 'slash';
        this.slashQuery = query;
        this.slashGroups = groupSlashItems(
          this.availableSlashItems(query.query)
        );
      }

      this.closeModelPicker();
      this.slashOpen = true;
      this.slashIndex = this.resolveSlashIndex(previousId, sameQuery);
      return;
    }

    this.slashQuery = null;
    if (this.slashSource === 'plus' && this.slashOpen) {
      this.refreshPlusMenu(true);
      return;
    }

    this.closeSlashMenu();
  }

  private availableSourceItems(query = ''): SlashCommandItem[] {
    const items = this.composerSourceItems;
    return query ? filterSlashItems(items, query) : items;
  }

  private availableCommandItems(query = ''): SlashCommandItem[] {
    if (!this.canUseAgentCommands) return [];
    const available = excludeMentionedSlashItems(
      this.composerCommandItems,
      this.draftMentions
    );
    return query ? filterSlashItems(available, query) : available;
  }

  private buildAtMenuGroups(query = ''): { group: string; items: SlashCommandItem[] }[] {
    return [
      ...groupSlashItems(this.availableSourceItems(query)),
      ...groupSlashItems(this.availableCommandItems(query)),
    ];
  }

  private buildPlusMenuGroups(query = ''): { group: string; items: SlashCommandItem[] }[] {
    const groups: { group: string; items: SlashCommandItem[] }[] = [];
    const sources = this.availableSourceItems(query);
    if (sources.length) {
      groups.push(...groupSlashItems(sources));
    }
    const commands = this.availableCommandItems(query);
    if (commands.length) {
      groups.push(...groupSlashItems(commands));
    }
    if (this.canUseAgentCommands) {
      const agents = this.availableSlashItems(query);
      if (agents.length) {
        groups.push(...groupSlashItems(agents));
      }
    }
    return groups;
  }

  private availableSlashItems(query = ''): SlashCommandItem[] {
    const available = excludeMentionedSlashItems(
      this.slashItems,
      this.draftMentions
    );
    return query ? filterSlashItems(available, query) : available;
  }

  private refreshPlusMenu(keepSelection = false): void {
    const previousId = this.flatSlashItems[this.slashIndex]?.id;
    this.slashGroups = this.buildPlusMenuGroups();
    this.slashIndex = this.resolveSlashIndex(previousId, keepSelection);
  }

  private removeSlashQueryFromDraft(): void {
    if (!this.slashQuery) return;

    const end = this.messageInput?.nativeElement?.selectionStart ?? this.draft.length;
    this.draft = this.draft.slice(0, this.slashQuery.start) + this.draft.slice(end);
    this.draftMentions = syncMentions(this.draft, this.draftMentions);
    this.refreshDraftHighlight();

    const textarea = this.messageInput?.nativeElement;
    if (!textarea) return;

    textarea.value = this.draft;
    const caret = this.slashQuery.start;
    textarea.setSelectionRange(caret, caret);
    this.autoResizeComposer(textarea);
  }

  private resolveSlashIndex(previousId: string | undefined, keepSelection: boolean): number {
    const items = this.flatSlashItems;
    if (!items.length) return 0;
    if (keepSelection && previousId) {
      const kept = items.findIndex((item) => item.id === previousId);
      if (kept >= 0) return kept;
    }
    if (keepSelection) {
      return Math.min(this.slashIndex, items.length - 1);
    }
    return 0;
  }

  private closeSlashMenu(): void {
    this.slashOpen = false;
    this.slashSource = null;
    this.slashQuery = null;
    this.slashIndex = 0;
    this.slashGroups = [];
  }

  private focusComposer(): void {
    setTimeout(() => this.messageInput?.nativeElement?.focus(), 0);
  }

  private moveSlashIndex(delta: number): void {
    const total = this.flatSlashItems.length;
    if (!total) return;
    this.slashIndex = (this.slashIndex + delta + total) % total;
    this.cdr.detectChanges();
    this.scrollActiveSlashItemIntoView();
  }

  hoverSlashItem(item: SlashCommandItem): void {
    const index = this.flatSlashItems.findIndex((entry) => entry.id === item.id);
    if (index < 0 || index === this.slashIndex) return;
    this.slashIndex = index;
  }

  private scrollActiveSlashItemIntoView(): void {
    if (!isPlatformBrowser(this.platformId)) return;

    requestAnimationFrame(() => {
      this.chatPanel?.nativeElement
        ?.querySelector('.chatbot__slashItem.is-active')
        ?.scrollIntoView({ block: 'nearest' });
    });
  }

  private refreshDraftHighlight(): void {
    const html = highlightMentions(this.draft, this.draftMentions);
    this.draftHighlightHtml = this.sanitizer.bypassSecurityTrustHtml(html);
  }

  private getComposerHeightLimits(): { min: number; max: number } {
    if (!isPlatformBrowser(this.platformId)) {
      return { min: 44, max: 168 };
    }

    const chatRoot = (this.messageInput?.nativeElement.closest('.chatbot') ??
      this.chatPanel?.nativeElement?.querySelector('.chatbot')) as HTMLElement | null;
    const styles = getComputedStyle(chatRoot ?? document.documentElement);
    const min = Number.parseFloat(styles.getPropertyValue('--chat-composer-min-height')) || 44;
    const max = Number.parseFloat(styles.getPropertyValue('--chat-composer-max-height')) || 168;
    return { min, max };
  }

  private syncComposerHighlightScroll(textarea: HTMLTextAreaElement): void {
    const highlight = textarea.previousElementSibling as HTMLElement | null;
    if (!highlight?.classList.contains('chatbot__highlight')) return;

    highlight.style.transform = textarea.scrollTop
      ? `translateY(-${textarea.scrollTop}px)`
      : '';
  }

  private ensureComposerCaretVisible(textarea: HTMLTextAreaElement): void {
    const lineHeight =
      Number.parseFloat(getComputedStyle(textarea).lineHeight) || 22;
    const caret = textarea.selectionStart ?? textarea.value.length;
    const linesBeforeCaret = textarea.value.slice(0, caret).split('\n').length;
    const caretTop = Math.max(0, (linesBeforeCaret - 1) * lineHeight);
    const caretBottom = caretTop + lineHeight;
    const visibleTop = textarea.scrollTop;
    const visibleBottom = visibleTop + textarea.clientHeight;

    if (caretBottom > visibleBottom) {
      textarea.scrollTop = caretBottom - textarea.clientHeight + 6;
    } else if (caretTop < visibleTop) {
      textarea.scrollTop = Math.max(0, caretTop - 6);
    }

    this.syncComposerHighlightScroll(textarea);
  }

  private syncComposerExpanded(textarea: HTMLTextAreaElement): boolean {
    const wasExpanded = this.composerExpanded;

    if (!this.draft.length) {
      this.composerExpanded = false;
      return this.composerExpanded !== wasExpanded;
    }

    if (/\n/.test(this.draft)) {
      this.composerExpanded = true;
      return this.composerExpanded !== wasExpanded;
    }

    const { min } = this.getComposerHeightLimits();
    const previousHeight = textarea.style.height;
    textarea.style.height = `${min}px`;
    const overflowsSingleLine = textarea.scrollHeight > min + 2;
    textarea.style.height = previousHeight;

    if (overflowsSingleLine) {
      this.composerExpanded = true;
    } else if (this.composerExpanded) {
      this.composerExpanded = false;
    }

    return this.composerExpanded !== wasExpanded;
  }

  private autoResizeComposer(textarea: HTMLTextAreaElement): void {
    const layoutChanged = this.syncComposerExpanded(textarea);
    if (layoutChanged) {
      this.cdr.detectChanges();
    }

    const { min, max } = this.getComposerHeightLimits();
    textarea.style.height = `${min}px`;

    if (this.composerExpanded) {
      const nextHeight = Math.min(textarea.scrollHeight, max);
      textarea.style.height = `${nextHeight}px`;
      textarea.classList.toggle('is-scrollable', textarea.scrollHeight > max);
      textarea.style.overflowY = textarea.scrollHeight > max ? 'auto' : 'hidden';
    } else {
      textarea.style.height = `${min}px`;
      textarea.classList.remove('is-scrollable');
      textarea.style.overflowY = 'hidden';
    }

    if (layoutChanged) {
      const relayouted = this.syncComposerExpanded(textarea);
      if (relayouted) {
        this.cdr.detectChanges();
        textarea.style.height = `${min}px`;
        if (this.composerExpanded) {
          const nextHeight = Math.min(textarea.scrollHeight, max);
          textarea.style.height = `${nextHeight}px`;
          textarea.classList.toggle('is-scrollable', textarea.scrollHeight > max);
          textarea.style.overflowY = textarea.scrollHeight > max ? 'auto' : 'hidden';
        }
      }
    }

    this.ensureComposerCaretVisible(textarea);
    this.syncComposerHighlightScroll(textarea);
  }

  private resetComposerHeight(): void {
    const textarea = this.messageInput?.nativeElement;
    this.composerExpanded = false;
    if (!textarea) return;

    const { min } = this.getComposerHeightLimits();
    textarea.style.height = `${min}px`;
    textarea.style.overflowY = 'hidden';
    textarea.scrollTop = 0;
    textarea.classList.remove('is-scrollable');
    this.syncComposerHighlightScroll(textarea);
  }

  private renderUserHtml(text: string, mentions?: ChatMention[]): SafeHtml {
    if (!mentions?.length) {
      return this.renderMarkdown(text);
    }

    const html = highlightMentions(text, mentions);
    const sanitized = this.sanitizer.sanitize(SecurityContext.HTML, html) ?? '';
    return this.sanitizer.bypassSecurityTrustHtml(sanitized);
  }

  private renderMarkdown(
    markdown: string,
    sources?: ChatSource[]
  ): SafeHtml {
    const html = marked.parse(markdown || '') as string;
    const enhanced = this.enhanceStreamHtml(html, sources);
    const sanitizedHtml =
      this.sanitizer.sanitize(SecurityContext.HTML, enhanced) ?? '';

    return this.sanitizer.bypassSecurityTrustHtml(sanitizedHtml);
  }

  /** Markdown + caret inline no último bloco (typewriter). */
  private renderStreamingMarkdown(
    markdown: string,
    sources?: ChatSource[]
  ): SafeHtml {
    const html = marked.parse(markdown || '') as string;
    const enhanced = this.enhanceStreamHtml(html, sources);
    const sanitizedHtml =
      this.sanitizer.sanitize(SecurityContext.HTML, enhanced) ?? '';

    return this.sanitizer.bypassSecurityTrustHtml(
      this.injectStreamCaret(sanitizedHtml)
    );
  }

  private enhanceStreamHtml(
    html: string,
    sources?: ChatSource[]
  ): string {
    if (!html) return html;
    let result = html;

    for (const source of sources ?? []) {
      const url = source.url?.trim();
      if (!url) continue;
      const host = this.sourceHostLabel(url);
      const label = host || source.title;
      const escapedUrl = url.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
      const pill = this.buildCitePillHtml(url, label);
      result = result.replace(
        new RegExp(`<a href="${escapedUrl}"[^>]*>[^<]*</a>`, 'gi'),
        pill
      );
    }

    result = result.replace(
      /<a href="(https?:\/\/[^"]+)"(?![^>]*class="chatbot__cite")([^>]*)>([^<]*)<\/a>/gi,
      (_match, href, _attrs, text) => {
        const host = this.sourceHostLabel(href);
        const label = host || text;
        return this.buildCitePillHtml(href, label);
      }
    );

    result = result.replace(
      /<a href="(\/[^"]*)"([^>]*)>/gi,
      (_match, href, attrs) => {
        if (attrs.includes('chatbot__inlineLink')) return _match;
        return `<a href="${href}" class="chatbot__inlineLink"${attrs}>`;
      }
    );

    return this.injectWebSourceCites(result, sources);
  }

  private buildCitePillHtml(url: string, label: string): string {
    const host = this.sourceHostLabel(url);
    const favicon = faviconUrlForUrl(url);
    const icon = favicon
      ? `<img class="chatbot__citeFavicon" src="${favicon}" alt="" width="14" height="14" loading="lazy" decoding="async" />`
      : `<span class="chatbot__citeIcon" aria-hidden="true"></span>`;
    return `<a href="${url}" class="chatbot__cite" target="_blank" rel="noopener noreferrer">${icon}<span class="chatbot__citeLabel">${this.escapeHtml(label || host)}</span></a>`;
  }

  private injectWebSourceCites(
    html: string,
    sources?: ChatSource[]
  ): string {
    return injectWebSourceCitesIntoHtml(
      html,
      sources,
      (url, label) => this.buildCitePillHtml(url, label),
      (url) => this.sourceHostLabel(url)
    );
  }

  private escapeHtml(value: string): string {
    return value
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;');
  }

  private injectStreamCaret(html: string): string {
    const caret =
      '<span class="chatbot__streamCaret" aria-hidden="true"></span>';
    const trimmed = html.trim();
    if (!trimmed) {
      return `<p>${caret}</p>`;
    }

    const tagPattern = /<\/(p|li|h[1-6]|blockquote|pre|td|th)>/gi;
    let insertAt = -1;
    let match: RegExpExecArray | null;
    while ((match = tagPattern.exec(trimmed)) !== null) {
      insertAt = match.index;
    }

    if (insertAt < 0) {
      return `${trimmed}${caret}`;
    }

    return trimmed.slice(0, insertAt) + caret + trimmed.slice(insertAt);
  }
}
